import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  RefreshCw, Save, Check, AlertTriangle, Search, Image as ImageIcon,
  X, Upload, Info, Star, ArrowUp, ArrowDown, Trash2, Tag as TagIcon,
  ChefHat, Utensils, UtensilsCrossed, CookingPot, Soup, Fish, ChevronDown, Crop,
} from 'lucide-react';
import {
  bcApi, bcApiStore, fetchAllPages, inBatches, readFileAsBase64,
  UPLOAD_PRODUCT_URL, UPLOAD_MARQUEE_ICON_URL, MAX_UPLOAD_BYTES, MAX_MARQUEE_ICON_BYTES, BASE_URL,
} from './siteContentShared';
import { neonQuery } from '../../services/api';
import { getActiveEnv } from '../../services/env';
import { getAccessToken } from '../../services/auth';
import PageBlocksEditor from './PageBlocksEditor';
import BannerSizingInfo from './BannerSizingInfo';
import DropZone from './DropZone';
import MediaPicker from '../../components/MediaPicker';
import ImageCropModal from '../../components/ImageCropModal';
import {
  PRODUCT_TAGS, TAG_KEYS, MAX_VISIBLE_TAGS,
  TAG_NAMESPACE, TAG_VALUE, TAG_PERMISSION_SET, isTagKey,
} from '../../data/productTags';

// ── Option badges metafield shape ────────────────────────────────────────────
// namespace: 'pdp', key: 'option_badges', permission: 'read_and_sf_access'
// value: JSON string of { [option_value_id]: { badge, pctOff, greatFor } }
const PDP_NS = 'pdp';
const OPTION_BADGES_KEY = 'option_badges';
// Per-pill count subtext. Shows a "{count} crabs" line under specific option
// pills, where the count varies based on another option's selection (size).
// Value: JSON { label, showOnOptionId, variesByOptionId, matrix: { showOnOvId: { variesByOvId: count } } }
// label is a template — {count} is replaced with the resolved number.
const PILL_COUNTS_KEY = 'pill_counts';
const PILL_COUNTS_LABEL_DEFAULT = '{count} crabs';

// Grid Row Tags — colored "Hot Deal"-style pills shown under the Quantity value
// on a matching row in the storefront "View all as a grid" overlay. Stored in the
// product METAFIELD pdp/grid_row_tags (permission read_and_sf_access), value =
// JSON array:
//   [{ "crab": "Premiums", "size": "Bushel", "label": "Hot Deal", "color": "orange" }]
// The storefront (cpx-tcp variant-table.js) reads this metafield via GraphQL
// (falling back to the legacy `grid_row_tags` CUSTOM FIELD during rollout), matches
// `crab` as a case-insensitive SUBSTRING of the grade label and `size` as an EXACT
// (case-insensitive) match, and renders a pill via .variant-row-tag--{color}.
//
// MIGRATION: this used to live in a product CUSTOM FIELD (250-char `value` cap,
// which blocked 4+ tags). It now lives in a METAFIELD (~65,535-char cap). The old
// custom field is KEPT in place as a read fallback/rollback — the editor reads the
// metafield first and falls back to the custom field, and on save writes ONLY the
// metafield.
const GRID_ROW_TAGS_KEY = 'grid_row_tags';           // metafield key (namespace = PDP_NS = 'pdp')
const GRID_ROW_TAGS_FIELD = 'grid_row_tags';         // legacy custom-field name (read fallback only)
// BigCommerce caps a metafield `value` at 65,535 chars. Keep a soft ceiling well
// under that so the UI can still warn if someone pastes something absurd, but the
// old 250-char hard block is gone.
const GRID_ROW_TAGS_MAX_VALUE = 60000;
const GRID_ROW_TAG_COLORS = [
  { value: 'orange', label: 'Orange', swatch: '#dc582a' },
  { value: 'navy', label: 'Navy', swatch: '#00395c' },
  { value: 'green', label: 'Green', swatch: '#059669' },
  { value: 'red', label: 'Red', swatch: '#dc2626' },
];
const GRID_ROW_TAG_COLOR_DEFAULT = 'orange';
const GRID_ROW_TAG_LABEL_DEFAULT = 'Hot Deal';

function parseGridRowTags(value) {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(t => t && typeof t === 'object')
      .map(t => ({
        crab: String(t.crab || ''),
        size: String(t.size || ''),
        label: String(t.label || ''),
        color: GRID_ROW_TAG_COLORS.some(c => c.value === t.color) ? t.color : GRID_ROW_TAG_COLOR_DEFAULT,
      }));
  } catch {
    return [];
  }
}

// Drop incomplete rows (no crab, size, or label). Returns a normalized array
// suitable for serialization; an empty array means "delete the custom field".
function cleanGridRowTags(rows) {
  if (!Array.isArray(rows)) return [];
  const out = [];
  for (const t of rows) {
    if (!t || typeof t !== 'object') continue;
    const crab = String(t.crab || '').trim();
    const size = String(t.size || '').trim();
    const label = String(t.label || '').trim();
    if (!crab || !size || !label) continue;
    const color = GRID_ROW_TAG_COLORS.some(c => c.value === t.color) ? t.color : GRID_ROW_TAG_COLOR_DEFAULT;
    out.push({ crab, size, label, color });
  }
  return out;
}

function gridRowTagsEqual(a, b) {
  return JSON.stringify(cleanGridRowTags(a)) === JSON.stringify(cleanGridRowTags(b));
}

// Resolve the storefront's groupBy (grade) + sizeBy (quantity) option axes from
// a product's options. Mirrors detectOptionNames() in cpx-tcp variant-table.js.
function detectGridTagAxes(options) {
  const list = options || [];
  const byName = (name) => list.find(o => o.display_name === name) || null;
  const gradeOption = byName('Crab Type') || byName('Crab Size') || null;
  const qtyOption = byName('Serving Size') || byName('Quantity') || null;
  return { gradeOption, qtyOption };
}
// Map of { option_value_id: image_id } — our own source of truth for which gallery
// image backs each option value. We still write variant.image_url too (best effort
// for Stencil's native swap behavior), but BC transforms gallery URLs into
// attribute_rule_images/*, so we can't round-trip through variant.image_url alone.
const VARIANT_IMAGE_IDS_KEY = 'variant_image_ids';
// Thumbnail layout per-product. 'left' = vertical strip beside main image;
// blank/omitted = horizontal strip below main image (theme default).
// Storefront reads via gql.data.site.product.pdpMetafields and toggles
// .productView--thumbs-left.
const THUMBS_POSITION_KEY = 'thumbs_position';
const THUMBS_POSITIONS = [
  { value: '', label: 'Bottom (default)' },
  { value: 'left', label: 'Left of main image' },
];

function parseOptionBadges(value) {
  if (!value) return {};
  try {
    const parsed = JSON.parse(value);
    return (parsed && typeof parsed === 'object') ? parsed : {};
  } catch {
    return {};
  }
}

function optionBadgesEqual(a, b) {
  return JSON.stringify(a || {}) === JSON.stringify(b || {});
}

function cleanOptionBadges(obj) {
  // Drop entries that have no meaningful content so we don't persist empty slots.
  const out = {};
  for (const [k, v] of Object.entries(obj || {})) {
    if (!v || typeof v !== 'object') continue;
    const entry = {};
    if (v.badge && String(v.badge).trim()) entry.badge = String(v.badge).trim();
    if (v.pctOff != null && String(v.pctOff).trim() !== '') {
      const n = Number(v.pctOff);
      if (!Number.isNaN(n)) entry.pctOff = n;
    }
    if (v.unitCount != null && String(v.unitCount).trim() !== '') {
      const n = Number(v.unitCount);
      if (!Number.isNaN(n) && n > 0) entry.unitCount = Math.round(n);
    }
    if (v.greatFor && String(v.greatFor).trim()) entry.greatFor = String(v.greatFor).trim();
    // Per-row badge color overrides — preset keys from the marquee palette.
    // Storefront resolves these to hex via OPTION_BADGE_PALETTE in atf-pdp.js
    // and applies them as inline styles, beating the SCSS auto-detect classes
    // (.option-badge--most-popular / --best-value / etc.) when set.
    if (v.badgeBg && String(v.badgeBg).trim()) entry.badgeBg = String(v.badgeBg).trim();
    if (v.badgeText && String(v.badgeText).trim()) entry.badgeText = String(v.badgeText).trim();
    // Size-conditional gate: the storefront shows this badge only when the
    // given SIZE option value is selected (e.g. Dozen "Most Popular" only
    // under Colossals). No UI control yet — carry it through verbatim so an
    // admin save doesn't strip it.
    if (v.whenSizeOvId != null && String(v.whenSizeOvId).trim() !== '') {
      const n = Number(v.whenSizeOvId);
      if (!Number.isNaN(n) && n > 0) entry.whenSizeOvId = Math.round(n);
    }
    if (Object.keys(entry).length > 0) out[k] = entry;
  }
  return out;
}

function parsePillCounts(value) {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

function pillCountsEqual(a, b) {
  return JSON.stringify(a || null) === JSON.stringify(b || null);
}

// Strip the matrix to only meaningful entries and validate axis IDs. Returns
// null when nothing usable remains so the save path can DELETE the metafield.
function cleanPillCounts(obj) {
  if (!obj || typeof obj !== 'object') return null;
  const showOnOptionId = Number(obj.showOnOptionId);
  const variesByOptionId = Number(obj.variesByOptionId);
  if (!Number.isFinite(showOnOptionId) || showOnOptionId <= 0) return null;
  const matrix = {};
  const rawMatrix = (obj.matrix && typeof obj.matrix === 'object') ? obj.matrix : {};
  for (const [rowKey, rowVal] of Object.entries(rawMatrix)) {
    if (!rowVal || typeof rowVal !== 'object') continue;
    const row = {};
    for (const [colKey, n] of Object.entries(rowVal)) {
      const num = Number(n);
      if (Number.isFinite(num) && num > 0) row[colKey] = Math.round(num);
    }
    if (Object.keys(row).length > 0) matrix[rowKey] = row;
  }
  if (Object.keys(matrix).length === 0) return null;
  const label = String(obj.label || '').trim();
  const out = { showOnOptionId, matrix };
  if (Number.isFinite(variesByOptionId) && variesByOptionId > 0) out.variesByOptionId = variesByOptionId;
  if (label) out.label = label;
  return out;
}

// ── Product loading ──────────────────────────────────────────────────────────
async function loadProductList() {
  // Bring the list small: just what the master-list needs.
  const path = '/v3/catalog/products?include_fields=name,sku,is_visible,categories&include=primary_image';
  return fetchAllPages(path);
}

// Banner metafield shape: namespace 'banner'. Each key stored as a separate
// metafield so they're easy to audit and individually deletable.
//   enabled:   "true" | "false" — default off; storefront hides the banner otherwise
//   sticky:    "true" | "false" — if true, banner sticks to top on scroll
//   title, subhead, image_url: override fields (blank = use fallback)
const BANNER_NS = 'banner';
const BANNER_KEYS = ['enabled', 'sticky', 'title', 'subhead', 'image_url', 'wave_enabled', 'wave_color'];
// Banner wave defaults — animation ON by default; only an explicit 'false'
// metafield disables it. Color falls back to the SCSS keyframe default
// (#ffb020) when no preset is selected.
const BANNER_WAVE_ENABLED_DEFAULT = true;

// PDP option tooltip metafields. Overrides the hardcoded default text next to the
// QUANTITY / SEASONING labels in templates/components/atf/products/options/set-radio.html.
// Storefront reads these via the same pdp-banner Netlify fn (one request per PDP load).
// Per-product only — no category fallback (intentional; categories don't have options).
const TOOLTIP_NS = 'pdp_tooltips';
const TOOLTIP_KEYS = ['quantity', 'seasoning'];
const TOOLTIP_DEFAULTS = {
  quantity: 'General guidelines are a bushel feeds 8-12 people in a traditional crab feast. A dozen is usually a 2-person serving.',
  seasoning: 'We use an Old Bay alternative seasoning that blends spices and salt to optimum taste for crabs. Light, Medium, Heavy choices reflect the amount we dump on the crabs.',
};
const TOOLTIP_LABELS = { quantity: 'Quantity', seasoning: 'Seasoning' };

// PDP marquee — per-product scrolling tag strip that replaces the pill tag row
// (templates/components/atf/products/custom-fields-v3.html) when enabled.
// namespace: 'pdp_marquee', permission: read_and_sf_access
// Keys:
//   enabled: 'true' | 'false'
//   items:   JSON string of [{ icon: 'crab-v2', text: 'Authentic Blue Crabs' }, ...]
// Storefront reads via pdp-banner Netlify fn (same per-PDP request).
const MARQUEE_NS = 'pdp_marquee';
const MARQUEE_KEYS = ['enabled', 'items', 'duration_sec', 'sticky', 'bg_color', 'text_color', 'icon_color', 'flash_color', 'flash_effect', 'link_banner', 'padding_y_px', 'scroll_mode', 'rotate_interval_sec', 'static_item_index', 'mobile_scroll_mode', 'mobile_static_item_index'];
const MARQUEE_ROTATE_INTERVAL_DEFAULT = 3;
const MARQUEE_ROTATE_INTERVAL_MIN = 1;
const MARQUEE_ROTATE_INTERVAL_MAX = 60;
const MARQUEE_COLOR_KEYS = ['bg_color', 'text_color', 'icon_color', 'flash_color'];

// Highlight effect choices — must stay in sync with MARQUEE_EFFECTS in
// netlify/functions/pdp-banner.js and the SCSS selectors in
// pdp-marquee.scss (.pdp-marquee-strip--effect-{key}).
const MARQUEE_EFFECTS = [
  { key: 'flash',  label: 'Flash',  hint: 'Color hold-pulse with glow (default)' },
  { key: 'wave',   label: 'Wave',   hint: 'Per-letter bob, like the PDP banner subhead' },
  { key: 'bounce', label: 'Bounce', hint: 'Whole item scales up + back' },
  { key: 'none',   label: 'None',   hint: 'No highlight when an item passes center' },
];
const MARQUEE_EFFECT_DEFAULT = 'flash';
const MARQUEE_ITEM_LIMIT = 8;
const MARQUEE_DEFAULT_DURATION = 90; // site-wide default seconds per scroll loop (sent by pdp-banner.js)
const MARQUEE_MIN_DURATION = 10;
const MARQUEE_MAX_DURATION = 180;

// Vertical padding on the strip (top/bottom). Total visible height ≈ 36 (icon) + 2*padding,
// so default 2 → ~40px tall. Range 0–40 → ~36–116px tall. Site-wide default is sent by
// pdp-banner.js (duration_sec / padding_y_px) so a blank field uses these values everywhere.
const MARQUEE_DEFAULT_PADDING_Y = 2;
const MARQUEE_MIN_PADDING_Y = 0;
const MARQUEE_MAX_PADDING_Y = 40;

// Color presets for the marquee — brand guideline colors + the VIP card gold.
// Storefront has matching constants in pdp-banner.js (resolveMarqueeColors).
// `swatch` is the CSS background used for the admin chip preview; `color` is
// the foreground (used as a flat fallback for VIP Gold on text/icon).
const MARQUEE_PALETTE = {
  navy:     { label: 'Brand Navy',        color: '#003b5c', swatch: '#003b5c' },
  orange:   { label: 'Brand Orange',      color: '#dc582a', swatch: '#dc582a' },
  orange_2: { label: 'Orange (secondary)', color: '#ea733d', swatch: '#ea733d' },
  peach:    { label: 'Peach / Cream',     color: '#fcd299', swatch: '#fcd299' },
  gold:     { label: 'VIP Gold',          color: '#dcb164', swatch: 'linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 30%, rgba(255,255,255,0.12) 65%, rgba(0,0,0,0.06) 100%), linear-gradient(180deg, #fce5b3 0%, #f1cc88 55%, #dcb164 100%)' },
  green:    { label: 'Best Value Green',  color: '#059669', swatch: '#059669' },
  white:    { label: 'White',             color: '#ffffff', swatch: '#ffffff' },
};
const MARQUEE_PALETTE_ORDER = ['navy', 'orange', 'orange_2', 'peach', 'gold', 'green', 'white'];

// Defaults match the storefront SCSS — keep these in sync with pdp-marquee.scss.
const MARQUEE_DEFAULT_COLOR_KEYS = { bg: 'gold', text: 'navy', icon: 'navy', flash: 'orange' };

// PDP UGC gallery — per-product customer-content panel rendered between the
// VIP Club marketing block and the "Original Source" content on the PDP.
// namespace: 'pdp_ugc', permission: read_and_sf_access
// Keys:
//   enabled  ('true' | 'false')
//   heading  (optional headline text; theme has a default)
//   subhead  (optional)
//   items    JSON array of:
//     { url, thumbUrl, caption, handle, platform, sourceUrl, mediaAssetId }
// Storefront reads via the same pdp-banner Netlify fn (one request per PDP load).
const UGC_NS = 'pdp_ugc';
const UGC_KEYS = ['enabled', 'heading', 'subhead', 'layout', 'items'];

// PDP Overlay — expand/collapse text panel anchored to the TOP of the main
// PDP image. Lets the merchant drop a paragraph above the fold without
// pushing other content down. namespace: 'pdp_overlay', permission:
// read_and_sf_access. Keys:
//   enabled         ('true' | 'false') — per-product; absent = site default
//   text            paragraph body shown when expanded
//   teaser          optional one-liner for collapsed state (falls back to
//                   first ~80 chars of `text`)
//   font_size       px override for body text (12-22, default 14)
//   text_color      preset key from MARQUEE_PALETTE (text + caret tint)
//   bg_color        preset key from MARQUEE_PALETTE (panel background base)
//   bg_opacity      0-100 (default 80; applied as alpha on bg_color)
//   start_expanded  ('true' | 'false') — opens expanded on first paint
const OVERLAY_NS = 'pdp_overlay';
const OVERLAY_KEYS = ['enabled', 'text', 'teaser', 'font_size', 'text_color', 'bg_color', 'bg_opacity', 'start_expanded'];
const OVERLAY_FONT_SIZE_DEFAULT = 14;
const OVERLAY_FONT_SIZE_MIN = 10;
const OVERLAY_FONT_SIZE_MAX = 28;
const OVERLAY_OPACITY_DEFAULT = 80;

// Price Guarantee modal — the "Best Seafood, Best Price — We Guarantee It"
// popup. Per-product copy override; blank fields fall back to pdp-banner's
// product-agnostic brand-price-story defaults. namespace: 'pdp_guarantee',
// permission: read_and_sf_access. Keys below.
const GUARANTEE_NS = 'pdp_guarantee';
const GUARANTEE_KEYS = ['title', 'lede', 'body', 'guarantee_title', 'guarantee_body'];

// PDP Live Viewers badge -- the "N people are viewing this" social-proof badge
// on the PDP. Per-product OVERRIDE of the site-wide default (pdp_defaults keys
// live_viewers_enabled / live_viewers_min_count). namespace: 'pdp_live_viewers',
// permission: read_and_sf_access. Keys:
//   enabled:   'true' | 'false' | absent -- absent = inherit the site default
//   min_count: integer-as-string; badge hides at/below this count. Absent = inherit.
// Storefront resolves via the pdp-banner Netlify fn (merges this over the env default).
const LIVE_VIEWERS_NS = 'pdp_live_viewers';
// Badge data sources. 'recent' is the GA4 basis: distinct devices that opened
// the PDP inside a trailing window, with no leave signal (GA4 has none either).
const LV_MODE_VALUES = ['simulated', 'real', 'recent'];
const LIVE_VIEWERS_KEYS = ['enabled', 'min_count'];

const UGC_ITEM_LIMIT = 12;
const UGC_PLATFORMS = ['', 'instagram', 'facebook', 'tiktok', 'twitter', 'youtube', 'email', 'review', 'other'];
const UGC_LAYOUTS = ['grid', 'carousel'];
const UGC_LAYOUT_DEFAULT = 'grid';

// PDP Recipe CTA — per-product "Send me this Recipe" button overlaid on the
// plated product photos chosen here. namespace: 'pdp_recipe', permission:
// read_and_sf_access. Keys:
//   enabled            ('true' | 'false')
//   photos             JSON array of { id: <imageId>, recipe: <slug|''> } —
//                      one entry per photo that gets the button; `recipe`
//                      blank = auto-match by SKU. Each photo can point at a
//                      DIFFERENT recipe.
//   button_text        optional label override (blank = storefront default)
//   bg_color           button background hex (blank = storefront default)
//   text_color         button text hex (blank = storefront default)
//   show_on_thumbnails 'true' | 'false' — show the discovery badge on
//                      recipe thumbnails (default true)
//   image_ids / recipe_slug — LEGACY keys (one recipe for all photos);
//                      migrated into `photos` on load, deleted on next save.
// Storefront reads via the pdp-banner Netlify fn; the button POSTs the
// shopper's email to recipe-email.js. The email template + Dotdigital list
// are site-wide (Site Content → Recipe Email tab).
const RECIPE_NS = 'pdp_recipe';
const RECIPE_KEYS = ['enabled', 'photos', 'button_text', 'bg_color', 'text_color', 'show_on_thumbnails', 'icon', 'icon_color', 'thumb_icon_only', 'image_ids', 'recipe_slug'];
const RECIPE_BUTTON_DEFAULT = 'Send me this Recipe';
const RECIPE_BG_DEFAULT = '#DC582A';   // storefront SCSS default (brand orange)
const RECIPE_TEXT_DEFAULT = '#FFFFFF';
// Brand quick-pick swatches for the button color fields (navy, orange,
// light-orange, peach, green, white) — plus a free custom color input.
const RECIPE_COLOR_SWATCHES = ['#00395C', '#DC582A', '#EA733D', '#FCD299', '#0E7C5A', '#FFFFFF'];

// Recipe-button icons. `key` is stored in the pdp_recipe `icon` metafield;
// the storefront SVG for each lives in RECIPE_ICON_SVG in pdp-banner.js —
// keep the key list in sync with that map.
const RECIPE_ICON_DEFAULT = 'chef-hat';
const RECIPE_ICONS = [
  { key: 'chef-hat',         label: 'Chef Hat',         Icon: ChefHat },
  { key: 'utensils',         label: 'Fork & Knife',     Icon: Utensils },
  { key: 'utensils-crossed', label: 'Crossed Utensils', Icon: UtensilsCrossed },
  { key: 'cooking-pot',      label: 'Cooking Pot',      Icon: CookingPot },
  { key: 'soup',             label: 'Soup Bowl',        Icon: Soup },
  { key: 'fish',             label: 'Fish',             Icon: Fish },
];
const RECIPE_ICON_BY_KEY = Object.fromEntries(RECIPE_ICONS.map(i => [i.key, i]));

// Parse the `photos` metafield. Tolerates the legacy bare-id array form
// ([1183, 1184]) so old `image_ids` values can be fed through this too.
function parseRecipePhotos(value) {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map(p => (p && typeof p === 'object')
        ? { id: Number(p.id), recipe: String(p.recipe || '') }
        : { id: Number(p), recipe: '' })
      .filter(p => Number.isFinite(p.id) && p.id > 0);
  } catch {
    return [];
  }
}

function recipePhotosEqual(a, b) {
  const norm = (arr) => JSON.stringify(
    (arr || []).map(p => ({ id: p.id, recipe: p.recipe || '' })).sort((x, y) => x.id - y.id)
  );
  return norm(a) === norm(b);
}

// Admin icon picker. `key` becomes `#icon-atf-{key}` on the storefront.
// `emoji` is only shown in the admin preview — the storefront renders the sprite.
// Built-in icons. `svgUrl` (optional) points at a local copy of the same
// sprite symbol the storefront renders, so the admin preview matches
// production 1:1 instead of showing an approximate emoji. Entries without
// svgUrl fall back to emoji in the admin — storefront still uses the
// #icon-atf-{key} sprite reference either way.
const MARQUEE_ICONS = [
  { key: 'crab-silhouette',          label: 'Crab (silhouette)',    emoji: '🦀', svgUrl: '/marquee-icons/crab-silhouette.svg' },
  { key: 'md-state',                 label: 'Maryland State',       emoji: '🗺️', svgUrl: '/marquee-icons/md-state.svg' },
  { key: 'crab-v2',                  label: 'Crab (detailed)',      emoji: '🦀' },
  { key: 'checkmark',                label: 'Checkmark',            emoji: '✓' },
  { key: 'location',                 label: 'Location Pin',         emoji: '📍' },
  { key: 'boat',                     label: 'Boat',                 emoji: '⚓' },
  { key: 'fresh',                    label: 'Fresh',                emoji: '🌱' },
  { key: 'frozen',                   label: 'Frozen',               emoji: '❄️' },
  { key: 'steamed',                  label: 'Steamed',              emoji: '♨️' },
  { key: 'heart-filled',             label: 'Heart',                emoji: '❤️' },
  { key: 'shipping-box',             label: 'Shipping Box',         emoji: '📦' },
  { key: 'ready-to-ship',            label: 'Ready to Ship',        emoji: '🚚' },
  { key: 'success',                  label: 'Success / Check',      emoji: '✓' },
  { key: 'tag-authentic-blue-crabs', label: 'Authentic Blue Crab (badge)', emoji: '🦀' },
  { key: 'tag-hand-graded',          label: 'Hand-Graded (badge)',  emoji: '✓' },
  { key: 'tag-shipped-from-md',      label: 'Shipped from MD (badge)', emoji: '📍' },
];
const MARQUEE_ICON_MAP = Object.fromEntries(MARQUEE_ICONS.map(i => [i.key, i]));

function parseMarqueeItems(value) {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(it => it && typeof it === 'object' && it.text)
      .map(it => ({
        icon: String(it.icon || ''),
        text: String(it.text),
        // Optional banner-text overrides — used when link_banner is on and
        // the marquee item bounces past center. Always normalized to strings
        // (empty string = "no override, use original banner copy").
        headline: it.headline ? String(it.headline) : '',
        description: it.description ? String(it.description) : '',
      }));
  } catch {
    return [];
  }
}

function marqueeItemsEqual(a, b) {
  return JSON.stringify(a || []) === JSON.stringify(b || []);
}

function parseUgcItems(value) {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(it => it && typeof it === 'object' && (it.url || it.thumbUrl))
      .map(it => ({
        url: String(it.url || it.thumbUrl || ''),
        thumbUrl: String(it.thumbUrl || it.url || ''),
        caption: String(it.caption || ''),
        handle: String(it.handle || ''),
        platform: String(it.platform || ''),
        sourceUrl: String(it.sourceUrl || ''),
        mediaAssetId: it.mediaAssetId != null ? Number(it.mediaAssetId) : null,
      }));
  } catch {
    return [];
  }
}

function ugcItemsEqual(a, b) {
  return JSON.stringify(a || []) === JSON.stringify(b || []);
}

// Sentinel written to the BC image description field for banner uploads.
// The storefront (cpx-tcp product-view-pdp.html) skips images whose alt
// matches this marker, and the admin's Images section hides them too,
// so banner uploads don't pollute the product gallery/carousel.
const PDP_BANNER_MARKER = '__PDP_BANNER__';
// Alt-text marker for variant-library-only images. Stored as a description
// prefix so user labels can live after it, e.g. "__VARIANT_LIBRARY__|my label".
// Storefront filters any image whose alt contains this marker out of the main
// carousel — these are only pickable from the Variant photos dropdowns.
const VARIANT_LIBRARY_MARKER = '__VARIANT_LIBRARY__';
const isVariantLibraryImage = (img) => (img?.description || '').indexOf(VARIANT_LIBRARY_MARKER) !== -1;

// Hero-video custom field names. The storefront reads these from
// templates/components/atf/products/product-view-pdp.html:
//   pdp-autoplay-video        — MP4 URL that overlays the main image (autoplay/loop/muted)
//   pdp-autoplay-video-thumb  — optional image URL for the first thumbnail (falls back to product main image)
//   pdp-autoplay-video-label  — optional label text on the thumb ("Watch" if unset)
const HERO_VIDEO_URL_FIELD = 'pdp-autoplay-video';
const HERO_VIDEO_THUMB_FIELD = 'pdp-autoplay-video-thumb';
const HERO_VIDEO_LABEL_FIELD = 'pdp-autoplay-video-label';

// Multiple self-hosted hero videos. Phase 1 moves the single-hero model (the 3
// custom fields above) to ONE metafield holding an ordered list, fully
// reorderable in the unified Media panel. Shape = JSON array of objects:
//   [{ id, url, thumb, label }]   (id is a short stable string; thumb/label optional)
// The legacy custom fields are kept as a read-only fallback for migration; once
// the pdp.hero_videos metafield is written it supersedes them. The storefront is
// updated in Phase 2 — until then nothing here changes what the PDP renders.
const HERO_VIDEOS_KEY = 'hero_videos';
// Short stable id for a hero video list entry.
const genHeroVideoId = () => `hv${Math.random().toString(36).slice(2, 8)}${Date.now().toString(36).slice(-4)}`;

// Sticky add-to-cart footer behavior. When this custom field is "true", the
// storefront replaces the static "Crab Size: Standards" labels in the sticky
// footer with bound dropdowns that mirror the main PDP option radios, so the
// shopper can change variant selections without scrolling back up.
const STICKY_ATC_DROPDOWNS_FIELD = 'pdp-sticky-atc-dropdowns';

// When "true", the storefront hides the PDP banner + marquee strip while the
// sticky add-to-cart footer is visible (recovers ~150px of viewport space).
const STICKY_ATC_HIDE_HEADER_FIELD = 'pdp-sticky-atc-hide-header';

// True Blue Maryland Crab certified overlay — Blue Crabs Steamed (product 132)
// only. Two custom fields drive it: an on/off toggle and a corner picker. The
// section is conditionally rendered in the admin so other products never see
// these options. Stored as BC custom fields (not metafields) so the storefront
// can read them directly in Handlebars without going through pdp-banner.js.
const TRUEBLUE_PRODUCT_ID = 132;
const TRUEBLUE_ENABLED_FIELD = 'pdp-trueblue-enabled';
const TRUEBLUE_POSITION_FIELD = 'pdp-trueblue-position';
const TRUEBLUE_POSITIONS = ['bottom-left', 'top-left', 'top-right', 'bottom-right'];
const TRUEBLUE_POSITION_DEFAULT = 'bottom-left';

// Unified carousel ordering. Stored as a JSON array of strings, e.g.
//   ["hero", "image:889", "video:124", "image:890"]
// The storefront reads this metafield, reorders the thumbnail <li> elements
// before slick inits, and falls back to the default order (hero → images →
// videos) when absent. Entries that no longer match a real image/video are
// ignored at render time, so stale lists degrade gracefully.
const MEDIA_ORDER_KEY = 'media_order';

// Maps a BC product image id → the R2 key of the Media Library original it
// was cropped from. Lets "Re-crop" re-open the crop modal on the untouched
// original. JSON object { "<imageId>": "<r2Key>" } in the pdp namespace.
// Cropping never alters the Media Library master — it only writes a new BC
// product image — so the original is always available to re-crop from.
const IMAGE_SOURCES_KEY = 'image_sources';

function parseImageSources(value) {
  if (!value) return {};
  try {
    const parsed = JSON.parse(value);
    return (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? parsed : {};
  } catch { return {}; }
}

function parseMediaOrder(value) {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter(s => typeof s === 'string') : null;
  } catch { return null; }
}

// Safe-parse the pdp.hero_videos metafield → array of { id, url, thumb, label }.
// Filters to objects that actually carry a url; backfills a stable id when
// missing so reorder keys stay consistent.
function parseHeroVideos(value) {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(v => v && typeof v === 'object' && typeof v.url === 'string' && v.url.trim())
      .map(v => ({
        id: (typeof v.id === 'string' && v.id) ? v.id : genHeroVideoId(),
        url: String(v.url || ''),
        thumb: String(v.thumb || ''),
        label: String(v.label || ''),
      }));
  } catch { return []; }
}

const getVariantLibraryLabel = (img) => {
  const d = img?.description || '';
  const idx = d.indexOf(VARIANT_LIBRARY_MARKER);
  if (idx === -1) return '';
  const after = d.slice(idx + VARIANT_LIBRARY_MARKER.length);
  return after.startsWith('|') ? after.slice(1) : '';
};
const withVariantLibraryMarker = (label) => label ? `${VARIANT_LIBRARY_MARKER}|${label}` : VARIANT_LIBRARY_MARKER;

async function loadProductDetail(productId) {
  const [imagesRes, optionsRes, variantsRes, pdpMetaRes, tagMetaRes, bannerMetaRes, tooltipMetaRes, marqueeMetaRes, ugcMetaRes, productRes, customFieldsRes, videosRes, recipeMetaRes, overlayMetaRes, guaranteeMetaRes, liveViewersMetaRes] = await Promise.all([
    bcApi('GET', `/v3/catalog/products/${productId}/images?limit=250`),
    bcApi('GET', `/v3/catalog/products/${productId}/options?limit=250`),
    bcApi('GET', `/v3/catalog/products/${productId}/variants?limit=250`),
    bcApi('GET', `/v3/catalog/products/${productId}/metafields?namespace=${PDP_NS}&limit=50`),
    bcApi('GET', `/v3/catalog/products/${productId}/metafields?namespace=${TAG_NAMESPACE}&limit=50`),
    bcApi('GET', `/v3/catalog/products/${productId}/metafields?namespace=${BANNER_NS}&limit=50`),
    bcApi('GET', `/v3/catalog/products/${productId}/metafields?namespace=${TOOLTIP_NS}&limit=50`),
    bcApi('GET', `/v3/catalog/products/${productId}/metafields?namespace=${MARQUEE_NS}&limit=50`),
    bcApi('GET', `/v3/catalog/products/${productId}/metafields?namespace=${UGC_NS}&limit=50`),
    bcApi('GET', `/v3/catalog/products/${productId}?include_fields=categories,name,sku,custom_url,primary_image&include=primary_image`),
    bcApi('GET', `/v3/catalog/products/${productId}/custom-fields?limit=250`),
    bcApi('GET', `/v3/catalog/products/${productId}/videos?limit=250`),
    bcApi('GET', `/v3/catalog/products/${productId}/metafields?namespace=${RECIPE_NS}&limit=50`),
    bcApi('GET', `/v3/catalog/products/${productId}/metafields?namespace=${OVERLAY_NS}&limit=50`),
    bcApi('GET', `/v3/catalog/products/${productId}/metafields?namespace=${GUARANTEE_NS}&limit=50`),
    bcApi('GET', `/v3/catalog/products/${productId}/metafields?namespace=${LIVE_VIEWERS_NS}&limit=50`),
  ]);
  const images = (imagesRes?.data || []).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  const options = optionsRes?.data || [];
  const variants = variantsRes?.data || [];
  const pdpMetafields = pdpMetaRes?.data || [];
  const optionBadgesMf = pdpMetafields.find(m => m.key === OPTION_BADGES_KEY) || null;
  const optionBadges = optionBadgesMf ? parseOptionBadges(optionBadgesMf.value) : {};
  const pillCountsMf = pdpMetafields.find(m => m.key === PILL_COUNTS_KEY) || null;
  const pillCounts = pillCountsMf ? parsePillCounts(pillCountsMf.value) : null;
  const mediaOrderMf = pdpMetafields.find(m => m.key === MEDIA_ORDER_KEY) || null;
  const mediaOrder = mediaOrderMf ? parseMediaOrder(mediaOrderMf.value) : null;
  const imageSourcesMf = pdpMetafields.find(m => m.key === IMAGE_SOURCES_KEY) || null;
  const imageSources = imageSourcesMf ? parseImageSources(imageSourcesMf.value) : {};
  const thumbsPositionMf = pdpMetafields.find(m => m.key === THUMBS_POSITION_KEY) || null;
  const thumbsPosition = thumbsPositionMf?.value || '';
  const variantImageIdsMf = pdpMetafields.find(m => m.key === VARIANT_IMAGE_IDS_KEY) || null;
  let variantImageIds = {};
  if (variantImageIdsMf?.value) {
    try {
      const parsed = JSON.parse(variantImageIdsMf.value);
      if (parsed && typeof parsed === 'object') {
        for (const [k, v] of Object.entries(parsed)) {
          const ovId = Number(k), imgId = Number(v);
          if (Number.isFinite(ovId) && Number.isFinite(imgId)) variantImageIds[ovId] = imgId;
        }
      }
    } catch { /* ignore malformed */ }
  }

  const tagMfs = tagMetaRes?.data || [];
  const appliedTagKeys = new Set();
  const tagMetafieldIds = {};
  for (const mf of tagMfs) {
    if (isTagKey(mf.key) && TAG_KEYS.has(mf.key)) {
      appliedTagKeys.add(mf.key);
      tagMetafieldIds[mf.key] = mf.id;
    }
  }

  // Banner: product override metafields + primary-category fallback for placeholders
  const bannerMfs = bannerMetaRes?.data || [];
  // wave_enabled defaults to true (subhead wave animation is on by default).
  // Only an explicit 'false' metafield disables it.
  const banner = { enabled: false, sticky: false, wave_enabled: BANNER_WAVE_ENABLED_DEFAULT, wave_color: '' };
  const bannerMfIds = {};
  for (const mf of bannerMfs) {
    if (!BANNER_KEYS.includes(mf.key)) continue;
    bannerMfIds[mf.key] = mf.id;
    if (mf.key === 'enabled' || mf.key === 'sticky') {
      banner[mf.key] = String(mf.value || '').toLowerCase() === 'true';
    } else if (mf.key === 'wave_enabled') {
      banner.wave_enabled = String(mf.value || '').toLowerCase() !== 'false';
    } else {
      banner[mf.key] = mf.value || '';
    }
  }

  // Option tooltips: product-only overrides, blank = fall back to theme's hardcoded default
  const tooltipMfs = tooltipMetaRes?.data || [];
  const tooltips = {};
  const tooltipMfIds = {};
  for (const mf of tooltipMfs) {
    if (!TOOLTIP_KEYS.includes(mf.key)) continue;
    tooltipMfIds[mf.key] = mf.id;
    tooltips[mf.key] = mf.value || '';
  }

  // Marquee: per-product scrolling strip (enabled flag + JSON items array + optional duration override + sticky flag + color overrides)
  const marqueeMfs = marqueeMetaRes?.data || [];
  const marquee = {
    enabled: false, linkBanner: false, items: [], duration_sec: null, sticky: false,
    padding_y_px: null, // null = inherit theme default (MARQUEE_DEFAULT_PADDING_Y)
    colors: { bg: '', text: '', icon: '', flash: '' },
    flashEffect: '', // empty = inherit theme default; admin compares against MARQUEE_EFFECT_DEFAULT
    scrollMode: '', // '' = inherit default 'scroll'; 'rotate' = no scroll + JS-driven cycle
    rotateIntervalSec: null, // null = inherit MARQUEE_ROTATE_INTERVAL_DEFAULT
    mobileScrollMode: '', // '' = same as desktop
    mobileStaticItemIndex: null, // null = 0
  };
  const marqueeMfIds = {};
  for (const mf of marqueeMfs) {
    if (!MARQUEE_KEYS.includes(mf.key)) continue;
    marqueeMfIds[mf.key] = mf.id;
    if (mf.key === 'enabled') {
      marquee.enabled = String(mf.value || '').toLowerCase() === 'true';
    } else if (mf.key === 'items') {
      marquee.items = parseMarqueeItems(mf.value);
    } else if (mf.key === 'duration_sec') {
      const n = Number(mf.value);
      marquee.duration_sec = Number.isFinite(n) && n > 0 ? n : null;
    } else if (mf.key === 'padding_y_px') {
      const n = Number(mf.value);
      marquee.padding_y_px = Number.isFinite(n) && n >= 0 ? n : null;
    } else if (mf.key === 'sticky') {
      marquee.sticky = String(mf.value || '').toLowerCase() === 'true';
    } else if (mf.key === 'link_banner') {
      marquee.linkBanner = String(mf.value || '').toLowerCase() === 'true';
    } else if (mf.key === 'bg_color') {
      marquee.colors.bg = String(mf.value || '').trim();
    } else if (mf.key === 'text_color') {
      marquee.colors.text = String(mf.value || '').trim();
    } else if (mf.key === 'icon_color') {
      marquee.colors.icon = String(mf.value || '').trim();
    } else if (mf.key === 'flash_color') {
      marquee.colors.flash = String(mf.value || '').trim();
    } else if (mf.key === 'flash_effect') {
      const ev = String(mf.value || '').trim().toLowerCase();
      if (MARQUEE_EFFECTS.some(e => e.key === ev)) marquee.flashEffect = ev;
    } else if (mf.key === 'scroll_mode') {
      const sm = String(mf.value || '').trim().toLowerCase();
      if (['rotate', 'scroll', 'static'].includes(sm)) marquee.scrollMode = sm;
    } else if (mf.key === 'rotate_interval_sec') {
      const n = Number(mf.value);
      marquee.rotateIntervalSec = Number.isFinite(n) && n >= MARQUEE_ROTATE_INTERVAL_MIN && n <= MARQUEE_ROTATE_INTERVAL_MAX ? n : null;
    } else if (mf.key === 'mobile_scroll_mode') {
      const msm = String(mf.value || '').trim().toLowerCase();
      if (['rotate', 'scroll', 'static'].includes(msm)) marquee.mobileScrollMode = msm;
    } else if (mf.key === 'mobile_static_item_index') {
      const n = Number(mf.value);
      marquee.mobileStaticItemIndex = Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
    }
  }

  // UGC: per-product customer-content gallery (enabled + heading + subhead + layout + items)
  const ugcMfs = ugcMetaRes?.data || [];
  const ugc = { enabled: false, heading: '', subhead: '', layout: UGC_LAYOUT_DEFAULT, items: [] };
  const ugcMfIds = {};
  for (const mf of ugcMfs) {
    if (!UGC_KEYS.includes(mf.key)) continue;
    ugcMfIds[mf.key] = mf.id;
    if (mf.key === 'enabled') {
      ugc.enabled = String(mf.value || '').toLowerCase() === 'true';
    } else if (mf.key === 'items') {
      ugc.items = parseUgcItems(mf.value);
    } else if (mf.key === 'layout') {
      const lv = String(mf.value || '').toLowerCase();
      ugc.layout = UGC_LAYOUTS.includes(lv) ? lv : UGC_LAYOUT_DEFAULT;
    } else {
      ugc[mf.key] = mf.value || '';
    }
  }

  // Recipe CTA: per-product "Send me this Recipe" button config — per-photo
  // recipe binding (photos), button label, button colors, thumbnail toggle.
  const recipeMfs = recipeMetaRes?.data || [];
  const recipeCta = { enabled: false, photos: [], buttonText: '', bgColor: '', textColor: '', showOnThumbnails: true, icon: RECIPE_ICON_DEFAULT, iconColor: '', thumbIconOnly: false };
  const recipeMfIds = {};
  let legacyImageIds = null;
  let legacyRecipeSlug = '';
  for (const mf of recipeMfs) {
    if (!RECIPE_KEYS.includes(mf.key)) continue;
    recipeMfIds[mf.key] = mf.id;
    if (mf.key === 'enabled') {
      recipeCta.enabled = String(mf.value || '').toLowerCase() === 'true';
    } else if (mf.key === 'photos') {
      recipeCta.photos = parseRecipePhotos(mf.value);
    } else if (mf.key === 'button_text') {
      recipeCta.buttonText = mf.value || '';
    } else if (mf.key === 'bg_color') {
      recipeCta.bgColor = String(mf.value || '').trim();
    } else if (mf.key === 'text_color') {
      recipeCta.textColor = String(mf.value || '').trim();
    } else if (mf.key === 'show_on_thumbnails') {
      recipeCta.showOnThumbnails = String(mf.value || '').toLowerCase() !== 'false';
    } else if (mf.key === 'icon') {
      const ik = String(mf.value || '').trim();
      if (RECIPE_ICON_BY_KEY[ik]) recipeCta.icon = ik;
    } else if (mf.key === 'icon_color') {
      recipeCta.iconColor = String(mf.value || '').trim();
    } else if (mf.key === 'thumb_icon_only') {
      recipeCta.thumbIconOnly = String(mf.value || '').toLowerCase() === 'true';
    } else if (mf.key === 'image_ids') {
      legacyImageIds = parseRecipePhotos(mf.value); // tolerates the bare-id array
    } else if (mf.key === 'recipe_slug') {
      legacyRecipeSlug = String(mf.value || '').trim();
    }
  }
  // Migrate the legacy one-recipe-for-all-photos shape into `photos` when the
  // new key isn't present yet. The next save writes `photos` and deletes the
  // legacy metafields.
  if (recipeCta.photos.length === 0 && legacyImageIds && legacyImageIds.length) {
    recipeCta.photos = legacyImageIds.map(p => ({ id: p.id, recipe: legacyRecipeSlug }));
  }

  // Primary category fallback info
  const primaryCatId = (productRes?.data?.categories || [])[0] || null;
  let categoryFallback = { id: primaryCatId, name: '', description: '', image_url: '', bannerTitle: '', bannerSubhead: '' };
  if (primaryCatId) {
    try {
      const [catRes, catMetaRes] = await Promise.all([
        bcApi('GET', `/v3/catalog/categories/${primaryCatId}`),
        bcApi('GET', `/v3/catalog/categories/${primaryCatId}/metafields?namespace=${BANNER_NS}&limit=50`),
      ]);
      categoryFallback.name = catRes?.data?.name || '';
      categoryFallback.description = catRes?.data?.description || '';
      categoryFallback.image_url = catRes?.data?.image_url || '';
      for (const mf of (catMetaRes?.data || [])) {
        if (mf.key === 'title') categoryFallback.bannerTitle = mf.value || '';
        if (mf.key === 'subhead') categoryFallback.bannerSubhead = mf.value || '';
      }
    } catch { /* non-fatal */ }
  }

  // Hero-video custom fields (URL / thumb / label) and BC product videos list.
  // We track the BC custom-field IDs so the VideosSection can PUT/DELETE in place
  // instead of POSTing duplicates.
  const customFields = customFieldsRes?.data || [];
  const heroVideoFieldIds = {};
  const heroVideo = { url: '', thumb: '', label: '' };
  let stickyAtcDropdowns = false;
  let stickyAtcDropdownsFieldId = null;
  let stickyAtcHideHeader = false;
  let stickyAtcHideHeaderFieldId = null;
  // True Blue overlay (product 132 only) — absent = default ON + bottom-left.
  let trueblueEnabled = true;
  let trueblueEnabledExplicit = false;
  let trueblueEnabledFieldId = null;
  let truebluePosition = TRUEBLUE_POSITION_DEFAULT;
  let truebluePositionFieldId = null;
  // Grid Row Tags — colored pills on the variant-grid overlay.
  // Source of truth = the pdp/grid_row_tags METAFIELD (resolved just below). We
  // still capture the legacy CUSTOM FIELD's value + id here as a read fallback for
  // products not yet migrated. `gridRowTagsFieldId` is retained only so the mirror
  // path can locate the old custom field if it ever needs to.
  let gridRowTagsFallback = [];
  let gridRowTagsFieldId = null;
  for (const cf of customFields) {
    if (cf.name === HERO_VIDEO_URL_FIELD) { heroVideo.url = cf.value || ''; heroVideoFieldIds.url = cf.id; }
    else if (cf.name === HERO_VIDEO_THUMB_FIELD) { heroVideo.thumb = cf.value || ''; heroVideoFieldIds.thumb = cf.id; }
    else if (cf.name === HERO_VIDEO_LABEL_FIELD) { heroVideo.label = cf.value || ''; heroVideoFieldIds.label = cf.id; }
    else if (cf.name === STICKY_ATC_DROPDOWNS_FIELD) {
      stickyAtcDropdowns = String(cf.value || '').toLowerCase() === 'true';
      stickyAtcDropdownsFieldId = cf.id;
    }
    else if (cf.name === STICKY_ATC_HIDE_HEADER_FIELD) {
      stickyAtcHideHeader = String(cf.value || '').toLowerCase() === 'true';
      stickyAtcHideHeaderFieldId = cf.id;
    }
    else if (cf.name === TRUEBLUE_ENABLED_FIELD) {
      trueblueEnabled = String(cf.value || '').toLowerCase() !== 'false';
      trueblueEnabledExplicit = true;
      trueblueEnabledFieldId = cf.id;
    }
    else if (cf.name === TRUEBLUE_POSITION_FIELD) {
      const v = String(cf.value || '').toLowerCase();
      if (TRUEBLUE_POSITIONS.includes(v)) truebluePosition = v;
      truebluePositionFieldId = cf.id;
    }
    else if (cf.name === GRID_ROW_TAGS_FIELD) {
      gridRowTagsFallback = parseGridRowTags(cf.value);
      gridRowTagsFieldId = cf.id;
    }
  }
  // Resolve Grid Row Tags: prefer the pdp/grid_row_tags METAFIELD (already fetched
  // in `pdpMetafields`), else fall back to the legacy custom field for products not
  // yet migrated.
  const gridRowTagsMf = pdpMetafields.find(m => m.key === GRID_ROW_TAGS_KEY) || null;
  const gridRowTags = gridRowTagsMf ? parseGridRowTags(gridRowTagsMf.value) : gridRowTagsFallback;
  const gridRowTagsMfId = gridRowTagsMf?.id || null;
  const bcVideos = (videosRes?.data || []).slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  // Multiple hero videos (Phase 1). The pdp.hero_videos metafield is the new
  // source of truth. MIGRATION: when it's absent but the legacy single-hero
  // custom fields carry a url, seed a one-entry list so existing products show
  // their video in the unified list. We do NOT delete the legacy custom fields
  // here — they remain as a read-only fallback and are simply superseded once
  // hero_videos is saved.
  const heroVideosMf = pdpMetafields.find(m => m.key === HERO_VIDEOS_KEY) || null;
  let heroVideos;
  if (heroVideosMf) {
    heroVideos = parseHeroVideos(heroVideosMf.value);
  } else if (heroVideo.url) {
    heroVideos = [{ id: 'hv-legacy', url: heroVideo.url, thumb: heroVideo.thumb || '', label: heroVideo.label || '' }];
  } else {
    heroVideos = [];
  }

  const primaryImageUrl = productRes?.data?.primary_image?.url_standard
    || productRes?.data?.primary_image?.url_thumbnail
    || '';

  // PDP Overlay: expand/collapse text panel on top of the main PDP image.
  // Same parse-into-dict + capture-mf-ids pattern as ugc/marquee. Numeric
  // fields get coerced to Number so the dirty-check downstream can compare
  // shapes correctly (input fields return strings — see CLAUDE.md note on
  // the marquee numeric-input dirty-check gotcha).
  const overlayMfs = overlayMetaRes?.data || [];
  const overlay = {
    enabled: false,
    enabledExplicit: false,
    text: '',
    teaser: '',
    fontSize: OVERLAY_FONT_SIZE_DEFAULT,
    textColor: '',
    bgColor: '',
    bgOpacity: OVERLAY_OPACITY_DEFAULT,
    startExpanded: false,
  };
  const overlayMfIds = {};
  for (const mf of overlayMfs) {
    if (!OVERLAY_KEYS.includes(mf.key)) continue;
    overlayMfIds[mf.key] = mf.id;
    if (mf.key === 'enabled') {
      overlay.enabled = String(mf.value || '').toLowerCase() === 'true';
      overlay.enabledExplicit = true;
    } else if (mf.key === 'text') {
      overlay.text = String(mf.value || '');
    } else if (mf.key === 'teaser') {
      overlay.teaser = String(mf.value || '');
    } else if (mf.key === 'font_size') {
      const n = Number(mf.value);
      if (Number.isFinite(n)) overlay.fontSize = n;
    } else if (mf.key === 'text_color') {
      overlay.textColor = String(mf.value || '').trim();
    } else if (mf.key === 'bg_color') {
      overlay.bgColor = String(mf.value || '').trim();
    } else if (mf.key === 'bg_opacity') {
      const n = Number(mf.value);
      if (Number.isFinite(n)) overlay.bgOpacity = n;
    } else if (mf.key === 'start_expanded') {
      overlay.startExpanded = String(mf.value || '').toLowerCase() === 'true';
    }
  }

  // Price Guarantee modal copy. All fields optional — blank = use pdp-banner's
  // product-agnostic default. Parse-into-dict + capture-mf-ids, same pattern.
  const guaranteeMfs = guaranteeMetaRes?.data || [];
  const guarantee = { title: '', lede: '', body: '', guaranteeTitle: '', guaranteeBody: '' };
  const guaranteeMfIds = {};
  for (const mf of guaranteeMfs) {
    if (!GUARANTEE_KEYS.includes(mf.key)) continue;
    guaranteeMfIds[mf.key] = mf.id;
    if (mf.key === 'title') guarantee.title = String(mf.value || '');
    else if (mf.key === 'lede') guarantee.lede = String(mf.value || '');
    else if (mf.key === 'body') guarantee.body = String(mf.value || '');
    else if (mf.key === 'guarantee_title') guarantee.guaranteeTitle = String(mf.value || '');
    else if (mf.key === 'guarantee_body') guarantee.guaranteeBody = String(mf.value || '');
  }

  // PDP Live Viewers badge. Per-product override of the site-wide default.
  // enabled null = inherit; minCount null = inherit. Same parse-into-dict +
  // capture-mf-ids pattern as overlay/guarantee. min_count kept as a Number so
  // the dirty-check compares cleanly (blank input = inherit = delete the key).
  const liveViewersMfs = liveViewersMetaRes?.data || [];
  const liveViewers = { enabled: null, minCount: null };
  const liveViewersMfIds = {};
  for (const mf of liveViewersMfs) {
    if (!LIVE_VIEWERS_KEYS.includes(mf.key)) continue;
    liveViewersMfIds[mf.key] = mf.id;
    if (mf.key === 'enabled') {
      const v = String(mf.value || '').toLowerCase();
      liveViewers.enabled = v === 'true' ? true : (v === 'false' ? false : null);
    } else if (mf.key === 'min_count') {
      const n = Number(mf.value);
      if (Number.isFinite(n)) liveViewers.minCount = n;
    }
  }

  return {
    images,
    options,
    variants,
    optionBadgesMfId: optionBadgesMf?.id || null,
    optionBadges,
    pillCountsMfId: pillCountsMf?.id || null,
    pillCounts,
    variantImageIds,
    variantImageIdsMfId: variantImageIdsMf?.id || null,
    appliedTagKeys,
    tagMetafieldIds,
    banner,
    bannerMfIds,
    tooltips,
    tooltipMfIds,
    marquee,
    marqueeMfIds,
    ugc,
    ugcMfIds,
    recipeCta,
    recipeMfIds,
    overlay,
    overlayMfIds,
    guarantee,
    guaranteeMfIds,
    liveViewers,
    liveViewersMfIds,
    categoryFallback,
    heroVideo,
    heroVideoFieldIds,
    heroVideos,
    heroVideosMfId: heroVideosMf?.id || null,
    stickyAtcDropdowns,
    stickyAtcDropdownsFieldId,
    stickyAtcHideHeader,
    stickyAtcHideHeaderFieldId,
    trueblueEnabled,
    trueblueEnabledExplicit,
    trueblueEnabledFieldId,
    truebluePosition,
    truebluePositionFieldId,
    gridRowTags,
    gridRowTagsMfId,
    gridRowTagsFieldId,
    // Cross-store mapping keys — used to mirror grid_row_tags to PROD.
    customUrl: productRes?.data?.custom_url?.url || '',
    sku: productRes?.data?.sku || '',
    productName: productRes?.data?.name || '',
    bcVideos,
    primaryImageUrl,
    mediaOrder,
    mediaOrderMfId: mediaOrderMf?.id || null,
    imageSources,
    imageSourcesMfId: imageSourcesMf?.id || null,
    thumbsPosition,
    thumbsPositionMfId: thumbsPositionMf?.id || null,
  };
}

// ── Grid Row Tags PROD mirror ────────────────────────────────────────────────
// The Site Content editor targets sandbox-2 (see bcApi/env). Grid Row Tags drive
// the storefront "Hot Deal"-style pills, which Greg wants LIVE, so on save we also
// upsert the pdp/grid_row_tags metafield on the matching PRODUCTION product.
//
// Cross-store id mapping: match by custom_url first, then base SKU, then exact
// product NAME. Custom URLs and product-level SKUs can BOTH differ or be empty
// across SB-2 and PROD (e.g. /blue-crabs-steamed/ vs /steamed-maryland-blue-crabs/,
// and variant products often have no product-level SKU), so the name match is
// the reliable backstop. `cleanedTags` is the already-cleaned array (empty =
// delete the field on PROD too). Returns a small result object so the caller can
// surface a non-blocking note; NEVER throws — the PROD mirror must not block the
// primary SB-2 save.
async function resolveProdProductId(sb2Detail) {
  const customUrl = (sb2Detail?.customUrl || '').trim();
  const sku = (sb2Detail?.sku || '').trim();
  // Match by custom_url (authoritative — both stores share URL paths).
  if (customUrl) {
    const byUrl = await bcApiStore('production', 'GET',
      `/v3/catalog/products?url=${encodeURIComponent(customUrl)}&include_fields=sku,custom_url&limit=2`);
    const hit = (byUrl?.data || []).find(p => (p.custom_url?.url || '') === customUrl);
    if (hit) return hit.id;
  }
  // Fallback: exact base SKU match.
  if (sku) {
    const bySku = await bcApiStore('production', 'GET',
      `/v3/catalog/products?sku=${encodeURIComponent(sku)}&include_fields=sku&limit=2`);
    const hit = (bySku?.data || []).find(p => (p.sku || '') === sku);
    if (hit) return hit.id;
  }
  // Final fallback: exact product NAME match. URL + SKU can both miss (differing
  // custom_urls / empty product-level SKU on variant products), but product
  // names are identical across stores.
  const name = (sb2Detail?.productName || '').trim();
  if (name) {
    const byName = await bcApiStore('production', 'GET',
      `/v3/catalog/products?name=${encodeURIComponent(name)}&include_fields=name&limit=5`);
    const hit = (byName?.data || []).find(p => (p.name || '').trim() === name);
    if (hit) return hit.id;
  }
  return null;
}

async function mirrorGridRowTagsToProd(sb2Detail, cleanedTags) {
  try {
    const prodId = await resolveProdProductId(sb2Detail);
    if (!prodId) {
      return { ok: false, reason: 'no_prod_match' };
    }
    // Find the PROD product's existing pdp/grid_row_tags metafield id.
    const mfRes = await bcApiStore('production', 'GET',
      `/v3/catalog/products/${prodId}/metafields?namespace=${PDP_NS}&limit=50`);
    const existing = (mfRes?.data || []).find(m => m.key === GRID_ROW_TAGS_KEY) || null;
    const existingId = existing?.id || null;
    if (cleanedTags.length > 0) {
      const value = JSON.stringify(cleanedTags);
      const body = { key: GRID_ROW_TAGS_KEY, value, namespace: PDP_NS, permission_set: 'read_and_sf_access' };
      if (existingId) {
        await bcApiStore('production', 'PUT',
          `/v3/catalog/products/${prodId}/metafields/${existingId}`, body);
      } else {
        await bcApiStore('production', 'POST',
          `/v3/catalog/products/${prodId}/metafields`, body);
      }
    } else if (existingId) {
      await bcApiStore('production', 'DELETE',
        `/v3/catalog/products/${prodId}/metafields/${existingId}`);
    }
    return { ok: true, prodId };
  } catch (err) {
    return { ok: false, reason: 'error', message: err.message };
  }
}

// Shown when there's no admin session (or the server rejects one) on an upload.
// upload-product-image is admin-gated, so a token-less request just 401s with a
// cryptic "Unauthorized" — surface a clear re-login prompt instead.
const SESSION_EXPIRED_MSG = 'Your session expired — please sign in again.';

async function uploadProductImage(productId, file, { description, sortOrder, isThumbnail } = {}) {
  const token = getAccessToken();
  if (!token) throw new Error(SESSION_EXPIRED_MSG);
  // BigCommerce rejects HEIC/HEIF, and browsers can't convert it in a canvas.
  // Route it through media-library-upload (server converts to JPEG), then hand BC
  // the resulting public URL to download. Same approach as the Media Library +
  // Social Media Builder uploaders.
  const isHeic = /image\/hei[cf]/i.test(file.type || '') || /\.(heic|heif)$/i.test(file.name || '');
  if (isHeic) {
    const heicDataUrl = await readFileAsBase64(file);
    const rawBase64 = String(heicDataUrl).slice(String(heicDataUrl).indexOf(',') + 1);
    const conv = await fetch(BASE_URL + '/media-library-upload', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename: file.name, contentType: 'image/heic', originalBase64: rawBase64 }),
    });
    const cd = await conv.json().catch(() => ({}));
    if (!conv.ok) throw new Error(cd?.error || 'HEIC conversion failed');
    const key = (cd.row || cd || {}).r2_original_key;
    if (!key) throw new Error('HEIC conversion returned no image');
    const jpegUrl = `https://cpx-prototype.netlify.app/.netlify/functions/r2-image?key=${encodeURIComponent(key)}`;
    return uploadProductImageFromUrl(productId, jpegUrl, { description, sortOrder, isThumbnail });
  }
  const dataUrl = await readFileAsBase64(file);
  const res = await fetch(UPLOAD_PRODUCT_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      productId,
      env: getActiveEnv(),
      imageBase64: dataUrl,
      filename: file.name,
      contentType: file.type || 'image/jpeg',
      description,
      sortOrder,
      isThumbnail,
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 401) throw new Error(SESSION_EXPIRED_MSG);
  if (!res.ok) throw new Error(data?.error || 'Upload failed');
  return data;
}

// Add a BC product image by URL — BC downloads it server-side. Used for
// "Pick from Media Library" so large R2 originals never hit the 5 MB cap.
async function uploadProductImageFromUrl(productId, imageUrl, { description, sortOrder, isThumbnail } = {}) {
  const token = getAccessToken();
  if (!token) throw new Error(SESSION_EXPIRED_MSG);
  const res = await fetch(UPLOAD_PRODUCT_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ productId, env: getActiveEnv(), imageUrl, description, sortOrder, isThumbnail }),
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 401) throw new Error(SESSION_EXPIRED_MSG);
  if (!res.ok) throw new Error(data?.error || 'Upload failed');
  return data;
}

// ── Component ────────────────────────────────────────────────────────────────
export default function SiteContentProductTab() {
  // subtab: 'products' (browser+editor) | 'tag-audit'
  const [subtab, setSubtab] = useState('products');

  const [products, setProducts] = useState([]);
  const [categoriesById, setCategoriesById] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState('');
  const [status, setStatus] = useState(null);
  const [search, setSearch] = useState('');
  const [enabledOnly, setEnabledOnly] = useState(true);

  // selected product + detail
  const [selectedId, setSelectedId] = useState(() => {
    try {
      const saved = localStorage.getItem('cpx.sitecontent.pdpProductId');
      const n = saved ? Number(saved) : null;
      return Number.isFinite(n) && n > 0 ? n : null;
    } catch { return null; }
  });
  const [detail, setDetail] = useState(null); // { images, options, optionBadges, optionBadgesMfId, appliedTagKeys, tagMetafieldIds }
  const [detailLoading, setDetailLoading] = useState(false);

  // edits on the detail view
  const [imageEdits, setImageEdits] = useState({}); // { [imageId]: { description?, sort_order?, is_thumbnail?, deleted? } }
  const [optionBadgeEdits, setOptionBadgeEdits] = useState(null); // null (no changes) | object (replacement)
  const [pillCountsEdits, setPillCountsEdits] = useState(null); // null | { label, showOnOptionId, variesByOptionId, matrix }
  const [tagEdits, setTagEdits] = useState(null); // null | { add: Set, remove: Set }
  const [bannerEdits, setBannerEdits] = useState(null); // null | { enabled?, title?, subhead?, image_url? }
  const [tooltipEdits, setTooltipEdits] = useState(null); // null | { quantity?, seasoning? }
  const [marqueeEdits, setMarqueeEdits] = useState(null); // null | { enabled?, items? }
  const [ugcEdits, setUgcEdits] = useState(null); // null | { enabled?, heading?, subhead?, items? }
  const [recipeEdits, setRecipeEdits] = useState(null); // null | { enabled?, photos?, buttonText?, bgColor?, textColor?, showOnThumbnails? }
  const [overlayEdits, setOverlayEdits] = useState(null); // null | { enabled?, text?, teaser?, fontSize?, textColor?, bgColor?, bgOpacity?, startExpanded? }
  const [guaranteeEdits, setGuaranteeEdits] = useState(null); // null | { title?, lede?, body?, guaranteeTitle?, guaranteeBody? }
  const [liveViewersEdits, setLiveViewersEdits] = useState(null); // null | { enabled?: true|false|null, minCount?: number|null }
  const [trueblueEdits, setTrueblueEdits] = useState(null); // null | { enabled?, position? } — product 132 only
  const [gridRowTagsEdits, setGridRowTagsEdits] = useState(null); // null | array of { crab, size, label, color }
  const [thumbsPositionEdit, setThumbsPositionEdit] = useState(null); // null = no edit; otherwise the new value
  const [customIcons, setCustomIcons] = useState([]); // library: [{slug, name, url}, ...]
  const [customIconsLoading, setCustomIconsLoading] = useState(false);
  const [iconUploading, setIconUploading] = useState(false);
  const [marqueePresets, setMarqueePresets] = useState([]); // [{id, label, icon, text, banner_heading, banner_subhead}, ...]
  const [marqueePresetsLoading, setMarqueePresetsLoading] = useState(false);
  const [variantImageEdits, setVariantImageEdits] = useState({}); // { [option_value_id]: imageId | null } — null = clear assignment
  const [saving, setSaving] = useState(false);

  // upload
  const [uploading, setUploading] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [uploadingLibrary, setUploadingLibrary] = useState(false);
  const [replacingId, setReplacingId] = useState(null);
  const fileInputRef = useRef(null);
  const bannerFileInputRef = useRef(null);
  const libraryFileInputRef = useRef(null);
  const replaceFileInputRef = useRef(null);
  const pendingReplaceIdRef = useRef(null);
  const [preview, setPreview] = useState(null);

  // Tag Audit state (uses the product list, which carries tag applied-state once loaded)
  const [auditTagKey, setAuditTagKey] = useState(PRODUCT_TAGS[0]?.key || '');
  const [auditFilter, setAuditFilter] = useState('all');
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditRows, setAuditRows] = useState([]); // rows with per-row applied/pending state for the currently-audited tag
  const [auditEdits, setAuditEdits] = useState({}); // { [productId]: 'add' | 'remove' }
  const [auditSaving, setAuditSaving] = useState(false);

  // Site-wide PDP behavior defaults (Neon table pdp_defaults). Per-product
  // custom fields override; admin manages via PdpDefaultsPanel below.
  const [pdpDefaults, setPdpDefaults] = useState({}); // { stickyAtcDropdowns: bool, stickyAtcHideHeader: bool }
  const [pdpDefaultsRaw, setPdpDefaultsRaw] = useState({}); // raw string values for non-boolean defaults (e.g. live_viewers_min_count)
  const [pdpDefaultsLoading, setPdpDefaultsLoading] = useState(false);

  const loadPdpDefaults = useCallback(async () => {
    setPdpDefaultsLoading(true);
    try {
      const rows = await neonQuery('pdp_defaults_get', { env: getActiveEnv() });
      const next = {};
      const nextRaw = {};
      (Array.isArray(rows) ? rows : []).forEach(r => {
        next[r.key] = String(r.value || '').toLowerCase() === 'true';
        nextRaw[r.key] = r.value == null ? '' : String(r.value);
      });
      setPdpDefaults(next);
      setPdpDefaultsRaw(nextRaw);
    } catch (e) {
      console.error('pdp_defaults_get failed:', e.message);
    } finally {
      setPdpDefaultsLoading(false);
    }
  }, []);
  useEffect(() => { loadPdpDefaults(); }, [loadPdpDefaults]);

  const setPdpDefault = useCallback(async (key, value) => {
    try {
      await neonQuery('pdp_defaults_set', { env: getActiveEnv(), key, value: value ? 'true' : 'false' });
      setPdpDefaults(prev => ({ ...prev, [key]: !!value }));
    } catch (e) {
      setStatus({ type: 'error', msg: `Default save failed: ${e.message}` });
      throw e;
    }
  }, []);

  // Non-boolean default setter (raw string). Used for live_viewers_min_count,
  // which is an integer-as-string rather than a 'true'/'false' toggle.
  const setPdpDefaultRaw = useCallback(async (key, value) => {
    const v = String(value == null ? '' : value);
    try {
      await neonQuery('pdp_defaults_set', { env: getActiveEnv(), key, value: v });
      setPdpDefaultsRaw(prev => ({ ...prev, [key]: v }));
    } catch (e) {
      setStatus({ type: 'error', msg: `Default save failed: ${e.message}` });
      throw e;
    }
  }, []);

  // ── Initial load ───────────────────────────────────────────────────────────
  const loadAll = useCallback(async () => {
    setLoading(true);
    setStatus(null);
    try {
      setLoadingMsg('Loading products...');
      const [cats, prods] = await Promise.all([
        fetchAllPages('/v3/catalog/categories'),
        loadProductList(),
      ]);
      const catMap = Object.fromEntries(cats.map(c => [c.id, c.name]));
      setCategoriesById(catMap);
      const rows = prods.map(p => ({
        id: p.id,
        name: p.name,
        sku: p.sku || '',
        isVisible: p.is_visible,
        primaryImage: p.primary_image?.url_thumbnail || p.primary_image?.url_standard || null,
        categoryNames: (p.categories || []).map(id => catMap[id]).filter(Boolean).join(', '),
      }));
      rows.sort((a, b) => a.name.localeCompare(b.name));
      setProducts(rows);
    } catch (e) {
      setStatus({ type: 'error', msg: e.message });
    } finally {
      setLoading(false);
      setLoadingMsg('');
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Marquee icon library (shared across products) ──────────────────────────
  const loadCustomIcons = useCallback(async () => {
    setCustomIconsLoading(true);
    try {
      const rows = await neonQuery('marquee_icons_list', { env: getActiveEnv() });
      setCustomIcons(Array.isArray(rows) ? rows : []);
    } catch (e) {
      console.error('marquee_icons_list failed:', e.message);
    } finally {
      setCustomIconsLoading(false);
    }
  }, []);
  useEffect(() => { loadCustomIcons(); }, [loadCustomIcons]);

  const uploadMarqueeIcon = useCallback(async (file, name) => {
    if (!file) return null;
    if (file.size > MAX_MARQUEE_ICON_BYTES) {
      setStatus({ type: 'error', msg: `Icon too large (${(file.size / 1024).toFixed(0)} KB). Max ${MAX_MARQUEE_ICON_BYTES / 1024} KB.` });
      return null;
    }
    setIconUploading(true);
    try {
      const dataUrl = await readFileAsBase64(file);
      const base64 = (dataUrl.split(',')[1]) || '';
      const uploadRes = await fetch(UPLOAD_MARQUEE_ICON_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: file.name, contentType: file.type || 'image/svg+xml', data: base64 }),
      });
      const uploadData = await uploadRes.json().catch(() => ({}));
      if (!uploadRes.ok) throw new Error(uploadData?.error || 'Upload failed');

      // Derive slug from name (admin-entered or filename)
      const displayName = (name || file.name.replace(/\.(svg|png|webp|jpe?g)$/i, '')).trim();
      const slug = displayName.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-|-$/g, '').slice(0, 80);
      if (!slug) throw new Error('Give the icon a name (letters/numbers)');

      const row = await neonQuery('marquee_icons_upsert', {
        env: getActiveEnv(),
        slug,
        name: displayName,
        url: uploadData.url,
        r2_key: uploadData.key,
      });
      await loadCustomIcons();
      setStatus({ type: 'ok', msg: `Uploaded "${displayName}" to the marquee icon library.` });
      return row;
    } catch (e) {
      setStatus({ type: 'error', msg: `Icon upload failed: ${e.message}` });
      return null;
    } finally {
      setIconUploading(false);
    }
  }, [loadCustomIcons]);

  const deleteMarqueeIcon = useCallback(async (slug) => {
    try {
      await neonQuery('marquee_icons_delete', { slug, env: getActiveEnv() });
      await loadCustomIcons();
      setStatus({ type: 'ok', msg: `Removed icon "${slug}" from the library. The R2 asset is preserved so existing products still render.` });
    } catch (e) {
      setStatus({ type: 'error', msg: `Delete failed: ${e.message}` });
    }
  }, [loadCustomIcons]);

  // ── Marquee item presets (shared across PDPs, snapshot model) ──────────────
  // Picking a preset copies icon/text/headline/description into a row; later
  // edits to the preset don't propagate to PDPs that already used it.
  const loadMarqueePresets = useCallback(async () => {
    setMarqueePresetsLoading(true);
    try {
      const rows = await neonQuery('marquee_presets_list', { env: getActiveEnv() });
      setMarqueePresets(Array.isArray(rows) ? rows : []);
    } catch (e) {
      console.error('marquee_presets_list failed:', e.message);
    } finally {
      setMarqueePresetsLoading(false);
    }
  }, []);
  useEffect(() => { loadMarqueePresets(); }, [loadMarqueePresets]);

  const saveMarqueePreset = useCallback(async ({ id, label, icon, text, banner_heading, banner_subhead }) => {
    const trimmed = String(label || '').trim();
    if (!trimmed) {
      setStatus({ type: 'error', msg: 'Preset name required.' });
      return null;
    }
    try {
      const row = await neonQuery('marquee_presets_upsert', {
        env: getActiveEnv(),
        id: id || undefined,
        label: trimmed,
        icon: icon || '',
        text: text || '',
        banner_heading: banner_heading || '',
        banner_subhead: banner_subhead || '',
      });
      await loadMarqueePresets();
      setStatus({ type: 'ok', msg: id ? `Updated preset "${trimmed}".` : `Saved preset "${trimmed}".` });
      return Array.isArray(row) ? row[0] : row;
    } catch (e) {
      setStatus({ type: 'error', msg: `Preset save failed: ${e.message}` });
      return null;
    }
  }, [loadMarqueePresets]);

  const deleteMarqueePreset = useCallback(async (id) => {
    try {
      await neonQuery('marquee_presets_delete', { id, env: getActiveEnv() });
      await loadMarqueePresets();
      setStatus({ type: 'ok', msg: 'Preset removed. PDPs already using it keep their copied values.' });
    } catch (e) {
      setStatus({ type: 'error', msg: `Delete failed: ${e.message}` });
    }
  }, [loadMarqueePresets]);

  // On first mount, if we restored a selectedId from localStorage, load its detail
  // so the editor is populated without the user having to click again.
  useEffect(() => {
    if (!selectedId || detail || detailLoading) return;
    (async () => {
      setDetailLoading(true);
      try {
        const d = await loadProductDetail(selectedId);
        setDetail(d);
      } catch (e) {
        setStatus({ type: 'error', msg: `Failed to load product: ${e.message}` });
      } finally {
        setDetailLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Select product & load detail ───────────────────────────────────────────
  const selectProduct = useCallback(async (id) => {
    setSelectedId(id);
    try {
      if (id) localStorage.setItem('cpx.sitecontent.pdpProductId', String(id));
      else localStorage.removeItem('cpx.sitecontent.pdpProductId');
    } catch {}
    setDetail(null);
    setImageEdits({});
    setOptionBadgeEdits(null);
    setPillCountsEdits(null);
    setTagEdits(null);
    setBannerEdits(null);
    setTooltipEdits(null);
    setMarqueeEdits(null);
    setUgcEdits(null);
    setRecipeEdits(null);
    setOverlayEdits(null);
    setGuaranteeEdits(null);
    setLiveViewersEdits(null);
    setTrueblueEdits(null);
    setGridRowTagsEdits(null);
    setThumbsPositionEdit(null);
    setVariantImageEdits({});
    if (!id) return;
    setDetailLoading(true);
    try {
      const d = await loadProductDetail(id);
      setDetail(d);
    } catch (e) {
      setStatus({ type: 'error', msg: `Failed to load product: ${e.message}` });
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const reloadDetail = useCallback(async () => {
    if (!selectedId) return;
    setDetailLoading(true);
    try {
      const d = await loadProductDetail(selectedId);
      setDetail(d);
      setImageEdits({});
      setOptionBadgeEdits(null);
      setPillCountsEdits(null);
      setTagEdits(null);
      setGridRowTagsEdits(null);
      setVariantImageEdits({});
    } finally {
      setDetailLoading(false);
    }
  }, [selectedId]);

  // ── Image edits ────────────────────────────────────────────────────────────
  const patchImageEdit = (imageId, patch) => {
    setImageEdits(prev => ({ ...prev, [imageId]: { ...(prev[imageId] || {}), ...patch } }));
  };

  const getImageField = (img, field) => {
    const e = imageEdits[img.id];
    if (e && field in e) return e[field];
    return img[field];
  };

  const moveImage = (imageId, direction) => {
    if (!detail) return;
    const visible = detail.images.filter(img => !imageEdits[img.id]?.deleted);
    // current order derived from sort overrides
    const withSort = visible.map(img => ({ img, sort: getImageField(img, 'sort_order') ?? 0 }))
      .sort((a, b) => a.sort - b.sort || a.img.id - b.img.id);
    const idx = withSort.findIndex(x => x.img.id === imageId);
    if (idx < 0) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= withSort.length) return;
    // reassign sort_order for every item to be its new index (clean + predictable)
    const nextOrder = withSort.slice();
    const tmp = nextOrder[idx];
    nextOrder[idx] = nextOrder[swapIdx];
    nextOrder[swapIdx] = tmp;
    setImageEdits(prev => {
      const next = { ...prev };
      nextOrder.forEach((entry, i) => {
        if ((entry.img.sort_order ?? 0) !== i) {
          next[entry.img.id] = { ...(next[entry.img.id] || {}), sort_order: i };
        } else if (next[entry.img.id]) {
          const { sort_order: _drop, ...rest } = next[entry.img.id];
          if (Object.keys(rest).length === 0) delete next[entry.img.id]; else next[entry.img.id] = rest;
        }
      });
      return next;
    });
  };

  const toggleDeleteImage = (imageId) => {
    patchImageEdit(imageId, { deleted: !imageEdits[imageId]?.deleted });
  };

  const setThumbnail = (imageId) => {
    // Mark this one as thumbnail, clear the others
    if (!detail) return;
    setImageEdits(prev => {
      const next = { ...prev };
      for (const img of detail.images) {
        const isTargetTrue = img.id === imageId;
        const original = img.is_thumbnail;
        if (original === isTargetTrue) {
          // Reset edit if it matches original
          if (next[img.id]) {
            const { is_thumbnail: _d, ...rest } = next[img.id];
            if (Object.keys(rest).length === 0) delete next[img.id]; else next[img.id] = rest;
          }
        } else {
          next[img.id] = { ...(next[img.id] || {}), is_thumbnail: isTargetTrue };
        }
      }
      // BigCommerce's storefront uses the image with the LOWEST sort_order as
      // the main PDP photo — the is_thumbnail flag alone does NOT move it. So
      // when an image becomes the primary, also push it to sort_order 0 and
      // renumber the rest, mirroring what BC's own admin does. Without this
      // the storefront keeps showing the oldest (lowest sort_order) image.
      const effSort = (img) => {
        const e = next[img.id];
        return (e && 'sort_order' in e) ? e.sort_order : (img.sort_order ?? 0);
      };
      const live = detail.images.filter(img => !next[img.id]?.deleted);
      const ordered = live.slice().sort((a, b) => effSort(a) - effSort(b) || a.id - b.id);
      const primary = ordered.find(i => i.id === imageId);
      const finalOrder = primary ? [primary, ...ordered.filter(i => i.id !== imageId)] : ordered;
      finalOrder.forEach((img, idx) => {
        if ((img.sort_order ?? 0) === idx) {
          // Matches the original sort_order — drop any staged change.
          if (next[img.id] && 'sort_order' in next[img.id]) {
            const { sort_order: _s, ...keep } = next[img.id];
            if (Object.keys(keep).length === 0) delete next[img.id]; else next[img.id] = keep;
          }
        } else {
          next[img.id] = { ...(next[img.id] || {}), sort_order: idx };
        }
      });
      return next;
    });
  };

  // ── Option badge edits ─────────────────────────────────────────────────────
  const currentOptionBadges = optionBadgeEdits != null ? optionBadgeEdits : (detail?.optionBadges || {});

  const patchOptionBadge = (optionValueId, field, value) => {
    const key = String(optionValueId);
    const base = { ...currentOptionBadges };
    const entry = { ...(base[key] || {}) };
    entry[field] = value;
    base[key] = entry;
    setOptionBadgeEdits(base);
  };

  const optionBadgesDirty = optionBadgeEdits != null && !optionBadgesEqual(cleanOptionBadges(optionBadgeEdits), cleanOptionBadges(detail?.optionBadges || {}));

  // ── Pill counts edits ──────────────────────────────────────────────────────
  const currentPillCounts = pillCountsEdits != null ? pillCountsEdits : (detail?.pillCounts || null);
  const patchPillCounts = (partial) => {
    setPillCountsEdits((prev) => {
      const base = prev != null ? prev : (detail?.pillCounts ? { ...detail.pillCounts, matrix: { ...(detail.pillCounts.matrix || {}) } } : { label: '', showOnOptionId: null, variesByOptionId: null, matrix: {} });
      return { ...base, ...partial };
    });
  };
  const patchPillCount = (showOnOvId, variesByOvId, value) => {
    setPillCountsEdits((prev) => {
      const base = prev != null ? prev : (detail?.pillCounts ? { ...detail.pillCounts, matrix: { ...(detail.pillCounts.matrix || {}) } } : { label: '', showOnOptionId: null, variesByOptionId: null, matrix: {} });
      const matrix = { ...(base.matrix || {}) };
      const row = { ...(matrix[String(showOnOvId)] || {}) };
      const key = variesByOvId == null ? '_' : String(variesByOvId);
      if (value === '' || value == null) delete row[key];
      else row[key] = value;
      if (Object.keys(row).length === 0) delete matrix[String(showOnOvId)];
      else matrix[String(showOnOvId)] = row;
      return { ...base, matrix };
    });
  };
  const pillCountsDirty = pillCountsEdits != null && !pillCountsEqual(cleanPillCounts(pillCountsEdits), cleanPillCounts(detail?.pillCounts || null));

  // ── Grid row tags edits ────────────────────────────────────────────────────
  const currentGridRowTags = gridRowTagsEdits != null ? gridRowTagsEdits : (detail?.gridRowTags || []);
  const addGridRowTag = () => {
    const base = gridRowTagsEdits != null ? gridRowTagsEdits : (detail?.gridRowTags || []);
    setGridRowTagsEdits([...base, { crab: '', size: '', label: GRID_ROW_TAG_LABEL_DEFAULT, color: GRID_ROW_TAG_COLOR_DEFAULT }]);
  };
  const patchGridRowTag = (index, partial) => {
    const base = gridRowTagsEdits != null ? gridRowTagsEdits : (detail?.gridRowTags || []);
    const next = base.map((t, i) => (i === index ? { ...t, ...partial } : t));
    setGridRowTagsEdits(next);
  };
  const removeGridRowTag = (index) => {
    const base = gridRowTagsEdits != null ? gridRowTagsEdits : (detail?.gridRowTags || []);
    setGridRowTagsEdits(base.filter((_, i) => i !== index));
  };
  const gridRowTagsDirty = gridRowTagsEdits != null && !gridRowTagsEqual(gridRowTagsEdits, detail?.gridRowTags || []);

  // ── Tag edits ──────────────────────────────────────────────────────────────
  const currentTagsSet = useMemo(() => {
    const base = detail?.appliedTagKeys || new Set();
    if (!tagEdits) return new Set(base);
    const out = new Set(base);
    for (const k of tagEdits.add) out.add(k);
    for (const k of tagEdits.remove) out.delete(k);
    return out;
  }, [detail, tagEdits]);

  const toggleTag = (tagKey) => {
    if (!detail) return;
    const wasApplied = detail.appliedTagKeys.has(tagKey);
    const isOn = currentTagsSet.has(tagKey);
    const add = new Set(tagEdits?.add || []);
    const remove = new Set(tagEdits?.remove || []);
    if (isOn) {
      add.delete(tagKey);
      if (wasApplied) remove.add(tagKey);
    } else {
      remove.delete(tagKey);
      if (!wasApplied) add.add(tagKey);
    }
    if (add.size === 0 && remove.size === 0) setTagEdits(null);
    else setTagEdits({ add, remove });
  };

  // ── Banner edits ───────────────────────────────────────────────────────────
  const bannerValue = (field) => {
    if (bannerEdits && field in bannerEdits) return bannerEdits[field];
    if (field === 'enabled' || field === 'sticky') return !!detail?.banner?.[field];
    if (field === 'wave_enabled') {
      // Default to ON when no metafield exists yet — matches storefront default.
      return detail?.banner?.wave_enabled == null ? BANNER_WAVE_ENABLED_DEFAULT : !!detail.banner.wave_enabled;
    }
    return detail?.banner?.[field] || '';
  };
  const patchBanner = (field, value) => {
    setBannerEdits((prev) => ({ ...(prev || {}), [field]: value }));
  };
  const bannerDirty = bannerEdits != null && Object.keys(bannerEdits).some((k) => {
    if (k === 'enabled' || k === 'sticky' || k === 'wave_enabled') {
      const baseline = k === 'wave_enabled'
        ? (detail?.banner?.wave_enabled == null ? BANNER_WAVE_ENABLED_DEFAULT : !!detail.banner.wave_enabled)
        : !!detail?.banner?.[k];
      return (!!bannerEdits[k]) !== baseline;
    }
    return (bannerEdits[k] || '') !== (detail?.banner?.[k] || '');
  });

  // ── Tooltip edits ──────────────────────────────────────────────────────────
  const tooltipValue = (key) => {
    if (tooltipEdits && key in tooltipEdits) return tooltipEdits[key];
    return detail?.tooltips?.[key] || '';
  };
  const patchTooltip = (key, value) => {
    setTooltipEdits((prev) => ({ ...(prev || {}), [key]: value }));
  };
  const tooltipDirty = tooltipEdits != null && Object.keys(tooltipEdits).some((k) => {
    return (tooltipEdits[k] || '') !== (detail?.tooltips?.[k] || '');
  });

  // ── Marquee edits ──────────────────────────────────────────────────────────
  const marqueeEnabled = marqueeEdits && 'enabled' in marqueeEdits
    ? !!marqueeEdits.enabled
    : !!detail?.marquee?.enabled;
  const marqueeItems = marqueeEdits && 'items' in marqueeEdits
    ? marqueeEdits.items
    : (detail?.marquee?.items || []);
  const marqueeDuration = marqueeEdits && 'duration_sec' in marqueeEdits
    ? marqueeEdits.duration_sec
    : (detail?.marquee?.duration_sec ?? null);
  const marqueePaddingY = marqueeEdits && 'padding_y_px' in marqueeEdits
    ? marqueeEdits.padding_y_px
    : (detail?.marquee?.padding_y_px ?? null);
  const marqueeSticky = marqueeEdits && 'sticky' in marqueeEdits
    ? !!marqueeEdits.sticky
    : !!detail?.marquee?.sticky;
  const marqueeColors = marqueeEdits && 'colors' in marqueeEdits
    ? marqueeEdits.colors
    : (detail?.marquee?.colors || { bg: '', text: '', icon: '', flash: '' });
  const marqueeFlashEffect = marqueeEdits && 'flashEffect' in marqueeEdits
    ? marqueeEdits.flashEffect
    : (detail?.marquee?.flashEffect || '');
  const marqueeLinkBanner = marqueeEdits && 'linkBanner' in marqueeEdits
    ? !!marqueeEdits.linkBanner
    : !!detail?.marquee?.linkBanner;
  const marqueeScrollMode = marqueeEdits && 'scrollMode' in marqueeEdits
    ? marqueeEdits.scrollMode
    : (detail?.marquee?.scrollMode || '');
  const marqueeRotateIntervalSec = marqueeEdits && 'rotateIntervalSec' in marqueeEdits
    ? marqueeEdits.rotateIntervalSec
    : (detail?.marquee?.rotateIntervalSec ?? null);
  const patchMarqueeEnabled = (v) => setMarqueeEdits(prev => ({ ...(prev || {}), enabled: !!v }));
  const patchMarqueeItems = (items) => setMarqueeEdits(prev => ({ ...(prev || {}), items }));
  // Coerce numeric inputs to Number so the dirty check matches the reloaded
  // detail value (which is also Number). Without this, a saved value of 6
  // (number) compared to the still-typed '6' (string) keeps dirty=true and
  // the floating Save bar refuses to disappear after a successful save.
  const toNumOrNull = (v) => (v == null || v === '') ? null : Number(v);
  const patchMarqueeDuration = (v) => setMarqueeEdits(prev => ({ ...(prev || {}), duration_sec: toNumOrNull(v) }));
  const patchMarqueePaddingY = (v) => setMarqueeEdits(prev => ({ ...(prev || {}), padding_y_px: toNumOrNull(v) }));
  const patchMarqueeSticky = (v) => setMarqueeEdits(prev => ({ ...(prev || {}), sticky: !!v }));
  const patchMarqueeColor = (slot, presetKey) => setMarqueeEdits(prev => {
    const baseline = detail?.marquee?.colors || { bg: '', text: '', icon: '', flash: '' };
    const current = (prev && 'colors' in prev) ? prev.colors : baseline;
    return { ...(prev || {}), colors: { ...current, [slot]: presetKey || '' } };
  });
  const patchMarqueeFlashEffect = (v) => setMarqueeEdits(prev => ({ ...(prev || {}), flashEffect: v || '' }));
  const patchMarqueeLinkBanner = (v) => setMarqueeEdits(prev => ({ ...(prev || {}), linkBanner: !!v }));
  const patchMarqueeScrollMode = (v) => setMarqueeEdits(prev => ({ ...(prev || {}), scrollMode: v || '' }));
  const patchMarqueeRotateInterval = (v) => setMarqueeEdits(prev => ({ ...(prev || {}), rotateIntervalSec: toNumOrNull(v) }));
  const marqueeMobileScrollMode = marqueeEdits && 'mobileScrollMode' in marqueeEdits
    ? marqueeEdits.mobileScrollMode
    : (detail?.marquee?.mobileScrollMode || '');
  const marqueeMobileStaticItemIndex = marqueeEdits && 'mobileStaticItemIndex' in marqueeEdits
    ? marqueeEdits.mobileStaticItemIndex
    : (detail?.marquee?.mobileStaticItemIndex ?? null);
  const patchMarqueeMobileScrollMode = (v) => setMarqueeEdits(prev => ({ ...(prev || {}), mobileScrollMode: v || '' }));
  const patchMarqueeMobileStaticItemIndex = (v) => setMarqueeEdits(prev => ({ ...(prev || {}), mobileStaticItemIndex: toNumOrNull(v) }));
  const marqueeDirty = marqueeEdits != null && (
    ('enabled' in marqueeEdits && (!!marqueeEdits.enabled) !== (!!detail?.marquee?.enabled)) ||
    ('items' in marqueeEdits && !marqueeItemsEqual(marqueeEdits.items, detail?.marquee?.items || [])) ||
    ('duration_sec' in marqueeEdits && (marqueeEdits.duration_sec ?? null) !== (detail?.marquee?.duration_sec ?? null)) ||
    ('padding_y_px' in marqueeEdits && (marqueeEdits.padding_y_px ?? null) !== (detail?.marquee?.padding_y_px ?? null)) ||
    ('sticky' in marqueeEdits && (!!marqueeEdits.sticky) !== (!!detail?.marquee?.sticky)) ||
    ('colors' in marqueeEdits && ['bg','text','icon','flash'].some(k => (marqueeEdits.colors?.[k] || '') !== (detail?.marquee?.colors?.[k] || ''))) ||
    ('flashEffect' in marqueeEdits && (marqueeEdits.flashEffect || '') !== (detail?.marquee?.flashEffect || '')) ||
    ('linkBanner' in marqueeEdits && (!!marqueeEdits.linkBanner) !== (!!detail?.marquee?.linkBanner)) ||
    ('scrollMode' in marqueeEdits && (marqueeEdits.scrollMode || '') !== (detail?.marquee?.scrollMode || '')) ||
    ('rotateIntervalSec' in marqueeEdits && (marqueeEdits.rotateIntervalSec ?? null) !== (detail?.marquee?.rotateIntervalSec ?? null)) ||
    ('mobileScrollMode' in marqueeEdits && (marqueeEdits.mobileScrollMode || '') !== (detail?.marquee?.mobileScrollMode || '')) ||
    ('mobileStaticItemIndex' in marqueeEdits && (marqueeEdits.mobileStaticItemIndex ?? null) !== (detail?.marquee?.mobileStaticItemIndex ?? null))
  );

  // ── UGC edits ──────────────────────────────────────────────────────────────
  const ugcEnabled = ugcEdits && 'enabled' in ugcEdits
    ? !!ugcEdits.enabled
    : !!detail?.ugc?.enabled;
  const ugcHeading = ugcEdits && 'heading' in ugcEdits
    ? ugcEdits.heading
    : (detail?.ugc?.heading || '');
  const ugcSubhead = ugcEdits && 'subhead' in ugcEdits
    ? ugcEdits.subhead
    : (detail?.ugc?.subhead || '');
  const ugcLayout = ugcEdits && 'layout' in ugcEdits
    ? ugcEdits.layout
    : (detail?.ugc?.layout || UGC_LAYOUT_DEFAULT);
  const ugcItems = ugcEdits && 'items' in ugcEdits
    ? ugcEdits.items
    : (detail?.ugc?.items || []);
  const patchUgcEnabled = (v) => setUgcEdits(prev => ({ ...(prev || {}), enabled: !!v }));
  const patchUgcHeading = (v) => setUgcEdits(prev => ({ ...(prev || {}), heading: v }));
  const patchUgcSubhead = (v) => setUgcEdits(prev => ({ ...(prev || {}), subhead: v }));
  const patchUgcLayout = (v) => setUgcEdits(prev => ({ ...(prev || {}), layout: UGC_LAYOUTS.includes(v) ? v : UGC_LAYOUT_DEFAULT }));
  const patchUgcItems = (items) => setUgcEdits(prev => ({ ...(prev || {}), items }));
  const ugcDirty = ugcEdits != null && (
    ('enabled' in ugcEdits && (!!ugcEdits.enabled) !== (!!detail?.ugc?.enabled)) ||
    ('heading' in ugcEdits && (ugcEdits.heading || '') !== (detail?.ugc?.heading || '')) ||
    ('subhead' in ugcEdits && (ugcEdits.subhead || '') !== (detail?.ugc?.subhead || '')) ||
    ('layout' in ugcEdits && (ugcEdits.layout || UGC_LAYOUT_DEFAULT) !== (detail?.ugc?.layout || UGC_LAYOUT_DEFAULT)) ||
    ('items' in ugcEdits && !ugcItemsEqual(ugcEdits.items, detail?.ugc?.items || []))
  );

  // ── Recipe CTA edits ───────────────────────────────────────────────────────
  // recipeEdits: null | { enabled?, photos?, buttonText?, bgColor?, textColor?, showOnThumbnails? }
  const recipeVal = (key, fallback) => (recipeEdits && key in recipeEdits)
    ? recipeEdits[key]
    : (detail?.recipeCta && key in detail.recipeCta ? detail.recipeCta[key] : fallback);
  const recipeEnabled = !!recipeVal('enabled', false);
  const recipePhotos = recipeVal('photos', []) || [];
  const recipeButtonText = recipeVal('buttonText', '') || '';
  const recipeBgColor = recipeVal('bgColor', '') || '';
  const recipeTextColor = recipeVal('textColor', '') || '';
  const recipeShowOnThumbnails = recipeVal('showOnThumbnails', true) !== false;
  const recipeIcon = recipeVal('icon', RECIPE_ICON_DEFAULT) || RECIPE_ICON_DEFAULT;
  const recipeIconColor = recipeVal('iconColor', '') || '';
  const recipeThumbIconOnly = recipeVal('thumbIconOnly', false) === true;
  const patchRecipe = (partial) => setRecipeEdits(prev => ({ ...(prev || {}), ...partial }));
  const recipeDirty = recipeEdits != null && (
    ('enabled' in recipeEdits && (!!recipeEdits.enabled) !== (!!detail?.recipeCta?.enabled)) ||
    ('photos' in recipeEdits && !recipePhotosEqual(recipeEdits.photos, detail?.recipeCta?.photos || [])) ||
    ('buttonText' in recipeEdits && (recipeEdits.buttonText || '') !== (detail?.recipeCta?.buttonText || '')) ||
    ('bgColor' in recipeEdits && (recipeEdits.bgColor || '') !== (detail?.recipeCta?.bgColor || '')) ||
    ('textColor' in recipeEdits && (recipeEdits.textColor || '') !== (detail?.recipeCta?.textColor || '')) ||
    ('showOnThumbnails' in recipeEdits && (recipeEdits.showOnThumbnails !== false) !== (detail?.recipeCta?.showOnThumbnails !== false)) ||
    ('icon' in recipeEdits && (recipeEdits.icon || RECIPE_ICON_DEFAULT) !== (detail?.recipeCta?.icon || RECIPE_ICON_DEFAULT)) ||
    ('iconColor' in recipeEdits && (recipeEdits.iconColor || '') !== (detail?.recipeCta?.iconColor || '')) ||
    ('thumbIconOnly' in recipeEdits && (recipeEdits.thumbIconOnly === true) !== (detail?.recipeCta?.thumbIconOnly === true))
  );

  // ── Overlay edits ─────────────────────────────────────────────────────────
  // overlayEdits: null | { enabled?, text?, teaser?, fontSize?, textColor?, bgColor?, bgOpacity?, startExpanded? }
  // Numeric inputs return strings — coerce on patch so the dirty check below
  // can compare Number to Number (same gotcha CLAUDE.md flagged for marquee).
  const overlayVal = (key, fallback) => (overlayEdits && key in overlayEdits)
    ? overlayEdits[key]
    : (detail?.overlay && key in detail.overlay ? detail.overlay[key] : fallback);
  const overlayEnabledExplicit = !!detail?.overlay?.enabledExplicit;
  const overlayEnabled = !!overlayVal('enabled', overlayEnabledExplicit ? !!detail?.overlay?.enabled : !!pdpDefaults?.overlayEnabled);
  const overlayText = overlayVal('text', '') || '';
  const overlayTeaser = overlayVal('teaser', '') || '';
  const overlayFontSize = (() => {
    const v = overlayVal('fontSize', OVERLAY_FONT_SIZE_DEFAULT);
    const n = Number(v);
    return Number.isFinite(n) ? n : OVERLAY_FONT_SIZE_DEFAULT;
  })();
  const overlayTextColor = overlayVal('textColor', '') || '';
  const overlayBgColor = overlayVal('bgColor', '') || '';
  const overlayBgOpacity = (() => {
    const v = overlayVal('bgOpacity', OVERLAY_OPACITY_DEFAULT);
    const n = Number(v);
    return Number.isFinite(n) ? n : OVERLAY_OPACITY_DEFAULT;
  })();
  const overlayStartExpanded = !!overlayVal('startExpanded', false);
  const patchOverlay = (partial) => setOverlayEdits(prev => ({ ...(prev || {}), ...partial }));
  const overlayDirty = overlayEdits != null && (
    ('enabled' in overlayEdits && (!!overlayEdits.enabled) !== (!!detail?.overlay?.enabled)) ||
    ('text' in overlayEdits && (overlayEdits.text || '') !== (detail?.overlay?.text || '')) ||
    ('teaser' in overlayEdits && (overlayEdits.teaser || '') !== (detail?.overlay?.teaser || '')) ||
    ('fontSize' in overlayEdits && Number(overlayEdits.fontSize ?? OVERLAY_FONT_SIZE_DEFAULT) !== Number(detail?.overlay?.fontSize ?? OVERLAY_FONT_SIZE_DEFAULT)) ||
    ('textColor' in overlayEdits && (overlayEdits.textColor || '') !== (detail?.overlay?.textColor || '')) ||
    ('bgColor' in overlayEdits && (overlayEdits.bgColor || '') !== (detail?.overlay?.bgColor || '')) ||
    ('bgOpacity' in overlayEdits && Number(overlayEdits.bgOpacity ?? OVERLAY_OPACITY_DEFAULT) !== Number(detail?.overlay?.bgOpacity ?? OVERLAY_OPACITY_DEFAULT)) ||
    ('startExpanded' in overlayEdits && (!!overlayEdits.startExpanded) !== (!!detail?.overlay?.startExpanded))
  );

  // ── Price Guarantee modal edits ───────────────────────────────────────────
  // guaranteeEdits: null | { title?, lede?, body?, guaranteeTitle?, guaranteeBody? }
  // All blank = pdp-banner serves its product-agnostic default copy.
  const guaranteeVal = (key) => (guaranteeEdits && key in guaranteeEdits)
    ? guaranteeEdits[key]
    : (detail?.guarantee && key in detail.guarantee ? detail.guarantee[key] : '');
  const guaranteeTitle = guaranteeVal('title') || '';
  const guaranteeLede = guaranteeVal('lede') || '';
  const guaranteeBody = guaranteeVal('body') || '';
  const guaranteeCardTitle = guaranteeVal('guaranteeTitle') || '';
  const guaranteeCardBody = guaranteeVal('guaranteeBody') || '';
  const patchGuarantee = (partial) => setGuaranteeEdits(prev => ({ ...(prev || {}), ...partial }));
  const guaranteeDirty = guaranteeEdits != null && (
    ('title' in guaranteeEdits && (guaranteeEdits.title || '') !== (detail?.guarantee?.title || '')) ||
    ('lede' in guaranteeEdits && (guaranteeEdits.lede || '') !== (detail?.guarantee?.lede || '')) ||
    ('body' in guaranteeEdits && (guaranteeEdits.body || '') !== (detail?.guarantee?.body || '')) ||
    ('guaranteeTitle' in guaranteeEdits && (guaranteeEdits.guaranteeTitle || '') !== (detail?.guarantee?.guaranteeTitle || '')) ||
    ('guaranteeBody' in guaranteeEdits && (guaranteeEdits.guaranteeBody || '') !== (detail?.guarantee?.guaranteeBody || ''))
  );

  // Live Viewers badge edits.
  // liveViewersEdits: null | { enabled?: true|false|null, minCount?: number|null }
  // enabled null / minCount null = inherit the site default (delete the key).
  const liveViewersVal = (key, fallback) => (liveViewersEdits && key in liveViewersEdits)
    ? liveViewersEdits[key]
    : (detail?.liveViewers && key in detail.liveViewers ? detail.liveViewers[key] : fallback);
  const liveViewersEnabled = liveViewersVal('enabled', null); // true | false | null(inherit)
  const liveViewersMinCount = liveViewersVal('minCount', null); // number | null(inherit)
  const patchLiveViewers = (partial) => setLiveViewersEdits(prev => ({ ...(prev || {}), ...partial }));
  const liveViewersDirty = liveViewersEdits != null && (
    ('enabled' in liveViewersEdits && (liveViewersEdits.enabled ?? null) !== (detail?.liveViewers?.enabled ?? null)) ||
    ('minCount' in liveViewersEdits && (liveViewersEdits.minCount ?? null) !== (detail?.liveViewers?.minCount ?? null))
  );

  // ── True Blue overlay (product 132 only) ──────────────────────────────────
  // Two BC custom fields (pdp-trueblue-enabled, pdp-trueblue-position) drive
  // the in-image overlay AND the floating sticky badge. Section is hidden
  // for any product other than 132 — keeping the option chrome out of view
  // on products where the badge would never make sense.
  const trueblueVal = (key, fallback) => (trueblueEdits && key in trueblueEdits)
    ? trueblueEdits[key]
    : (detail && key in detail ? detail[key] : fallback);
  const trueblueEnabled = trueblueVal('trueblueEnabled', true) !== false;
  const truebluePosition = TRUEBLUE_POSITIONS.includes(trueblueVal('truebluePosition', TRUEBLUE_POSITION_DEFAULT))
    ? trueblueVal('truebluePosition', TRUEBLUE_POSITION_DEFAULT)
    : TRUEBLUE_POSITION_DEFAULT;
  const patchTrueblue = (partial) => setTrueblueEdits(prev => ({ ...(prev || {}), ...partial }));
  const trueblueDirty = trueblueEdits != null && (
    ('trueblueEnabled' in trueblueEdits && (trueblueEdits.trueblueEnabled !== false) !== (detail?.trueblueEnabled !== false)) ||
    ('truebluePosition' in trueblueEdits && (trueblueEdits.truebluePosition || TRUEBLUE_POSITION_DEFAULT) !== (detail?.truebluePosition || TRUEBLUE_POSITION_DEFAULT))
  );

  // ── Thumbs position ───────────────────────────────────────────────────────
  const thumbsPositionValue = thumbsPositionEdit != null ? thumbsPositionEdit : (detail?.thumbsPosition || '');
  const thumbsPositionDirty = thumbsPositionEdit != null && thumbsPositionEdit !== (detail?.thumbsPosition || '');

  // ── Variant image assignments ──────────────────────────────────────────────
  // Source of truth: pdp.variant_image_ids metafield (JSON: { ov_id: image_id }).
  // We maintain this because BC transforms gallery URLs into attribute_rule_images/*
  // when written to variant.image_url, so we can't round-trip via that field alone.
  // Fallback: URL-match against variant.image_url for products bound before the
  // metafield existed.
  const currentVariantImageByValueId = useMemo(() => {
    const out = {};
    if (!detail?.options || !detail?.images) return out;
    const imgIds = new Set(detail.images.map(i => i.id));
    const metaMap = detail.variantImageIds || {};
    const imgByUrl = new Map();
    for (const img of detail.images) {
      if (img.url_standard) imgByUrl.set(img.url_standard, img.id);
      if (img.url_zoom) imgByUrl.set(img.url_zoom, img.id);
      if (img.url_thumbnail) imgByUrl.set(img.url_thumbnail, img.id);
    }
    for (const opt of detail.options) {
      for (const ov of (opt.option_values || [])) {
        // 1. Metafield wins.
        if (ov.id in metaMap) {
          const imgId = metaMap[ov.id];
          out[ov.id] = imgIds.has(imgId) ? imgId : '__external__';
          continue;
        }
        // 2. Fallback: URL match against any variant bound through this value.
        const matching = (detail.variants || []).filter(v => (v.option_values || []).some(vv => vv.id === ov.id));
        const urls = [...new Set(matching.map(v => v.image_url || '').filter(Boolean))];
        if (urls.length === 0) out[ov.id] = null;
        else if (urls.length === 1) out[ov.id] = imgByUrl.get(urls[0]) ?? '__external__';
        else out[ov.id] = '__mixed__';
      }
    }
    return out;
  }, [detail?.variants, detail?.options, detail?.images, detail?.variantImageIds]);

  const patchVariantImageEdit = (optionValueId, imageId) => {
    setVariantImageEdits(prev => {
      const next = { ...prev };
      const current = currentVariantImageByValueId[optionValueId];
      // If the user's choice equals the current derived value, drop the edit (clean row).
      if (imageId === current) delete next[optionValueId];
      else next[optionValueId] = imageId; // null means "clear assignment"
      return next;
    });
  };

  // ── Dirty / save ───────────────────────────────────────────────────────────
  const imagesDirty = Object.keys(imageEdits).length > 0;
  const tagsDirty = !!tagEdits;
  const variantImagesDirty = Object.keys(variantImageEdits).length > 0;
  const detailDirty = imagesDirty || optionBadgesDirty || pillCountsDirty || gridRowTagsDirty || tagsDirty || bannerDirty || tooltipDirty || marqueeDirty || ugcDirty || recipeDirty || overlayDirty || guaranteeDirty || liveViewersDirty || trueblueDirty || thumbsPositionDirty || variantImagesDirty;

  const openFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const uploadGalleryFile = async (file) => {
    if (!file || !selectedId) return;
    if (!file.type.startsWith('image/') && !/\.(heic|heif)$/i.test(file.name || '')) {
      setStatus({ type: 'error', msg: 'Please pick an image file.' });
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setStatus({ type: 'error', msg: `Image too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 5 MB.` });
      return;
    }
    setUploading(true);
    setStatus(null);
    try {
      const nextSort = (detail?.images || []).reduce((max, img) => Math.max(max, img.sort_order ?? 0), -1) + 1;
      await uploadProductImage(selectedId, file, { sortOrder: nextSort });
      setStatus({ type: 'ok', msg: 'Image uploaded.' });
      await reloadDetail();
    } catch (err) {
      setStatus({ type: 'error', msg: `Upload failed: ${err.message}` });
    } finally {
      setUploading(false);
    }
  };

  const handleFileChosen = async (e) => {
    const file = e.target.files && e.target.files[0];
    await uploadGalleryFile(file);
  };

  // Replace: upload a new file, inheriting the old row's sort_order /
  // description / is_thumbnail, then delete the old image. Clears any
  // pending edits on the replaced row since its id no longer exists.
  const openReplacePicker = (imageId) => {
    pendingReplaceIdRef.current = imageId;
    if (replaceFileInputRef.current) {
      replaceFileInputRef.current.value = '';
      replaceFileInputRef.current.click();
    }
  };

  const replaceImageFile = async (imageId, file) => {
    if (!file || !selectedId) return;
    if (!file.type.startsWith('image/') && !/\.(heic|heif)$/i.test(file.name || '')) {
      setStatus({ type: 'error', msg: 'Please pick an image file.' });
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setStatus({ type: 'error', msg: `Image too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 5 MB.` });
      return;
    }
    const oldImg = detail?.images?.find(i => i.id === imageId);
    if (!oldImg) return;
    const effectiveDesc = getImageField(oldImg, 'description') ?? oldImg.description ?? '';
    const effectiveSort = getImageField(oldImg, 'sort_order') ?? oldImg.sort_order ?? 0;
    const effectiveThumb = getImageField(oldImg, 'is_thumbnail') ?? oldImg.is_thumbnail ?? false;
    setReplacingId(imageId);
    setStatus(null);
    try {
      await uploadProductImage(selectedId, file, {
        description: effectiveDesc,
        sortOrder: effectiveSort,
        isThumbnail: effectiveThumb,
      });
      await bcApi('DELETE', `/v3/catalog/products/${selectedId}/images/${imageId}`);
      setImageEdits(prev => {
        const next = { ...prev };
        delete next[imageId];
        return next;
      });
      setStatus({ type: 'ok', msg: 'Image replaced.' });
      await reloadDetail();
    } catch (err) {
      setStatus({ type: 'error', msg: `Replace failed: ${err.message}` });
    } finally {
      setReplacingId(null);
    }
  };

  const handleReplaceFileChosen = async (e) => {
    const file = e.target.files && e.target.files[0];
    const targetId = pendingReplaceIdRef.current;
    pendingReplaceIdRef.current = null;
    if (!file || !targetId) return;
    await replaceImageFile(targetId, file);
  };

  // Variant library upload: same as gallery upload but tags the image description
  // with VARIANT_LIBRARY_MARKER so the storefront carousel filters it out.
  const openLibraryFilePicker = () => {
    if (libraryFileInputRef.current) {
      libraryFileInputRef.current.value = '';
      libraryFileInputRef.current.click();
    }
  };

  const uploadLibraryFile = async (file) => {
    if (!file || !selectedId) return;
    if (!file.type.startsWith('image/') && !/\.(heic|heif)$/i.test(file.name || '')) {
      setStatus({ type: 'error', msg: 'Please pick an image file.' });
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setStatus({ type: 'error', msg: `Image too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 5 MB.` });
      return;
    }
    setUploadingLibrary(true);
    setStatus(null);
    try {
      const nextSort = (detail?.images || []).reduce((max, img) => Math.max(max, img.sort_order ?? 0), -1) + 1;
      await uploadProductImage(selectedId, file, {
        description: VARIANT_LIBRARY_MARKER,
        sortOrder: nextSort,
        isThumbnail: false,
      });
      setStatus({ type: 'ok', msg: 'Uploaded to variant library.' });
      await reloadDetail();
    } catch (err) {
      setStatus({ type: 'error', msg: `Upload failed: ${err.message}` });
    } finally {
      setUploadingLibrary(false);
    }
  };

  const handleLibraryFileChosen = async (e) => {
    const file = e.target.files && e.target.files[0];
    await uploadLibraryFile(file);
  };

  // Add images picked from the Media Library to the variant library. Each
  // asset is added to BC by its R2 URL (BC fetches it), tagged with
  // VARIANT_LIBRARY_MARKER so it stays out of the storefront carousel.
  const addLibraryImagesFromMedia = async (assets) => {
    if (!selectedId || !assets || assets.length === 0) return;
    const r2Url = (key) => `https://cpx-prototype.netlify.app/.netlify/functions/r2-image?key=${encodeURIComponent(key)}`;
    setUploadingLibrary(true);
    setStatus(null);
    let added = 0;
    try {
      let nextSort = (detail?.images || []).reduce((max, img) => Math.max(max, img.sort_order ?? 0), -1) + 1;
      for (const a of assets) {
        const key = a.r2_original_key || a.r2_thumb_key;
        if (!key) continue;
        await uploadProductImageFromUrl(selectedId, r2Url(key), {
          description: VARIANT_LIBRARY_MARKER,
          sortOrder: nextSort++,
          isThumbnail: false,
        });
        added++;
      }
      setStatus({ type: 'ok', msg: `Added ${added} image${added === 1 ? '' : 's'} from the Media Library.` });
      await reloadDetail();
    } catch (err) {
      setStatus({ type: 'error', msg: `Add from Media Library failed: ${err.message}` });
      if (added > 0) await reloadDetail();
    } finally {
      setUploadingLibrary(false);
    }
  };

  // Banner-only upload. Tags the image with PDP_BANNER_MARKER as its alt/
  // description, pushes it to the end of the sort order, and immediately
  // points banner.image_url at the returned CDN URL. Storefront + admin
  // Images section both filter by this marker so the image stays out of
  // the product carousel and the editor gallery.
  const openBannerFilePicker = () => {
    if (bannerFileInputRef.current) {
      bannerFileInputRef.current.value = '';
      bannerFileInputRef.current.click();
    }
  };

  const uploadBannerFile = async (file) => {
    if (!file || !selectedId) return;
    if (!file.type.startsWith('image/') && !/\.(heic|heif)$/i.test(file.name || '')) {
      setStatus({ type: 'error', msg: 'Please pick an image file.' });
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setStatus({ type: 'error', msg: `Image too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 5 MB.` });
      return;
    }
    setUploadingBanner(true);
    setStatus(null);
    try {
      const nextSort = (detail?.images || []).reduce((max, img) => Math.max(max, img.sort_order ?? 0), -1) + 1;
      const res = await uploadProductImage(selectedId, file, {
        sortOrder: nextSort,
        description: PDP_BANNER_MARKER,
        isThumbnail: false,
      });
      const url = res?.url_zoom || res?.url_standard || res?.url_thumbnail;
      if (!url) throw new Error('Upload succeeded but BC returned no image URL');
      patchBanner('image_url', url);
      setStatus({ type: 'ok', msg: 'Banner image uploaded. Click Save to persist the banner override.' });
      await reloadDetail();
    } catch (err) {
      setStatus({ type: 'error', msg: `Upload failed: ${err.message}` });
    } finally {
      setUploadingBanner(false);
    }
  };

  const handleBannerFileChosen = async (e) => {
    const file = e.target.files && e.target.files[0];
    await uploadBannerFile(file);
  };

  const saveDetail = async () => {
    if (!detail || !selectedId || !detailDirty) return;
    // Grid Row Tags now live in a METAFIELD (~65,535-char cap) instead of the old
    // 250-char custom field, so the hard block is gone. Keep only a sanity guard
    // against the metafield ceiling (GRID_ROW_TAGS_MAX_VALUE) so a pathological
    // paste can't silently blow past BC's limit.
    if (gridRowTagsDirty) {
      const cleanedTags = cleanGridRowTags(gridRowTagsEdits);
      if (cleanedTags.length > 0) {
        const len = JSON.stringify(cleanedTags).length;
        if (len > GRID_ROW_TAGS_MAX_VALUE) {
          setStatus({ type: 'error', msg: `Grid Row Tags are too long (${len} of ${GRID_ROW_TAGS_MAX_VALUE} chars). Remove a tag or shorten the labels, then save again.` });
          return;
        }
      }
    }
    setSaving(true);
    setStatus(null);
    // Track the cleaned grid-row-tags so we can mirror them to PROD after the
    // primary SB-2 save succeeds (non-fatal — never blocks the save).
    let gridTagsMirrorPayload = null;
    try {
      // 1. Image edits — update, then delete. Do updates first so deleted rows can't race with a sort swap.
      for (const img of detail.images) {
        const e = imageEdits[img.id];
        if (!e || e.deleted) continue;
        const body = {};
        if ('description' in e) body.description = e.description ?? '';
        if ('sort_order' in e) body.sort_order = e.sort_order;
        if ('is_thumbnail' in e) body.is_thumbnail = !!e.is_thumbnail;
        if (Object.keys(body).length > 0) {
          await bcApi('PUT', `/v3/catalog/products/${selectedId}/images/${img.id}`, body);
        }
      }
      for (const img of detail.images) {
        const e = imageEdits[img.id];
        if (e?.deleted) {
          await bcApi('DELETE', `/v3/catalog/products/${selectedId}/images/${img.id}`);
        }
      }

      // 2. Option badges — single metafield upsert/delete
      if (optionBadgesDirty) {
        const cleaned = cleanOptionBadges(optionBadgeEdits);
        const nextValue = JSON.stringify(cleaned);
        const hasContent = Object.keys(cleaned).length > 0;
        const existingId = detail.optionBadgesMfId;
        if (hasContent && existingId) {
          await bcApi('PUT', `/v3/catalog/products/${selectedId}/metafields/${existingId}`, {
            key: OPTION_BADGES_KEY, value: nextValue, namespace: PDP_NS, permission_set: 'read_and_sf_access',
          });
        } else if (hasContent && !existingId) {
          await bcApi('POST', `/v3/catalog/products/${selectedId}/metafields`, {
            key: OPTION_BADGES_KEY, value: nextValue, namespace: PDP_NS, permission_set: 'read_and_sf_access',
          });
        } else if (!hasContent && existingId) {
          await bcApi('DELETE', `/v3/catalog/products/${selectedId}/metafields/${existingId}`);
        }
      }

      // 2b. Pill counts — single metafield upsert/delete (clean returns null
      //     when no usable cells remain, which deletes the metafield).
      if (pillCountsDirty) {
        const cleaned = cleanPillCounts(pillCountsEdits);
        const existingId = detail.pillCountsMfId;
        if (cleaned && existingId) {
          await bcApi('PUT', `/v3/catalog/products/${selectedId}/metafields/${existingId}`, {
            key: PILL_COUNTS_KEY, value: JSON.stringify(cleaned), namespace: PDP_NS, permission_set: 'read_and_sf_access',
          });
        } else if (cleaned && !existingId) {
          await bcApi('POST', `/v3/catalog/products/${selectedId}/metafields`, {
            key: PILL_COUNTS_KEY, value: JSON.stringify(cleaned), namespace: PDP_NS, permission_set: 'read_and_sf_access',
          });
        } else if (!cleaned && existingId) {
          await bcApi('DELETE', `/v3/catalog/products/${selectedId}/metafields/${existingId}`);
        }
      }

      // 3. Tags
      if (tagEdits) {
        for (const key of tagEdits.add) {
          await bcApi('POST', `/v3/catalog/products/${selectedId}/metafields`, {
            namespace: TAG_NAMESPACE, key, value: TAG_VALUE, permission_set: TAG_PERMISSION_SET,
          });
        }
        for (const key of tagEdits.remove) {
          const mfId = detail.tagMetafieldIds[key];
          if (mfId) await bcApi('DELETE', `/v3/catalog/products/${selectedId}/metafields/${mfId}`);
        }
      }

      // 4b. Tooltip metafields — one row per key (quantity / seasoning). Blank
      //     deletes the row so the theme's hardcoded default shows through.
      if (tooltipDirty && tooltipEdits) {
        for (const key of TOOLTIP_KEYS) {
          if (!(key in tooltipEdits)) continue;
          const stringValue = String(tooltipEdits[key] || '').trim();
          const existingId = detail.tooltipMfIds[key];
          if (stringValue === '') {
            if (existingId) {
              await bcApi('DELETE', `/v3/catalog/products/${selectedId}/metafields/${existingId}`);
            }
            continue;
          }
          const body = {
            key,
            value: stringValue,
            namespace: TOOLTIP_NS,
            permission_set: 'read_and_sf_access',
          };
          if (existingId) {
            await bcApi('PUT', `/v3/catalog/products/${selectedId}/metafields/${existingId}`, body);
          } else {
            await bcApi('POST', `/v3/catalog/products/${selectedId}/metafields`, body);
          }
        }
      }

      // 4a. Marquee metafields — enabled flag + items JSON (two separate rows)
      if (marqueeDirty && marqueeEdits) {
        if ('enabled' in marqueeEdits) {
          const stringValue = marqueeEdits.enabled ? 'true' : 'false';
          const existingId = detail.marqueeMfIds?.enabled;
          const body = { key: 'enabled', value: stringValue, namespace: MARQUEE_NS, permission_set: 'read_and_sf_access' };
          if (existingId) {
            await bcApi('PUT', `/v3/catalog/products/${selectedId}/metafields/${existingId}`, body);
          } else {
            await bcApi('POST', `/v3/catalog/products/${selectedId}/metafields`, body);
          }
        }
        if ('items' in marqueeEdits) {
          const cleaned = (marqueeEdits.items || [])
            .filter(it => it && String(it.text || '').trim() !== '')
            .slice(0, MARQUEE_ITEM_LIMIT)
            .map(it => {
              const out = { icon: String(it.icon || ''), text: String(it.text).trim() };
              const hl = String(it.headline || '').trim();
              const desc = String(it.description || '').trim();
              if (hl) out.headline = hl;
              if (desc) out.description = desc;
              return out;
            });
          const existingId = detail.marqueeMfIds?.items;
          if (cleaned.length === 0) {
            if (existingId) {
              await bcApi('DELETE', `/v3/catalog/products/${selectedId}/metafields/${existingId}`);
            }
          } else {
            const body = { key: 'items', value: JSON.stringify(cleaned), namespace: MARQUEE_NS, permission_set: 'read_and_sf_access' };
            if (existingId) {
              await bcApi('PUT', `/v3/catalog/products/${selectedId}/metafields/${existingId}`, body);
            } else {
              await bcApi('POST', `/v3/catalog/products/${selectedId}/metafields`, body);
            }
          }
        }
        if ('duration_sec' in marqueeEdits) {
          const raw = marqueeEdits.duration_sec;
          const existingId = detail.marqueeMfIds?.duration_sec;
          const numeric = (raw === '' || raw == null) ? null : Number(raw);
          // Blank / null / out-of-range → remove the metafield so the theme uses its CSS default
          if (numeric == null || !Number.isFinite(numeric) || numeric < MARQUEE_MIN_DURATION || numeric > MARQUEE_MAX_DURATION) {
            if (existingId) {
              await bcApi('DELETE', `/v3/catalog/products/${selectedId}/metafields/${existingId}`);
            }
          } else {
            const body = { key: 'duration_sec', value: String(Math.round(numeric)), namespace: MARQUEE_NS, permission_set: 'read_and_sf_access' };
            if (existingId) {
              await bcApi('PUT', `/v3/catalog/products/${selectedId}/metafields/${existingId}`, body);
            } else {
              await bcApi('POST', `/v3/catalog/products/${selectedId}/metafields`, body);
            }
          }
        }
        if ('sticky' in marqueeEdits) {
          const stringValue = marqueeEdits.sticky ? 'true' : 'false';
          const existingId = detail.marqueeMfIds?.sticky;
          const body = { key: 'sticky', value: stringValue, namespace: MARQUEE_NS, permission_set: 'read_and_sf_access' };
          if (existingId) {
            await bcApi('PUT', `/v3/catalog/products/${selectedId}/metafields/${existingId}`, body);
          } else {
            await bcApi('POST', `/v3/catalog/products/${selectedId}/metafields`, body);
          }
        }
        if ('padding_y_px' in marqueeEdits) {
          const raw = marqueeEdits.padding_y_px;
          const existingId = detail.marqueeMfIds?.padding_y_px;
          const numeric = (raw === '' || raw == null) ? null : Number(raw);
          // Blank / null / out-of-range → remove the metafield so the theme uses its CSS default
          if (numeric == null || !Number.isFinite(numeric) || numeric < MARQUEE_MIN_PADDING_Y || numeric > MARQUEE_MAX_PADDING_Y) {
            if (existingId) {
              await bcApi('DELETE', `/v3/catalog/products/${selectedId}/metafields/${existingId}`);
            }
          } else {
            const body = { key: 'padding_y_px', value: String(Math.round(numeric)), namespace: MARQUEE_NS, permission_set: 'read_and_sf_access' };
            if (existingId) {
              await bcApi('PUT', `/v3/catalog/products/${selectedId}/metafields/${existingId}`, body);
            } else {
              await bcApi('POST', `/v3/catalog/products/${selectedId}/metafields`, body);
            }
          }
        }
        if ('colors' in marqueeEdits && marqueeEdits.colors) {
          // One metafield per color slot. Empty / unknown preset key → DELETE
          // so the storefront falls back to its SCSS default for that slot.
          const slotToKey = { bg: 'bg_color', text: 'text_color', icon: 'icon_color', flash: 'flash_color' };
          for (const slot of ['bg', 'text', 'icon', 'flash']) {
            const presetKey = String(marqueeEdits.colors[slot] || '').trim();
            const mfKey = slotToKey[slot];
            const existingId = detail.marqueeMfIds?.[mfKey];
            // 'none' is a marker only valid for the flash slot — it tells
            // the storefront to skip the color flip + glow while keeping
            // whatever motion effect is active.
            const isValid = presetKey && (MARQUEE_PALETTE[presetKey] || (slot === 'flash' && presetKey === 'none'));
            if (!isValid) {
              if (existingId) {
                await bcApi('DELETE', `/v3/catalog/products/${selectedId}/metafields/${existingId}`);
              }
            } else {
              const body = { key: mfKey, value: presetKey, namespace: MARQUEE_NS, permission_set: 'read_and_sf_access' };
              if (existingId) {
                await bcApi('PUT', `/v3/catalog/products/${selectedId}/metafields/${existingId}`, body);
              } else {
                await bcApi('POST', `/v3/catalog/products/${selectedId}/metafields`, body);
              }
            }
          }
        }
        if ('flashEffect' in marqueeEdits) {
          // Empty / 'flash' (the storefront default) / unknown → DELETE so we
          // don't pollute the metafield set with the implicit default.
          const ev = String(marqueeEdits.flashEffect || '').trim().toLowerCase();
          const isValidNonDefault = ev && ev !== MARQUEE_EFFECT_DEFAULT && MARQUEE_EFFECTS.some(e => e.key === ev);
          const existingId = detail.marqueeMfIds?.flash_effect;
          if (!isValidNonDefault) {
            if (existingId) {
              await bcApi('DELETE', `/v3/catalog/products/${selectedId}/metafields/${existingId}`);
            }
          } else {
            const body = { key: 'flash_effect', value: ev, namespace: MARQUEE_NS, permission_set: 'read_and_sf_access' };
            if (existingId) {
              await bcApi('PUT', `/v3/catalog/products/${selectedId}/metafields/${existingId}`, body);
            } else {
              await bcApi('POST', `/v3/catalog/products/${selectedId}/metafields`, body);
            }
          }
        }
        if ('linkBanner' in marqueeEdits) {
          // Default is OFF — only persist when ON. Removing the metafield on
          // disable keeps the metafield set tidy and matches storefront default.
          const existingId = detail.marqueeMfIds?.link_banner;
          if (!marqueeEdits.linkBanner) {
            if (existingId) {
              await bcApi('DELETE', `/v3/catalog/products/${selectedId}/metafields/${existingId}`);
            }
          } else {
            const body = { key: 'link_banner', value: 'true', namespace: MARQUEE_NS, permission_set: 'read_and_sf_access' };
            if (existingId) {
              await bcApi('PUT', `/v3/catalog/products/${selectedId}/metafields/${existingId}`, body);
            } else {
              await bcApi('POST', `/v3/catalog/products/${selectedId}/metafields`, body);
            }
          }
        }
        if ('scrollMode' in marqueeEdits) {
          // Default is 'scroll' — only persist the metafield when non-default.
          const sm = String(marqueeEdits.scrollMode || '').trim().toLowerCase();
          const existingId = detail.marqueeMfIds?.scroll_mode;
          if (sm !== 'rotate' && sm !== 'static') {
            if (existingId) {
              await bcApi('DELETE', `/v3/catalog/products/${selectedId}/metafields/${existingId}`);
            }
          } else {
            const body = { key: 'scroll_mode', value: sm, namespace: MARQUEE_NS, permission_set: 'read_and_sf_access' };
            if (existingId) {
              await bcApi('PUT', `/v3/catalog/products/${selectedId}/metafields/${existingId}`, body);
            } else {
              await bcApi('POST', `/v3/catalog/products/${selectedId}/metafields`, body);
            }
          }
        }
        if ('rotateIntervalSec' in marqueeEdits) {
          // Default is MARQUEE_ROTATE_INTERVAL_DEFAULT (3s); only persist when
          // explicitly set to a different valid value. Same dirty-check pattern
          // as duration_sec / padding_y_px.
          const raw = marqueeEdits.rotateIntervalSec;
          const existingId = detail.marqueeMfIds?.rotate_interval_sec;
          const n = (raw == null || raw === '') ? null : Number(raw);
          const isValid = Number.isFinite(n) && n >= MARQUEE_ROTATE_INTERVAL_MIN && n <= MARQUEE_ROTATE_INTERVAL_MAX && n !== MARQUEE_ROTATE_INTERVAL_DEFAULT;
          if (!isValid) {
            if (existingId) {
              await bcApi('DELETE', `/v3/catalog/products/${selectedId}/metafields/${existingId}`);
            }
          } else {
            const body = { key: 'rotate_interval_sec', value: String(n), namespace: MARQUEE_NS, permission_set: 'read_and_sf_access' };
            if (existingId) {
              await bcApi('PUT', `/v3/catalog/products/${selectedId}/metafields/${existingId}`, body);
            } else {
              await bcApi('POST', `/v3/catalog/products/${selectedId}/metafields`, body);
            }
          }
        }
        if ('mobileScrollMode' in marqueeEdits) {
          const msm = String(marqueeEdits.mobileScrollMode || '').trim().toLowerCase();
          const existingId = detail.marqueeMfIds?.mobile_scroll_mode;
          if (!msm || !['rotate', 'scroll', 'static'].includes(msm)) {
            if (existingId) await bcApi('DELETE', `/v3/catalog/products/${selectedId}/metafields/${existingId}`);
          } else {
            const body = { key: 'mobile_scroll_mode', value: msm, namespace: MARQUEE_NS, permission_set: 'read_and_sf_access' };
            if (existingId) await bcApi('PUT', `/v3/catalog/products/${selectedId}/metafields/${existingId}`, body);
            else await bcApi('POST', `/v3/catalog/products/${selectedId}/metafields`, body);
          }
        }
        if ('mobileStaticItemIndex' in marqueeEdits) {
          const raw = marqueeEdits.mobileStaticItemIndex;
          const existingId = detail.marqueeMfIds?.mobile_static_item_index;
          const n = (raw == null || raw === '') ? null : Number(raw);
          if (n == null || n === 0) {
            if (existingId) await bcApi('DELETE', `/v3/catalog/products/${selectedId}/metafields/${existingId}`);
          } else {
            const body = { key: 'mobile_static_item_index', value: String(Math.floor(n)), namespace: MARQUEE_NS, permission_set: 'read_and_sf_access' };
            if (existingId) await bcApi('PUT', `/v3/catalog/products/${selectedId}/metafields/${existingId}`, body);
            else await bcApi('POST', `/v3/catalog/products/${selectedId}/metafields`, body);
          }
        }
      }

      // 4b. UGC metafields — enabled / heading / subhead / items (JSON)
      // Mirrors the marquee pattern: one metafield per key, blank-text deletes,
      // empty items array deletes the items metafield so the panel disappears.
      if (ugcDirty && ugcEdits) {
        if ('enabled' in ugcEdits) {
          const stringValue = ugcEdits.enabled ? 'true' : 'false';
          const existingId = detail.ugcMfIds?.enabled;
          const body = { key: 'enabled', value: stringValue, namespace: UGC_NS, permission_set: 'read_and_sf_access' };
          if (existingId) {
            await bcApi('PUT', `/v3/catalog/products/${selectedId}/metafields/${existingId}`, body);
          } else {
            await bcApi('POST', `/v3/catalog/products/${selectedId}/metafields`, body);
          }
        }
        for (const textKey of ['heading', 'subhead']) {
          if (!(textKey in ugcEdits)) continue;
          const stringValue = String(ugcEdits[textKey] || '').trim();
          const existingId = detail.ugcMfIds?.[textKey];
          if (stringValue === '') {
            if (existingId) {
              await bcApi('DELETE', `/v3/catalog/products/${selectedId}/metafields/${existingId}`);
            }
          } else {
            const body = { key: textKey, value: stringValue, namespace: UGC_NS, permission_set: 'read_and_sf_access' };
            if (existingId) {
              await bcApi('PUT', `/v3/catalog/products/${selectedId}/metafields/${existingId}`, body);
            } else {
              await bcApi('POST', `/v3/catalog/products/${selectedId}/metafields`, body);
            }
          }
        }
        // Layout: enum string. Default ('grid') deletes the metafield so the
        // storefront falls back to its own default — keeps BC clean of no-op rows.
        if ('layout' in ugcEdits) {
          const layoutValue = UGC_LAYOUTS.includes(ugcEdits.layout) ? ugcEdits.layout : UGC_LAYOUT_DEFAULT;
          const existingId = detail.ugcMfIds?.layout;
          if (layoutValue === UGC_LAYOUT_DEFAULT) {
            if (existingId) {
              await bcApi('DELETE', `/v3/catalog/products/${selectedId}/metafields/${existingId}`);
            }
          } else {
            const body = { key: 'layout', value: layoutValue, namespace: UGC_NS, permission_set: 'read_and_sf_access' };
            if (existingId) {
              await bcApi('PUT', `/v3/catalog/products/${selectedId}/metafields/${existingId}`, body);
            } else {
              await bcApi('POST', `/v3/catalog/products/${selectedId}/metafields`, body);
            }
          }
        }
        if ('items' in ugcEdits) {
          const cleaned = (ugcEdits.items || [])
            .filter(it => it && (it.url || it.thumbUrl))
            .slice(0, UGC_ITEM_LIMIT)
            .map(it => ({
              url: String(it.url || it.thumbUrl || ''),
              thumbUrl: String(it.thumbUrl || it.url || ''),
              caption: String(it.caption || '').trim(),
              handle: String(it.handle || '').trim(),
              platform: String(it.platform || '').trim(),
              sourceUrl: String(it.sourceUrl || '').trim(),
              mediaAssetId: it.mediaAssetId != null ? Number(it.mediaAssetId) : null,
            }));
          const existingId = detail.ugcMfIds?.items;
          if (cleaned.length === 0) {
            if (existingId) {
              await bcApi('DELETE', `/v3/catalog/products/${selectedId}/metafields/${existingId}`);
            }
          } else {
            const body = { key: 'items', value: JSON.stringify(cleaned), namespace: UGC_NS, permission_set: 'read_and_sf_access' };
            if (existingId) {
              await bcApi('PUT', `/v3/catalog/products/${selectedId}/metafields/${existingId}`, body);
            } else {
              await bcApi('POST', `/v3/catalog/products/${selectedId}/metafields`, body);
            }
          }
        }
      }

      // 4b-recipe. Recipe CTA metafields — enabled / photos (JSON) /
      // button_text / bg_color / text_color / show_on_thumbnails. enabled is
      // always written; the rest delete when blank/empty so BC stays free of
      // no-op rows. Legacy image_ids + recipe_slug rows are deleted on save.
      if (recipeDirty && recipeEdits) {
        const writeRecipeMf = async (mfKey, value) => {
          const existingId = detail.recipeMfIds?.[mfKey];
          if (value === '' || value == null) {
            if (existingId) await bcApi('DELETE', `/v3/catalog/products/${selectedId}/metafields/${existingId}`);
            return;
          }
          const body = { key: mfKey, value: String(value), namespace: RECIPE_NS, permission_set: 'read_and_sf_access' };
          if (existingId) await bcApi('PUT', `/v3/catalog/products/${selectedId}/metafields/${existingId}`, body);
          else await bcApi('POST', `/v3/catalog/products/${selectedId}/metafields`, body);
        };

        if ('enabled' in recipeEdits) {
          await writeRecipeMf('enabled', recipeEdits.enabled ? 'true' : 'false');
        }
        if ('photos' in recipeEdits) {
          const cleaned = (recipeEdits.photos || [])
            .map(p => ({ id: Number(p.id), recipe: String(p.recipe || '').trim() }))
            .filter(p => Number.isFinite(p.id) && p.id > 0);
          await writeRecipeMf('photos', cleaned.length ? JSON.stringify(cleaned) : '');
          // Legacy one-recipe-for-all keys are superseded by `photos` — clear them.
          await writeRecipeMf('image_ids', '');
          await writeRecipeMf('recipe_slug', '');
        }
        if ('buttonText' in recipeEdits) {
          await writeRecipeMf('button_text', String(recipeEdits.buttonText || '').trim());
        }
        if ('bgColor' in recipeEdits) {
          await writeRecipeMf('bg_color', String(recipeEdits.bgColor || '').trim());
        }
        if ('textColor' in recipeEdits) {
          await writeRecipeMf('text_color', String(recipeEdits.textColor || '').trim());
        }
        if ('showOnThumbnails' in recipeEdits) {
          // Default is true — only persist the metafield when it's been turned off.
          await writeRecipeMf('show_on_thumbnails', recipeEdits.showOnThumbnails === false ? 'false' : '');
        }
        if ('icon' in recipeEdits) {
          // Default icon (chef-hat) deletes the row — pdp-banner.js defaults to it.
          const iconVal = String(recipeEdits.icon || '').trim();
          await writeRecipeMf('icon', (!iconVal || iconVal === RECIPE_ICON_DEFAULT) ? '' : iconVal);
        }
        if ('iconColor' in recipeEdits) {
          await writeRecipeMf('icon_color', String(recipeEdits.iconColor || '').trim());
        }
        if ('thumbIconOnly' in recipeEdits) {
          // Default false — only persist when turned on.
          await writeRecipeMf('thumb_icon_only', recipeEdits.thumbIconOnly === true ? 'true' : '');
        }
      }

      // 4b-overlay. PDP Overlay metafields — enabled / text / teaser / font_size /
      // text_color / bg_color / bg_opacity / start_expanded. Same compact helper as
      // recipe — blank values delete so BC stays free of no-op rows.
      if (overlayDirty && overlayEdits) {
        const writeOverlayMf = async (mfKey, value) => {
          const existingId = detail.overlayMfIds?.[mfKey];
          if (value === '' || value == null) {
            if (existingId) await bcApi('DELETE', `/v3/catalog/products/${selectedId}/metafields/${existingId}`);
            return;
          }
          const body = { key: mfKey, value: String(value), namespace: OVERLAY_NS, permission_set: 'read_and_sf_access' };
          if (existingId) await bcApi('PUT', `/v3/catalog/products/${selectedId}/metafields/${existingId}`, body);
          else await bcApi('POST', `/v3/catalog/products/${selectedId}/metafields`, body);
        };
        if ('enabled' in overlayEdits) {
          await writeOverlayMf('enabled', overlayEdits.enabled ? 'true' : 'false');
        }
        if ('text' in overlayEdits) {
          await writeOverlayMf('text', String(overlayEdits.text || '').trim());
        }
        if ('teaser' in overlayEdits) {
          await writeOverlayMf('teaser', String(overlayEdits.teaser || '').trim());
        }
        if ('fontSize' in overlayEdits) {
          const n = Number(overlayEdits.fontSize);
          await writeOverlayMf('font_size', (Number.isFinite(n) && n !== OVERLAY_FONT_SIZE_DEFAULT) ? String(n) : '');
        }
        if ('textColor' in overlayEdits) {
          await writeOverlayMf('text_color', String(overlayEdits.textColor || '').trim());
        }
        if ('bgColor' in overlayEdits) {
          await writeOverlayMf('bg_color', String(overlayEdits.bgColor || '').trim());
        }
        if ('bgOpacity' in overlayEdits) {
          const n = Number(overlayEdits.bgOpacity);
          await writeOverlayMf('bg_opacity', (Number.isFinite(n) && n !== OVERLAY_OPACITY_DEFAULT) ? String(n) : '');
        }
        if ('startExpanded' in overlayEdits) {
          // Default false — only persist when turned on.
          await writeOverlayMf('start_expanded', overlayEdits.startExpanded === true ? 'true' : '');
        }
      }

      // 4b-guarantee. Price Guarantee modal copy — title / lede / body /
      // guarantee_title / guarantee_body. Blank deletes the row so pdp-banner
      // falls back to its product-agnostic default for that field.
      if (guaranteeDirty && guaranteeEdits) {
        const writeGuaranteeMf = async (mfKey, value) => {
          const existingId = detail.guaranteeMfIds?.[mfKey];
          const v = String(value || '').trim();
          if (!v) {
            if (existingId) await bcApi('DELETE', `/v3/catalog/products/${selectedId}/metafields/${existingId}`);
            return;
          }
          const body = { key: mfKey, value: v, namespace: GUARANTEE_NS, permission_set: 'read_and_sf_access' };
          if (existingId) await bcApi('PUT', `/v3/catalog/products/${selectedId}/metafields/${existingId}`, body);
          else await bcApi('POST', `/v3/catalog/products/${selectedId}/metafields`, body);
        };
        if ('title' in guaranteeEdits) await writeGuaranteeMf('title', guaranteeEdits.title);
        if ('lede' in guaranteeEdits) await writeGuaranteeMf('lede', guaranteeEdits.lede);
        if ('body' in guaranteeEdits) await writeGuaranteeMf('body', guaranteeEdits.body);
        if ('guaranteeTitle' in guaranteeEdits) await writeGuaranteeMf('guarantee_title', guaranteeEdits.guaranteeTitle);
        if ('guaranteeBody' in guaranteeEdits) await writeGuaranteeMf('guarantee_body', guaranteeEdits.guaranteeBody);
      }

      // 4b-liveviewers. PDP Live Viewers badge -- enabled / min_count. Both are
      // OVERRIDES of the site-wide pdp_defaults; "inherit" deletes the key so the
      // storefront falls back to the env default. Same compact write/delete helper.
      if (liveViewersDirty && liveViewersEdits) {
        const writeLiveViewersMf = async (mfKey, value) => {
          const existingId = detail.liveViewersMfIds?.[mfKey];
          if (value === '' || value == null) {
            if (existingId) await bcApi('DELETE', `/v3/catalog/products/${selectedId}/metafields/${existingId}`);
            return;
          }
          const body = { key: mfKey, value: String(value), namespace: LIVE_VIEWERS_NS, permission_set: 'read_and_sf_access' };
          if (existingId) await bcApi('PUT', `/v3/catalog/products/${selectedId}/metafields/${existingId}`, body);
          else await bcApi('POST', `/v3/catalog/products/${selectedId}/metafields`, body);
        };
        if ('enabled' in liveViewersEdits) {
          const e = liveViewersEdits.enabled;
          await writeLiveViewersMf('enabled', e === true ? 'true' : (e === false ? 'false' : ''));
        }
        if ('minCount' in liveViewersEdits) {
          const n = Number(liveViewersEdits.minCount);
          await writeLiveViewersMf('min_count', (liveViewersEdits.minCount != null && Number.isFinite(n)) ? String(n) : '');
        }
      }

      // 4b-trueblue. True Blue overlay custom fields (product 132 only).
      // Default state (enabled + bottom-left) deletes the rows so BC stays
      // clean — the storefront treats absence as the default.
      if (trueblueDirty && trueblueEdits) {
        const writeCf = async (name, value, existingId) => {
          if (value === '' || value == null) {
            if (existingId) await bcApi('DELETE', `/v3/catalog/products/${selectedId}/custom-fields/${existingId}`);
            return;
          }
          if (existingId) await bcApi('PUT', `/v3/catalog/products/${selectedId}/custom-fields/${existingId}`, { name, value: String(value) });
          else await bcApi('POST', `/v3/catalog/products/${selectedId}/custom-fields`, { name, value: String(value) });
        };
        if ('trueblueEnabled' in trueblueEdits) {
          // Persist only when explicitly OFF — default is ON, so absence = on.
          await writeCf(TRUEBLUE_ENABLED_FIELD, trueblueEdits.trueblueEnabled === false ? 'false' : '', detail.trueblueEnabledFieldId);
        }
        if ('truebluePosition' in trueblueEdits) {
          const v = trueblueEdits.truebluePosition;
          await writeCf(TRUEBLUE_POSITION_FIELD, (v && v !== TRUEBLUE_POSITION_DEFAULT) ? v : '', detail.truebluePositionFieldId);
        }
      }

      // 4b-gridrowtags. Grid Row Tags — colored pills on the variant-grid overlay.
      //   Stored in the pdp/grid_row_tags METAFIELD (was a 250-char custom field).
      //   Skip blank/incomplete rows; an emptied list deletes the metafield. The
      //   legacy custom field is left in place as a read fallback (not written here).
      if (gridRowTagsDirty) {
        const cleanedTags = cleanGridRowTags(gridRowTagsEdits);
        const existingId = detail.gridRowTagsMfId;
        if (cleanedTags.length > 0) {
          const value = JSON.stringify(cleanedTags);
          const body = { key: GRID_ROW_TAGS_KEY, value, namespace: PDP_NS, permission_set: 'read_and_sf_access' };
          if (existingId) {
            await bcApi('PUT', `/v3/catalog/products/${selectedId}/metafields/${existingId}`, body);
          } else {
            await bcApi('POST', `/v3/catalog/products/${selectedId}/metafields`, body);
          }
        } else if (existingId) {
          await bcApi('DELETE', `/v3/catalog/products/${selectedId}/metafields/${existingId}`);
        }
        // Mirror to PROD after the SB-2 save completes (only when editing SB-2 —
        // don't mirror when the admin is already targeting prod/sb1).
        if (getActiveEnv() === 'sb2') gridTagsMirrorPayload = cleanedTags;
      }

      // 4c. Thumbnail position — single string metafield in the pdp namespace.
      // Blank value → delete metafield (theme falls back to default bottom layout).
      if (thumbsPositionDirty) {
        const existingId = detail.thumbsPositionMfId;
        const value = (thumbsPositionEdit || '').trim();
        if (!value) {
          if (existingId) {
            await bcApi('DELETE', `/v3/catalog/products/${selectedId}/metafields/${existingId}`);
          }
        } else {
          const body = { key: THUMBS_POSITION_KEY, value, namespace: PDP_NS, permission_set: 'read_and_sf_access' };
          if (existingId) {
            await bcApi('PUT', `/v3/catalog/products/${selectedId}/metafields/${existingId}`, body);
          } else {
            await bcApi('POST', `/v3/catalog/products/${selectedId}/metafields`, body);
          }
        }
      }

      // 4. Banner metafields — one row per key (enabled / sticky / title /
      //    subhead / image_url / wave_enabled / wave_color).
      //    wave_enabled defaults to ON, so we only persist 'false' and delete
      //    the metafield when it flips back to default-true. wave_color is
      //    a preset key string — blank deletes so storefront uses SCSS default.
      if (bannerDirty && bannerEdits) {
        for (const key of BANNER_KEYS) {
          if (!(key in bannerEdits)) continue;
          const rawValue = bannerEdits[key];
          const isBool = key === 'enabled' || key === 'sticky';
          const existingId = detail.bannerMfIds[key];
          // Wave-enabled is a tri-state in storage: present='false' (off),
          // absent (default-on). When user toggles back to ON, delete the row.
          if (key === 'wave_enabled') {
            if (rawValue) {
              if (existingId) {
                await bcApi('DELETE', `/v3/catalog/products/${selectedId}/metafields/${existingId}`);
              }
            } else {
              const body = { key, value: 'false', namespace: BANNER_NS, permission_set: 'read_and_sf_access' };
              if (existingId) {
                await bcApi('PUT', `/v3/catalog/products/${selectedId}/metafields/${existingId}`, body);
              } else {
                await bcApi('POST', `/v3/catalog/products/${selectedId}/metafields`, body);
              }
            }
            continue;
          }
          const stringValue = isBool
            ? (rawValue ? 'true' : 'false')
            : String(rawValue || '').trim();
          // Treat blank strings on text keys as "remove" so the fallback shines through
          const isBlank = !isBool && stringValue === '';
          if (isBlank) {
            if (existingId) {
              await bcApi('DELETE', `/v3/catalog/products/${selectedId}/metafields/${existingId}`);
            }
            continue;
          }
          const body = {
            key,
            value: stringValue,
            namespace: BANNER_NS,
            permission_set: 'read_and_sf_access',
          };
          if (existingId) {
            await bcApi('PUT', `/v3/catalog/products/${selectedId}/metafields/${existingId}`, body);
          } else {
            await bcApi('POST', `/v3/catalog/products/${selectedId}/metafields`, body);
          }
        }
      }

      // 5. Variant image assignments — writes two things, no variant.image_url.
      //    a) pdp.variant_image_ids metafield (source of truth; theme JS reads
      //       this and intercepts swatch clicks to swap the main image via
      //       thumbnail click).
      //    b) option_value.value_data.image_url for each changed row (the swatch
      //       tile image on the PDP card).
      //    Does NOT write variant.image_url — BC transforms it into
      //    attribute_rule_images/* and causes the default-selected variant to
      //    override the product's primary image on PDP load.
      if (variantImagesDirty) {
        const mergedMap = { ...(detail.variantImageIds || {}) };
        for (const [ovIdStr, imageId] of Object.entries(variantImageEdits)) {
          const ovId = Number(ovIdStr);
          if (imageId == null) delete mergedMap[ovId];
          else mergedMap[ovId] = imageId;
        }
        const mfValue = JSON.stringify(mergedMap);
        const hasContent = Object.keys(mergedMap).length > 0;
        const existingId = detail.variantImageIdsMfId;
        const mfPayload = {
          key: VARIANT_IMAGE_IDS_KEY, value: mfValue, namespace: PDP_NS, permission_set: 'read_and_sf_access',
        };
        // Robust upsert: if the PUT 404s (stale id — e.g. the metafield was
        // deleted out-of-band since the UI loaded detail), fall back to POST.
        // Same for DELETE: ignore 404.
        if (hasContent && existingId) {
          try {
            await bcApi('PUT', `/v3/catalog/products/${selectedId}/metafields/${existingId}`, mfPayload);
          } catch (err) {
            if (err?.status === 404 || /404/.test(err?.message || '')) {
              await bcApi('POST', `/v3/catalog/products/${selectedId}/metafields`, mfPayload);
            } else { throw err; }
          }
        } else if (hasContent && !existingId) {
          await bcApi('POST', `/v3/catalog/products/${selectedId}/metafields`, mfPayload);
        } else if (!hasContent && existingId) {
          try {
            await bcApi('DELETE', `/v3/catalog/products/${selectedId}/metafields/${existingId}`);
          } catch (err) {
            if (!(err?.status === 404 || /404/.test(err?.message || ''))) throw err;
          }
        }

        // b) Option value swatch tile writes.
        const imgUrlById = new Map();
        for (const img of detail.images) {
          if (img.url_standard) imgUrlById.set(img.id, img.url_standard);
        }
        const ovToOptionId = new Map();
        for (const opt of (detail.options || [])) {
          for (const ov of (opt.option_values || [])) ovToOptionId.set(ov.id, opt.id);
        }
        for (const [ovIdStr, imageId] of Object.entries(variantImageEdits)) {
          const ovId = Number(ovIdStr);
          if (imageId == null) continue; // skip "clear" — leave swatch image as-is
          const url = imgUrlById.get(imageId);
          const optId = ovToOptionId.get(ovId);
          if (!url || !optId) continue;
          await bcApi('PUT', `/v3/catalog/products/${selectedId}/options/${optId}/values/${ovId}`, {
            value_data: { image_url: url },
          });
        }
      }

      // Grid Row Tags PROD mirror — runs AFTER the SB-2 save succeeded. Failures
      // are surfaced as a soft note, never as a save failure.
      let mirrorNote = '';
      if (gridTagsMirrorPayload != null) {
        const mirror = await mirrorGridRowTagsToProd(detail, gridTagsMirrorPayload);
        if (mirror.ok) {
          mirrorNote = ' Also updated on the LIVE store.';
        } else if (mirror.reason === 'no_prod_match') {
          mirrorNote = ' NOTE: could not find a matching product on the LIVE store — hot-deal pills were NOT mirrored to prod.';
        } else {
          mirrorNote = ` NOTE: LIVE-store mirror failed (${mirror.message || 'error'}) — saved to sandbox only.`;
        }
      }
      setStatus({ type: 'ok', msg: `Saved. (Storefront cache may take up to 60s.)${mirrorNote}` });
      await reloadDetail();
    } catch (err) {
      setStatus({ type: 'error', msg: `Save failed: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  const discardDetail = () => {
    setImageEdits({});
    setOptionBadgeEdits(null);
    setPillCountsEdits(null);
    setTagEdits(null);
    setBannerEdits(null);
    setTooltipEdits(null);
    setMarqueeEdits(null);
    setUgcEdits(null);
    setRecipeEdits(null);
    setOverlayEdits(null);
    setGuaranteeEdits(null);
    setLiveViewersEdits(null);
    setTrueblueEdits(null);
    setGridRowTagsEdits(null);
    setThumbsPositionEdit(null);
    setVariantImageEdits({});
  };

  // ── Filtering ──────────────────────────────────────────────────────────────
  const filteredProducts = useMemo(() => {
    const s = search.trim().toLowerCase();
    return products.filter(p => {
      if (enabledOnly && !p.isVisible) return false;
      if (!s) return true;
      return p.name.toLowerCase().includes(s) || p.sku.toLowerCase().includes(s);
    });
  }, [products, enabledOnly, search]);

  // ── Tag audit ──────────────────────────────────────────────────────────────
  const loadTagAudit = useCallback(async (tagKey) => {
    if (!tagKey || products.length === 0) return;
    setAuditLoading(true);
    try {
      // Fetch only the metafield with this key across all products. One per product in parallel, batched.
      const applied = new Set();
      const idsByProduct = {};
      await inBatches(products, 12, async (p) => {
        try {
          const res = await bcApi('GET', `/v3/catalog/products/${p.id}/metafields?namespace=${TAG_NAMESPACE}&key=${tagKey}&limit=5`);
          const mf = (res?.data || []).find(m => m.key === tagKey);
          if (mf) {
            applied.add(p.id);
            idsByProduct[p.id] = mf.id;
          }
        } catch { /* skip */ }
      });
      setAuditRows(products.map(p => ({
        ...p,
        applied: applied.has(p.id),
        metafieldId: idsByProduct[p.id] || null,
      })));
      setAuditEdits({});
    } finally {
      setAuditLoading(false);
    }
  }, [products]);

  useEffect(() => {
    if (subtab === 'tag-audit' && auditTagKey && products.length > 0 && auditRows.length === 0) {
      loadTagAudit(auditTagKey);
    }
  }, [subtab, auditTagKey, products, auditRows.length, loadTagAudit]);

  const auditEffective = useMemo(() => {
    return auditRows.map(r => {
      const edit = auditEdits[r.id];
      let applied = r.applied;
      if (edit === 'add') applied = true;
      if (edit === 'remove') applied = false;
      return { ...r, effectiveApplied: applied, pending: !!edit };
    });
  }, [auditRows, auditEdits]);

  const filteredAuditRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return auditEffective.filter(r => {
      if (enabledOnly && !r.isVisible) return false;
      if (auditFilter === 'applied' && !r.effectiveApplied) return false;
      if (auditFilter === 'missing' && r.effectiveApplied) return false;
      if (term && !r.name.toLowerCase().includes(term) && !r.sku.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [auditEffective, auditFilter, enabledOnly, search]);

  const auditAppliedCount = auditEffective.filter(r => r.effectiveApplied).length;
  const auditMissingCount = auditEffective.length - auditAppliedCount;
  const auditDirty = Object.keys(auditEdits).length > 0;

  const toggleAuditRow = (row) => {
    setAuditEdits(prev => {
      const next = { ...prev };
      const currentEdit = prev[row.id];
      const wasApplied = row.applied;
      const nowApplied = currentEdit === 'add' ? true : currentEdit === 'remove' ? false : wasApplied;
      const shouldBe = !nowApplied;
      if (shouldBe === wasApplied) {
        delete next[row.id];
      } else {
        next[row.id] = shouldBe ? 'add' : 'remove';
      }
      return next;
    });
  };

  const saveAudit = async () => {
    if (!auditDirty || !auditTagKey) return;
    setAuditSaving(true);
    setStatus(null);
    try {
      const adds = [];
      const removes = [];
      for (const [pidStr, op] of Object.entries(auditEdits)) {
        const pid = Number(pidStr);
        if (op === 'add') adds.push(pid);
        if (op === 'remove') {
          const row = auditRows.find(r => r.id === pid);
          if (row?.metafieldId) removes.push({ pid, mfId: row.metafieldId });
        }
      }
      await inBatches(adds, 8, async (pid) => {
        await bcApi('POST', `/v3/catalog/products/${pid}/metafields`, {
          namespace: TAG_NAMESPACE, key: auditTagKey, value: TAG_VALUE, permission_set: TAG_PERMISSION_SET,
        });
      });
      await inBatches(removes, 8, async ({ pid, mfId }) => {
        await bcApi('DELETE', `/v3/catalog/products/${pid}/metafields/${mfId}`);
      });
      setStatus({ type: 'ok', msg: `Saved — ${adds.length} added, ${removes.length} removed.` });
      await loadTagAudit(auditTagKey);
    } catch (err) {
      setStatus({ type: 'error', msg: `Save failed: ${err.message}` });
    } finally {
      setAuditSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      <PdpDefaultsPanel
        defaults={pdpDefaults}
        raw={pdpDefaultsRaw}
        loading={pdpDefaultsLoading}
        setDefault={setPdpDefault}
        setDefaultRaw={setPdpDefaultRaw}
      />

      {/* Sub-tabs */}
      <div style={{ borderBottom: '1px solid var(--gray-200)', marginBottom: 16, display: 'flex', gap: 4 }}>
        <SubTab label="Products" active={subtab === 'products'} onClick={() => setSubtab('products')} />
        <SubTab label="Tag Audit" active={subtab === 'tag-audit'} onClick={() => setSubtab('tag-audit')} />
        <div style={{ marginLeft: 'auto', padding: '4px 0' }}>
          <button
            onClick={loadAll}
            disabled={loading}
            style={{ padding: '6px 12px', background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 6, cursor: loading ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}
          >
            <RefreshCw size={12} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
            {loading ? 'Loading...' : 'Reload'}
          </button>
        </div>
      </div>

      {status && (
        <div style={{ margin: '0 0 14px', padding: '10px 12px', borderRadius: 6, background: status.type === 'error' ? '#fee' : '#e8f5e9', color: status.type === 'error' ? '#b00' : '#2a7', display: 'flex', alignItems: 'center', gap: 8 }}>
          {status.type === 'error' ? <AlertTriangle size={16} /> : <Check size={16} />}
          <span style={{ fontSize: 13 }}>{status.msg}</span>
          <button onClick={() => setStatus(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>
            <X size={14} />
          </button>
        </div>
      )}

      {loading && loadingMsg && <div style={{ color: 'var(--gray-500)', fontSize: 13, marginBottom: 12 }}>{loadingMsg}</div>}

      {/* Shared filter strip */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', minWidth: 280, flex: '0 1 360px' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-500)' }} />
          <input
            type="text"
            placeholder="Search products by name or SKU..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ padding: '8px 10px 8px 32px', width: '100%', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 13 }}
          />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={enabledOnly}
            onChange={(e) => setEnabledOnly(e.target.checked)}
            style={{ accentColor: 'var(--orange)' }}
          />
          Visible only
        </label>
        <span style={{ color: 'var(--gray-500)', fontSize: 12 }}>
          {filteredProducts.length} / {products.length} products
        </span>
      </div>

      {subtab === 'products' && (
        <ProductsMasterDetail
          products={filteredProducts}
          allLoading={loading}
          selectedId={selectedId}
          onSelect={selectProduct}
          pdpDefaults={pdpDefaults}
          detail={detail}
          detailLoading={detailLoading}
          imageEdits={imageEdits}
          patchImageEdit={patchImageEdit}
          getImageField={getImageField}
          moveImage={moveImage}
          toggleDeleteImage={toggleDeleteImage}
          setThumbnail={setThumbnail}
          optionBadges={currentOptionBadges}
          patchOptionBadge={patchOptionBadge}
          optionBadgesDirty={optionBadgesDirty}
          pillCounts={currentPillCounts}
          patchPillCounts={patchPillCounts}
          patchPillCount={patchPillCount}
          pillCountsDirty={pillCountsDirty}
          gridRowTags={currentGridRowTags}
          addGridRowTag={addGridRowTag}
          patchGridRowTag={patchGridRowTag}
          removeGridRowTag={removeGridRowTag}
          gridRowTagsDirty={gridRowTagsDirty}
          currentTagsSet={currentTagsSet}
          toggleTag={toggleTag}
          tagEdits={tagEdits}
          bannerValue={bannerValue}
          patchBanner={patchBanner}
          bannerDirty={bannerDirty}
          tooltipValue={tooltipValue}
          patchTooltip={patchTooltip}
          tooltipDirty={tooltipDirty}
          marqueeEnabled={marqueeEnabled}
          marqueeItems={marqueeItems}
          marqueeDuration={marqueeDuration}
          marqueePaddingY={marqueePaddingY}
          marqueeSticky={marqueeSticky}
          marqueeColors={marqueeColors}
          marqueeFlashEffect={marqueeFlashEffect}
          marqueeLinkBanner={marqueeLinkBanner}
          marqueeScrollMode={marqueeScrollMode}
          marqueeRotateIntervalSec={marqueeRotateIntervalSec}
          marqueeMobileScrollMode={marqueeMobileScrollMode}
          marqueeMobileStaticItemIndex={marqueeMobileStaticItemIndex}
          patchMarqueeEnabled={patchMarqueeEnabled}
          patchMarqueeItems={patchMarqueeItems}
          patchMarqueeDuration={patchMarqueeDuration}
          patchMarqueePaddingY={patchMarqueePaddingY}
          patchMarqueeSticky={patchMarqueeSticky}
          patchMarqueeColor={patchMarqueeColor}
          patchMarqueeFlashEffect={patchMarqueeFlashEffect}
          patchMarqueeLinkBanner={patchMarqueeLinkBanner}
          patchMarqueeScrollMode={patchMarqueeScrollMode}
          patchMarqueeRotateInterval={patchMarqueeRotateInterval}
          patchMarqueeMobileScrollMode={patchMarqueeMobileScrollMode}
          patchMarqueeMobileStaticItemIndex={patchMarqueeMobileStaticItemIndex}
          marqueeDirty={marqueeDirty}
          customIcons={customIcons}
          customIconsLoading={customIconsLoading}
          iconUploading={iconUploading}
          uploadMarqueeIcon={uploadMarqueeIcon}
          deleteMarqueeIcon={deleteMarqueeIcon}
          marqueePresets={marqueePresets}
          marqueePresetsLoading={marqueePresetsLoading}
          saveMarqueePreset={saveMarqueePreset}
          deleteMarqueePreset={deleteMarqueePreset}
          ugcEnabled={ugcEnabled}
          ugcHeading={ugcHeading}
          ugcSubhead={ugcSubhead}
          ugcLayout={ugcLayout}
          ugcItems={ugcItems}
          patchUgcEnabled={patchUgcEnabled}
          patchUgcHeading={patchUgcHeading}
          patchUgcSubhead={patchUgcSubhead}
          patchUgcLayout={patchUgcLayout}
          patchUgcItems={patchUgcItems}
          ugcDirty={ugcDirty}
          recipe={{ enabled: recipeEnabled, photos: recipePhotos, buttonText: recipeButtonText, bgColor: recipeBgColor, textColor: recipeTextColor, showOnThumbnails: recipeShowOnThumbnails, icon: recipeIcon, iconColor: recipeIconColor, thumbIconOnly: recipeThumbIconOnly, dirty: recipeDirty }}
          patchRecipe={patchRecipe}
          overlay={{ enabled: overlayEnabled, text: overlayText, teaser: overlayTeaser, fontSize: overlayFontSize, textColor: overlayTextColor, bgColor: overlayBgColor, bgOpacity: overlayBgOpacity, startExpanded: overlayStartExpanded, enabledExplicit: overlayEnabledExplicit, dirty: overlayDirty }}
          patchOverlay={patchOverlay}
          resetOverlayEnabled={() => setOverlayEdits(prev => { const next = { ...(prev || {}) }; delete next.enabled; return Object.keys(next).length ? next : null; })}
          guarantee={{ title: guaranteeTitle, lede: guaranteeLede, body: guaranteeBody, cardTitle: guaranteeCardTitle, cardBody: guaranteeCardBody, dirty: guaranteeDirty }}
          patchGuarantee={patchGuarantee}
          liveViewers={{ enabled: liveViewersEnabled, minCount: liveViewersMinCount, dirty: liveViewersDirty }}
          patchLiveViewers={patchLiveViewers}
          trueblue={{ enabled: trueblueEnabled, position: truebluePosition, dirty: trueblueDirty }}
          patchTrueblue={patchTrueblue}
          thumbsPositionValue={thumbsPositionValue}
          setThumbsPositionEdit={setThumbsPositionEdit}
          thumbsPositionDirty={thumbsPositionDirty}
          categoryFallback={detail?.categoryFallback}
          uploadingBanner={uploadingBanner}
          openBannerFilePicker={openBannerFilePicker}
          bannerFileInputRef={bannerFileInputRef}
          handleBannerFileChosen={handleBannerFileChosen}
          uploadBannerFile={uploadBannerFile}
          detailDirty={detailDirty}
          saveDetail={saveDetail}
          discardDetail={discardDetail}
          saving={saving}
          uploading={uploading}
          openFilePicker={openFilePicker}
          fileInputRef={fileInputRef}
          handleFileChosen={handleFileChosen}
          uploadGalleryFile={uploadGalleryFile}
          setPreview={setPreview}
          replacingId={replacingId}
          openReplacePicker={openReplacePicker}
          replaceFileInputRef={replaceFileInputRef}
          handleReplaceFileChosen={handleReplaceFileChosen}
          variantImageEdits={variantImageEdits}
          patchVariantImageEdit={patchVariantImageEdit}
          currentVariantImageByValueId={currentVariantImageByValueId}
          variantImagesDirty={variantImagesDirty}
          uploadingLibrary={uploadingLibrary}
          openLibraryFilePicker={openLibraryFilePicker}
          libraryFileInputRef={libraryFileInputRef}
          handleLibraryFileChosen={handleLibraryFileChosen}
          uploadLibraryFile={uploadLibraryFile}
          addLibraryImagesFromMedia={addLibraryImagesFromMedia}
          reloadDetail={reloadDetail}
          setStatus={setStatus}
        />
      )}

      {subtab === 'tag-audit' && (
        <TagAuditView
          tagKey={auditTagKey}
          onTagKeyChange={(k) => { setAuditTagKey(k); setAuditRows([]); setAuditEdits({}); }}
          filter={auditFilter}
          setFilter={setAuditFilter}
          loading={auditLoading}
          rows={filteredAuditRows}
          totalApplied={auditAppliedCount}
          totalMissing={auditMissingCount}
          onToggleRow={toggleAuditRow}
          onReload={() => loadTagAudit(auditTagKey)}
          dirty={auditDirty}
          dirtyCount={Object.keys(auditEdits).length}
          onSave={saveAudit}
          onDiscard={() => setAuditEdits({})}
          saving={auditSaving}
        />
      )}

      {preview && (
        <div
          onClick={() => setPreview(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(10, 15, 25, 0.82)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32, cursor: 'zoom-out' }}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setPreview(null); }}
            style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none', borderRadius: 999, width: 40, height: 40, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <X size={20} />
          </button>
          <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
            <img
              src={preview.url}
              alt={preview.name || ''}
              style={{ maxWidth: '100%', maxHeight: '82vh', objectFit: 'contain', borderRadius: 6, boxShadow: '0 20px 60px rgba(0,0,0,0.5)', background: '#fff' }}
            />
            <div style={{ color: '#fff', fontSize: 13, textAlign: 'center' }}>
              <div style={{ fontWeight: 600 }}>{preview.name}</div>
              <div style={{ color: '#cbd5e1', fontSize: 11, marginTop: 2, wordBreak: 'break-all', maxWidth: 800 }}>{preview.url}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────────
function SubTab({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '8px 18px', fontSize: 13, fontWeight: active ? 700 : 500,
        color: active ? 'var(--orange)' : 'var(--gray-500)', background: 'none', border: 'none',
        borderBottom: active ? '2px solid var(--orange)' : '2px solid transparent',
        cursor: 'pointer', marginBottom: -1,
      }}
    >
      {label}
    </button>
  );
}

function ProductsMasterDetail(props) {
  const {
    products, allLoading, selectedId, onSelect,
    pdpDefaults,
    detail, detailLoading,
    imageEdits, patchImageEdit, getImageField, moveImage, toggleDeleteImage, setThumbnail,
    optionBadges, patchOptionBadge, optionBadgesDirty,
    pillCounts, patchPillCounts, patchPillCount, pillCountsDirty,
    gridRowTags, addGridRowTag, patchGridRowTag, removeGridRowTag, gridRowTagsDirty,
    currentTagsSet, toggleTag, tagEdits,
    bannerValue, patchBanner, bannerDirty, categoryFallback,
    tooltipValue, patchTooltip, tooltipDirty,
    marqueeEnabled, marqueeItems, marqueeDuration, marqueePaddingY, marqueeSticky, marqueeColors, marqueeFlashEffect, marqueeLinkBanner, marqueeScrollMode, marqueeRotateIntervalSec, marqueeMobileScrollMode, marqueeMobileStaticItemIndex, patchMarqueeEnabled, patchMarqueeItems, patchMarqueeDuration, patchMarqueePaddingY, patchMarqueeSticky, patchMarqueeColor, patchMarqueeFlashEffect, patchMarqueeLinkBanner, patchMarqueeScrollMode, patchMarqueeRotateInterval, patchMarqueeMobileScrollMode, patchMarqueeMobileStaticItemIndex, marqueeDirty,
    customIcons, customIconsLoading, iconUploading, uploadMarqueeIcon, deleteMarqueeIcon,
    marqueePresets, marqueePresetsLoading, saveMarqueePreset, deleteMarqueePreset,
    ugcEnabled, ugcHeading, ugcSubhead, ugcLayout, ugcItems, patchUgcEnabled, patchUgcHeading, patchUgcSubhead, patchUgcLayout, patchUgcItems, ugcDirty,
    recipe, patchRecipe,
    overlay, patchOverlay, resetOverlayEnabled,
    guarantee, patchGuarantee,
    liveViewers, patchLiveViewers,
    trueblue, patchTrueblue,
    thumbsPositionValue, setThumbsPositionEdit, thumbsPositionDirty,
    uploadingBanner, openBannerFilePicker, bannerFileInputRef, handleBannerFileChosen, uploadBannerFile,
    detailDirty, saveDetail, discardDetail, saving,
    uploading, openFilePicker, fileInputRef, handleFileChosen, uploadGalleryFile, setPreview,
    replacingId, openReplacePicker, replaceFileInputRef, handleReplaceFileChosen,
    variantImageEdits, patchVariantImageEdit, currentVariantImageByValueId, variantImagesDirty,
    uploadingLibrary, openLibraryFilePicker, libraryFileInputRef, handleLibraryFileChosen, uploadLibraryFile,
    addLibraryImagesFromMedia,
    reloadDetail, setStatus,
  } = props;

  return (
    // minmax(0, 1fr): without the 0 floor, an intrinsic-sized child
    // (icon-library tile name, marquee text input) can blow the right pane
    // wider than the viewport.
    <div style={{ display: 'grid', gridTemplateColumns: '320px minmax(0, 1fr)', gap: 16, alignItems: 'flex-start' }}>
      {/* LEFT: Product list */}
      <div style={{ border: '1px solid var(--gray-200)', borderRadius: 8, background: '#fff', maxHeight: '75vh', overflow: 'auto' }}>
        {allLoading && products.length === 0 ? (
          <div style={{ padding: 24, color: 'var(--gray-500)', fontSize: 13, textAlign: 'center' }}>Loading...</div>
        ) : products.length === 0 ? (
          <div style={{ padding: 24, color: 'var(--gray-500)', fontSize: 13, textAlign: 'center' }}>No products.</div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {products.map(p => {
              const isSelected = p.id === selectedId;
              return (
                <li key={p.id}>
                  <button
                    onClick={() => onSelect(p.id)}
                    style={{
                      width: '100%', textAlign: 'left', padding: '10px 12px', border: 'none',
                      borderBottom: '1px solid var(--gray-100)',
                      background: isSelected ? 'rgba(220, 88, 42, 0.08)' : 'transparent',
                      cursor: 'pointer', display: 'flex', gap: 10, alignItems: 'center',
                    }}
                  >
                    {p.primaryImage ? (
                      <img src={p.primaryImage} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />
                    ) : (
                      <div style={{ width: 40, height: 40, borderRadius: 4, background: 'var(--gray-100)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--gray-400)', flexShrink: 0 }}>
                        <ImageIcon size={14} />
                      </div>
                    )}
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: isSelected ? 700 : 500, color: 'var(--navy)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--gray-500)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {p.sku || '—'}{!p.isVisible && <span style={{ marginLeft: 6, color: '#c53030' }}>hidden</span>}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* RIGHT: Editor */}
      <div style={{ border: '1px solid var(--gray-200)', borderRadius: 8, background: '#fff', padding: 16, minHeight: 400 }}>
        {!selectedId ? (
          <div style={{ color: 'var(--gray-500)', fontSize: 13, padding: '40px 0', textAlign: 'center' }}>
            Pick a product from the list to edit its images, option badges, and tags.
          </div>
        ) : detailLoading ? (
          <div style={{ color: 'var(--gray-500)', fontSize: 13, padding: '40px 0', textAlign: 'center' }}>
            <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite', marginBottom: 6 }} />
            <div>Loading product detail...</div>
          </div>
        ) : !detail ? null : (
          <>
            {/* Floating Save/Discard bar — fixed to the viewport's bottom-right
                so it's always reachable while editing any subsection of a
                long PDP detail panel. Appears only when there are unsaved
                changes. */}
            {detailDirty && (
              <div style={{
                position: 'fixed', right: 24, bottom: 24, zIndex: 50,
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 14px',
                background: '#fff',
                border: '1px solid var(--gray-300)',
                borderRadius: 8,
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.18)',
              }}>
                <span style={{ fontSize: 12, color: 'var(--orange)', fontWeight: 700 }}>Unsaved changes</span>
                <button
                  onClick={discardDetail} disabled={saving}
                  style={{ padding: '6px 12px', background: 'transparent', color: 'var(--gray-600)', border: '1px solid var(--gray-300)', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}
                >Discard</button>
                <button
                  onClick={saveDetail} disabled={saving}
                  style={{ padding: '7px 18px', background: 'var(--orange)', color: '#fff', border: 'none', borderRadius: 4, cursor: saving ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 }}
                >
                  <Save size={14} /> {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.heic,.heif"
              onChange={handleFileChosen}
              style={{ display: 'none' }}
            />
            <input
              ref={replaceFileInputRef}
              type="file"
              accept="image/*,.heic,.heif"
              onChange={handleReplaceFileChosen}
              style={{ display: 'none' }}
            />
            <input
              ref={libraryFileInputRef}
              type="file"
              accept="image/*,.heic,.heif"
              onChange={handleLibraryFileChosen}
              style={{ display: 'none' }}
            />

            {/* ── 1. TOP OF PAGE ─────────────────────────────────────────── */}
            <BannerSection
              bannerValue={bannerValue}
              patchBanner={patchBanner}
              dirty={bannerDirty}
              categoryFallback={categoryFallback}
              setPreview={setPreview}
              uploadingBanner={uploadingBanner}
              openBannerFilePicker={openBannerFilePicker}
              bannerFileInputRef={bannerFileInputRef}
              handleBannerFileChosen={handleBannerFileChosen}
              uploadBannerFile={uploadBannerFile}
            />

            {/* ── 2. BELOW BANNER ────────────────────────────────────────── */}
            <MarqueeSection
              enabled={marqueeEnabled}
              items={marqueeItems}
              duration={marqueeDuration}
              paddingY={marqueePaddingY}
              sticky={marqueeSticky}
              colors={marqueeColors}
              flashEffect={marqueeFlashEffect}
              linkBanner={marqueeLinkBanner}
              scrollMode={marqueeScrollMode}
              rotateIntervalSec={marqueeRotateIntervalSec}
              mobileScrollMode={marqueeMobileScrollMode}
              mobileStaticItemIndex={marqueeMobileStaticItemIndex}
              onToggleEnabled={patchMarqueeEnabled}
              onChangeItems={patchMarqueeItems}
              onChangeDuration={patchMarqueeDuration}
              onChangePaddingY={patchMarqueePaddingY}
              onToggleSticky={patchMarqueeSticky}
              onChangeColor={patchMarqueeColor}
              onChangeFlashEffect={patchMarqueeFlashEffect}
              onToggleLinkBanner={patchMarqueeLinkBanner}
              onChangeScrollMode={patchMarqueeScrollMode}
              onChangeRotateInterval={patchMarqueeRotateInterval}
              onChangeMobileScrollMode={patchMarqueeMobileScrollMode}
              onChangeMobileStaticItemIndex={patchMarqueeMobileStaticItemIndex}
              dirty={marqueeDirty}
              customIcons={customIcons}
              customIconsLoading={customIconsLoading}
              iconUploading={iconUploading}
              uploadMarqueeIcon={uploadMarqueeIcon}
              deleteMarqueeIcon={deleteMarqueeIcon}
              presets={marqueePresets}
              presetsLoading={marqueePresetsLoading}
              savePreset={saveMarqueePreset}
              deletePreset={deleteMarqueePreset}
            />

            {/* ── 3. GALLERY ─────────────────────────────────────────────── */}
            <ThumbsPositionSection
              value={thumbsPositionValue}
              onChange={setThumbsPositionEdit}
              dirty={thumbsPositionDirty}
            />

            <MediaSection
              productId={selectedId}
              detail={detail}
              imageEdits={imageEdits}
              patchImageEdit={patchImageEdit}
              getImageField={getImageField}
              toggleDeleteImage={toggleDeleteImage}
              setThumbnail={setThumbnail}
              uploading={uploading}
              openFilePicker={openFilePicker}
              uploadGalleryFile={uploadGalleryFile}
              setPreview={setPreview}
              replacingId={replacingId}
              openReplacePicker={openReplacePicker}
              reloadDetail={reloadDetail}
              setStatus={setStatus}
              recipe={recipe}
              patchRecipe={patchRecipe}
            />

            <VariantLibrarySection
              images={detail.images}
              imageEdits={imageEdits}
              patchImageEdit={patchImageEdit}
              getImageField={getImageField}
              toggleDeleteImage={toggleDeleteImage}
              setPreview={setPreview}
              uploadingLibrary={uploadingLibrary}
              openLibraryFilePicker={openLibraryFilePicker}
              uploadLibraryFile={uploadLibraryFile}
              onAddFromMediaLibrary={addLibraryImagesFromMedia}
              replacingId={replacingId}
              openReplacePicker={openReplacePicker}
            />

            <VariantImagesSection
              options={detail.options}
              variants={detail.variants}
              images={detail.images}
              variantImageEdits={variantImageEdits}
              patchVariantImageEdit={patchVariantImageEdit}
              currentVariantImageByValueId={currentVariantImageByValueId}
              dirty={variantImagesDirty}
              setPreview={setPreview}
            />

            {/* ── 4. OPTIONS / BUYING ────────────────────────────────────── */}
            <OptionBadgesSection
              options={detail.options}
              badges={optionBadges}
              patchOptionBadge={patchOptionBadge}
              dirty={optionBadgesDirty}
            />

            <PillCountsSection
              options={detail.options}
              pillCounts={pillCounts}
              patchPillCounts={patchPillCounts}
              patchPillCount={patchPillCount}
              dirty={pillCountsDirty}
            />

            <GridRowTagsSection
              options={detail.options}
              tags={gridRowTags}
              addTag={addGridRowTag}
              patchTag={patchGridRowTag}
              removeTag={removeGridRowTag}
              dirty={gridRowTagsDirty}
            />

            <TooltipSection
              tooltipValue={tooltipValue}
              patchTooltip={patchTooltip}
              dirty={tooltipDirty}
            />

            {/* ── 5. STICKY FOOTER ───────────────────────────────────────── */}
            <PdpBehaviorSection
              productId={selectedId}
              stickyAtcDropdowns={detail.stickyAtcDropdowns}
              stickyAtcDropdownsFieldId={detail.stickyAtcDropdownsFieldId}
              stickyAtcHideHeader={detail.stickyAtcHideHeader}
              stickyAtcHideHeaderFieldId={detail.stickyAtcHideHeaderFieldId}
              defaults={pdpDefaults}
              reloadDetail={reloadDetail}
              setStatus={setStatus}
            />

            {/* ── 6. BELOW-FOLD CONTENT ──────────────────────────────────── */}
            <UgcSection
              enabled={ugcEnabled}
              heading={ugcHeading}
              subhead={ugcSubhead}
              layout={ugcLayout}
              items={ugcItems}
              onToggleEnabled={patchUgcEnabled}
              onChangeHeading={patchUgcHeading}
              onChangeSubhead={patchUgcSubhead}
              onChangeLayout={patchUgcLayout}
              onChangeItems={patchUgcItems}
              dirty={ugcDirty}
            />

            <RecipeCtaSection
              recipe={recipe}
              patchRecipe={patchRecipe}
            />

            <OverlaySection
              overlay={overlay}
              patchOverlay={patchOverlay}
              resetOverlayEnabled={resetOverlayEnabled}
              siteDefaultEnabled={!!pdpDefaults?.overlayEnabled}
            />

            <GuaranteeSection
              guarantee={guarantee}
              patchGuarantee={patchGuarantee}
            />

            <LiveViewersSection
              liveViewers={liveViewers}
              patchLiveViewers={patchLiveViewers}
            />

            {selectedId === TRUEBLUE_PRODUCT_ID && (
              <TruebluePlacementSection
                enabled={trueblue.enabled}
                position={trueblue.position}
                dirty={trueblue.dirty}
                onToggleEnabled={(v) => patchTrueblue({ trueblueEnabled: v })}
                onChangePosition={(v) => patchTrueblue({ truebluePosition: v })}
              />
            )}

            {/* ── 7. TAGS ────────────────────────────────────────────────── */}
            <TagsSection
              currentTagsSet={currentTagsSet}
              tagEdits={tagEdits}
              toggleTag={toggleTag}
              originalApplied={detail.appliedTagKeys}
            />

            {/* Content Blocks — DB-driven (Neon page_content), rendered by the theme. */}
            <section style={{ background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8, padding: 16, marginBottom: 14 }}>
              <SectionHeader title="Content Blocks (DB)" subtitle="Structured PDP content stored in Neon and rendered by the storefront at load. No Page Builder." />
              <PageBlocksEditor
                pageType="pdp"
                pageKey={detail.customUrl}
                env={getActiveEnv()}
                viewUrl={detail.customUrl ? ('https://' + ({ sb1: 'the-crab-place-sandbox-1.mybigcommerce.com', sb2: 'the-crab-place-sandbox-2.mybigcommerce.com', prod: 'www.crabplace.com' }[getActiveEnv()] || '') + detail.customUrl) : null}
              />
            </section>
          </>
        )}
      </div>
    </div>
  );
}

function SectionHeader({ title, subtitle, rightSlot }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 10, borderBottom: '1px solid var(--gray-200)', paddingBottom: 6 }}>
      <div>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>{title}</h3>
        {subtitle && <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>{subtitle}</div>}
      </div>
      {rightSlot}
    </div>
  );
}

function BannerSection({ bannerValue, patchBanner, dirty, categoryFallback, setPreview, uploadingBanner, openBannerFilePicker, bannerFileInputRef, handleBannerFileChosen, uploadBannerFile }) {
  const enabled = !!bannerValue('enabled');
  const sticky = !!bannerValue('sticky');
  const titleVal = bannerValue('title');
  const subheadVal = bannerValue('subhead');
  const imageUrlVal = bannerValue('image_url');
  const waveEnabled = !!bannerValue('wave_enabled');
  const waveColor = bannerValue('wave_color') || '';

  const catName = categoryFallback?.name || '';
  const catBannerTitle = categoryFallback?.bannerTitle || '';
  const catBannerSubhead = categoryFallback?.bannerSubhead || '';
  const catImageUrl = categoryFallback?.image_url || '';

  const effectiveTitle = titleVal || catBannerTitle || catName || '(product name)';
  const effectiveSubhead = subheadVal || catBannerSubhead || '(category description)';
  const effectiveImage = imageUrlVal || catImageUrl || '';

  return (
    <div style={{ marginBottom: 24 }}>
      <SectionHeader
        title="PDP banner"
        subtitle="Optional hero banner at the top of the product page. Defaults to the primary category's banner content; override per-product here. Toggle off to hide the banner entirely."
        rightSlot={
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12 }}>
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => patchBanner('enabled', e.target.checked)}
              style={{ accentColor: 'var(--orange)' }}
            />
            <span style={{ color: enabled ? 'var(--orange)' : 'var(--gray-500)', fontWeight: 600 }}>
              {enabled ? 'Banner ON' : 'Banner off'}
            </span>
            {dirty && <span style={{ color: 'var(--orange)', fontWeight: 600, marginLeft: 8 }}>unsaved</span>}
          </label>
        }
      />

      {!enabled ? (
        <div style={{ color: 'var(--gray-500)', fontSize: 12, padding: '12px 0' }}>
          Banner is off — this PDP renders without a top banner. Flip the toggle above to enable. When enabled, fields left blank fall back to the primary category's banner.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ padding: '10px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, fontSize: 12, color: '#1e3a8a' }}>
            Fallback chain: <strong>product override</strong> → <strong>primary category banner</strong> ({catName || 'unknown'}) → category defaults. Leave a field blank to use the fallback.
          </div>

          <label style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '8px 12px', background: 'var(--gray-100)', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
            <input
              type="checkbox"
              checked={sticky}
              onChange={(e) => patchBanner('sticky', e.target.checked)}
              style={{ accentColor: 'var(--orange)' }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, color: 'var(--navy)' }}>Sticky banner</div>
              <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>
                When on, the banner stays pinned to the top of the viewport as shoppers scroll the PDP.
              </div>
            </div>
          </label>

          {/* Subhead wave animation — defaults ON. Toggle off to render the
              subhead as static text. Color picker reuses the marquee palette
              so admins can match brand colors across both surfaces; blank =
              SCSS default (#ffb020 gold). */}
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, padding: '8px 12px', background: 'var(--gray-100)', borderRadius: 6, fontSize: 13 }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={waveEnabled}
                onChange={(e) => patchBanner('wave_enabled', e.target.checked)}
                style={{ accentColor: 'var(--orange)' }}
              />
              <span style={{ fontWeight: 600, color: 'var(--navy)' }}>Subhead wave animation</span>
            </label>
            <div style={{ minWidth: 240, opacity: waveEnabled ? 1 : 0.5 }}>
              <ColorPresetPicker
                label="Highlight color"
                defaultKey={null}
                allowNone={false}
                value={waveColor}
                onChange={(v) => patchBanner('wave_color', v)}
              />
            </div>
            <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>
              Each subhead character bobs + flashes the highlight color in a left-to-right ripple every 7s. Blank picker = SCSS default gold.
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8, alignItems: 'start' }}>
            <label style={labelStyle}>Title</label>
            <input
              type="text"
              value={titleVal}
              placeholder={catBannerTitle || catName || '(product name)'}
              onChange={(e) => patchBanner('title', e.target.value)}
              style={input}
            />

            <label style={labelStyle}>Subhead</label>
            <input
              type="text"
              value={subheadVal}
              placeholder={catBannerSubhead || '(first block of category description)'}
              onChange={(e) => patchBanner('subhead', e.target.value)}
              style={input}
            />

            <label style={labelStyle}>Image</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="text"
                  value={imageUrlVal}
                  placeholder={catImageUrl || '(category image)'}
                  onChange={(e) => patchBanner('image_url', e.target.value)}
                  style={{ ...input, flex: 1 }}
                />
                <button
                  type="button"
                  onClick={openBannerFilePicker}
                  disabled={uploadingBanner}
                  title="Upload a banner image (does NOT appear in the product gallery/carousel)"
                  style={{ padding: '6px 12px', background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 4, cursor: uploadingBanner ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, whiteSpace: 'nowrap' }}
                >
                  <Upload size={12} /> {uploadingBanner ? 'Uploading...' : 'Upload banner image'}
                </button>
              </div>
              <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>
                Uploads are flagged as banner-only, so they stay out of the product image gallery and thumbnail carousel below. Or paste any existing CDN URL in the field.
              </div>
              <input
                ref={bannerFileInputRef}
                type="file"
                accept="image/*"
                onChange={handleBannerFileChosen}
                style={{ display: 'none' }}
              />
            </div>

            <div />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <DropZone
                disabled={uploadingBanner}
                onFile={(file) => uploadBannerFile && uploadBannerFile(file)}
                label={uploadingBanner ? 'Uploading...' : 'Drop banner image to upload'}
                style={{ display: 'inline-block' }}
              >
                {effectiveImage ? (
                  <button
                    type="button"
                    onClick={() => setPreview({ url: effectiveImage, name: `Banner preview: ${effectiveTitle}` })}
                    title="Click to preview the image that will render, or drag one here to replace"
                    style={{ width: 320, height: 60, padding: 0, border: '1px solid var(--gray-200)', borderRadius: 4, background: '#f7f8fa', cursor: 'zoom-in', overflow: 'hidden' }}
                  >
                    <img src={effectiveImage} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  </button>
                ) : (
                  <div style={{ width: 320, height: 60, border: '1px dashed var(--gray-300)', borderRadius: 4, color: 'var(--gray-400)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11 }}>
                    No image — drag one here or click Upload
                  </div>
                )}
              </DropZone>
              <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>
                Effective render: <strong style={{ color: 'var(--navy)' }}>{effectiveTitle}</strong>
                {subheadVal || catBannerSubhead ? <> — {effectiveSubhead.slice(0, 80)}{effectiveSubhead.length > 80 ? '…' : ''}</> : null}
              </div>
              <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>
                <BannerSizingInfo style={{ fontSize: 11 }} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TooltipSection({ tooltipValue, patchTooltip, dirty }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <SectionHeader
        title="Option tooltips"
        subtitle="Overrides the small info-popup text next to the Quantity and Seasoning labels on the PDP. Leave a field blank to use the theme's default copy."
        rightSlot={dirty ? <span style={{ color: 'var(--orange)', fontWeight: 600, fontSize: 12 }}>unsaved</span> : null}
      />
      <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8, alignItems: 'start' }}>
        {TOOLTIP_KEYS.map((key) => {
          const val = tooltipValue(key);
          return (
            <React.Fragment key={key}>
              <label style={labelStyle}>{TOOLTIP_LABELS[key]}</label>
              <textarea
                value={val}
                placeholder={TOOLTIP_DEFAULTS[key]}
                onChange={(e) => patchTooltip(key, e.target.value)}
                rows={3}
                style={{ ...input, minHeight: 60, resize: 'vertical', fontFamily: 'inherit' }}
              />
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// Standalone behavior toggles for the PDP — booleans stored as product custom
// fields, written immediately on toggle (no dirty-tracking integration). Add
// more per-PDP behavior toggles here as we build them.
function PdpBehaviorSection({
  productId,
  stickyAtcDropdowns,
  stickyAtcDropdownsFieldId,
  stickyAtcHideHeader,
  stickyAtcHideHeaderFieldId,
  defaults,
  reloadDetail,
  setStatus,
}) {
  const [savingField, setSavingField] = useState(null);
  const safeDefaults = defaults || {};

  const persistFlag = async (fieldName, fieldId, next, label) => {
    setSavingField(fieldName);
    try {
      const stringValue = next ? 'true' : 'false';
      if (fieldId) {
        await bcApi('PUT', `/v3/catalog/products/${productId}/custom-fields/${fieldId}`, {
          name: fieldName, value: stringValue,
        });
      } else {
        await bcApi('POST', `/v3/catalog/products/${productId}/custom-fields`, {
          name: fieldName, value: stringValue,
        });
      }
      setStatus({ type: 'success', msg: `${label} ${next ? 'enabled' : 'disabled'}` });
      await reloadDetail();
    } catch (e) {
      setStatus({ type: 'error', msg: `Toggle failed: ${e.message}` });
    } finally {
      setSavingField(null);
    }
  };

  const clearOverride = async (fieldName, fieldId, label) => {
    if (!fieldId) return;
    setSavingField(fieldName);
    try {
      await bcApi('DELETE', `/v3/catalog/products/${productId}/custom-fields/${fieldId}`);
      setStatus({ type: 'success', msg: `${label} reset to default` });
      await reloadDetail();
    } catch (e) {
      setStatus({ type: 'error', msg: `Reset failed: ${e.message}` });
    } finally {
      setSavingField(null);
    }
  };

  const saving = savingField !== null;

  const renderToggle = ({ fieldName, fieldId, value, defaultKey, title, hint, label }) => {
    const hasOverride = !!fieldId;
    const defaultOn = !!safeDefaults[defaultKey];
    const resolved = hasOverride ? !!value : defaultOn;
    return (
      <div style={{ background: '#fafbfc', border: '1px solid var(--gray-200)', borderRadius: 6 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, cursor: saving ? 'wait' : 'pointer', padding: '8px 12px' }}>
          <input
            type="checkbox"
            checked={resolved}
            disabled={saving}
            onChange={(e) => persistFlag(fieldName, fieldId, e.target.checked, label)}
          />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, color: 'var(--navy)' }}>{title}</div>
            <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>{hint}</div>
          </div>
        </label>
        <div style={{ padding: '0 12px 8px 36px', display: 'flex', alignItems: 'center', gap: 10, fontSize: 11, color: 'var(--gray-500)' }}>
          <span>
            Site default: <strong style={{ color: defaultOn ? 'var(--orange)' : 'var(--gray-700)' }}>{defaultOn ? 'ON' : 'OFF'}</strong>
            {hasOverride
              ? <span style={{ marginLeft: 6 }}>· this product overrides to <strong style={{ color: value ? 'var(--orange)' : 'var(--gray-700)' }}>{value ? 'ON' : 'OFF'}</strong></span>
              : <span style={{ marginLeft: 6 }}>· this product inherits</span>}
          </span>
          {hasOverride && (
            <button
              type="button"
              onClick={() => clearOverride(fieldName, fieldId, label)}
              disabled={saving}
              style={{ background: 'none', border: 'none', color: 'var(--orange)', cursor: saving ? 'wait' : 'pointer', padding: 0, fontSize: 11, textDecoration: 'underline' }}
            >
              Reset to default
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <SectionHeader
        title="Sticky add-to-cart footer"
        subtitle="The bar that stays at the bottom of the PDP as you scroll. Site-wide defaults live at the top of this tab."
        rightSlot={saving ? <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>Saving…</span> : null}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {renderToggle({
          fieldName: STICKY_ATC_DROPDOWNS_FIELD,
          fieldId: stickyAtcDropdownsFieldId,
          value: stickyAtcDropdowns,
          defaultKey: 'stickyAtcDropdowns',
          title: 'Show variant dropdowns instead of plain text',
          hint: 'Lets shoppers change Crab Size / Quantity / Seasoning right from the sticky footer. Off = read-only labels of the current selection.',
          label: 'Sticky-footer dropdowns',
        })}
        {renderToggle({
          fieldName: STICKY_ATC_HIDE_HEADER_FIELD,
          fieldId: stickyAtcHideHeaderFieldId,
          value: stickyAtcHideHeader,
          defaultKey: 'stickyAtcHideHeader',
          title: 'Hide page banner + marquee scroll while footer is visible',
          hint: 'While the sticky add-to-cart bar is showing, hide the PDP hero banner and the scrolling badge strip. Reclaims ~150px of viewport for the description and reviews.',
          label: 'Hide banner + marquee on pop-up',
        })}
      </div>
    </div>
  );
}

// Site-wide defaults panel — sits above the sub-tabs so it's always visible.
// Per-product overrides live in PdpBehaviorSection.
function PdpDefaultsPanel({ defaults, raw, loading, setDefault, setDefaultRaw }) {
  const [savingKey, setSavingKey] = useState(null);
  const safe = defaults || {};
  const rawSafe = raw || {};
  // Live-viewers minimum count -- integer-as-string default. Local draft so
  // typing is smooth; commit to Neon on blur.
  const storedMinCount = (rawSafe.live_viewers_min_count != null && rawSafe.live_viewers_min_count !== '')
    ? String(rawSafe.live_viewers_min_count) : '1';
  const [minCountDraft, setMinCountDraft] = useState(null); // null = show stored; string = editing
  const minCountValue = minCountDraft != null ? minCountDraft : storedMinCount;
  const commitMinCount = async () => {
    const n = parseInt(minCountValue, 10);
    const v = (Number.isFinite(n) && n >= 0) ? String(n) : '1';
    setSavingKey('live_viewers_min_count');
    try { await setDefaultRaw('live_viewers_min_count', v); setMinCountDraft(null); }
    finally { setSavingKey(null); }
  };
  // GA4 mode only: width of the trailing "active users" window. 30 is GA4's own
  // default session timeout and Realtime window; the endpoint clamps to 1-480.
  const liveViewersMode = LV_MODE_VALUES.indexOf(rawSafe.live_viewers_mode) !== -1
    ? rawSafe.live_viewers_mode : 'simulated';
  const storedWindow = (rawSafe.live_viewers_window_minutes != null && rawSafe.live_viewers_window_minutes !== '')
    ? String(rawSafe.live_viewers_window_minutes) : '30';
  const [windowDraft, setWindowDraft] = useState(null);
  const windowValue = windowDraft != null ? windowDraft : storedWindow;
  const commitWindow = async () => {
    const n = parseInt(windowValue, 10);
    const v = Number.isFinite(n) ? String(Math.min(480, Math.max(1, n))) : '30';
    setSavingKey('live_viewers_window_minutes');
    try { await setDefaultRaw('live_viewers_window_minutes', v); setWindowDraft(null); }
    finally { setSavingKey(null); }
  };

  const onToggle = async (key, next) => {
    setSavingKey(key);
    try { await setDefault(key, next); }
    finally { setSavingKey(null); }
  };

  const Row = ({ k, title }) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: savingKey === k ? 'wait' : 'pointer', padding: '4px 10px', background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 6 }}>
      <input
        type="checkbox"
        checked={!!safe[k]}
        disabled={loading || savingKey === k}
        onChange={(e) => onToggle(k, e.target.checked)}
      />
      <span style={{ color: 'var(--navy)', fontWeight: 500 }}>{title}</span>
      {savingKey === k && <span style={{ marginLeft: 4, color: 'var(--gray-500)', fontSize: 11 }}>saving…</span>}
    </label>
  );

  return (
    <details style={{ margin: '0 0 14px', padding: '8px 12px', background: '#fff8f1', border: '1px solid #ffd9b8', borderRadius: 6 }}>
      <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--navy)', fontWeight: 600 }}>
        Site-wide PDP defaults
        <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--gray-500)', fontWeight: 400 }}>
          Apply to every PDP unless a product overrides it.
        </span>
      </summary>
      <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <Row k="stickyAtcDropdowns" title="Sticky footer: variant dropdowns" />
        <Row k="stickyAtcHideHeader" title="Sticky footer: hide banner + marquee on pop-up" />
        <Row k="overlayEnabled" title="PDP image overlay (collapsed)" />
        <Row k="live_viewers_enabled" title="Live viewers badge" />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, padding: '4px 10px', background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 6 }}>
          <span style={{ color: 'var(--navy)', fontWeight: 500 }}>Live viewers: minimum count</span>
          <input
            type="number"
            min={0}
            value={minCountValue}
            disabled={loading || savingKey === 'live_viewers_min_count'}
            onChange={(e) => setMinCountDraft(e.target.value)}
            onBlur={commitMinCount}
            style={{ width: 64, padding: '3px 6px', border: '1px solid var(--gray-300)', borderRadius: 4, fontSize: 12 }}
          />
          {savingKey === 'live_viewers_min_count' && <span style={{ color: 'var(--gray-500)', fontSize: 11 }}>saving...</span>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, padding: '4px 10px', background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 6 }}>
          <span style={{ color: 'var(--navy)', fontWeight: 500 }}>Live viewers: data mode</span>
          <select
            value={liveViewersMode}
            disabled={loading || savingKey === 'live_viewers_mode'}
            onChange={async (e) => { setSavingKey('live_viewers_mode'); try { await setDefaultRaw('live_viewers_mode', e.target.value); } finally { setSavingKey(null); } }}
            style={{ padding: '3px 6px', border: '1px solid var(--gray-300)', borderRadius: 4, fontSize: 12 }}
          >
            <option value="simulated">Simulated</option>
            <option value="real">Real (focus-based)</option>
            <option value="recent">Real (GA4 active users)</option>
          </select>
          {savingKey === 'live_viewers_mode' && <span style={{ color: 'var(--gray-500)', fontSize: 11 }}>saving...</span>}
        </div>
        {liveViewersMode === 'recent' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, padding: '4px 10px', background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 6 }}>
            <span style={{ color: 'var(--navy)', fontWeight: 500 }}>GA4 window (minutes)</span>
            <input
              type="number"
              min={1}
              max={480}
              value={windowValue}
              disabled={loading || savingKey === 'live_viewers_window_minutes'}
              onChange={(e) => setWindowDraft(e.target.value)}
              onBlur={commitWindow}
              style={{ width: 64, padding: '3px 6px', border: '1px solid var(--gray-300)', borderRadius: 4, fontSize: 12 }}
            />
            <span style={{ color: 'var(--gray-500)', fontSize: 11 }}>counts anyone who opened the PDP in this window</span>
            {savingKey === 'live_viewers_window_minutes' && <span style={{ color: 'var(--gray-500)', fontSize: 11 }}>saving...</span>}
          </div>
        ) : null}
      </div>
    </details>
  );
}

function MarqueeSection({ enabled, items, duration, paddingY, sticky, colors, flashEffect, linkBanner, scrollMode, rotateIntervalSec, mobileScrollMode, mobileStaticItemIndex, onToggleEnabled, onChangeItems, onChangeDuration, onChangePaddingY, onToggleSticky, onChangeColor, onChangeFlashEffect, onToggleLinkBanner, onChangeScrollMode, onChangeRotateInterval, onChangeMobileScrollMode, onChangeMobileStaticItemIndex, dirty, customIcons = [], customIconsLoading, iconUploading, uploadMarqueeIcon, deleteMarqueeIcon, presets = [], presetsLoading = false, savePreset, deletePreset }) {
  const isRotateMode = String(scrollMode || '').toLowerCase() === 'rotate';
  const isStaticMode = String(scrollMode || '').toLowerCase() === 'static';
  const effectiveDuration = (duration == null || duration === '') ? MARQUEE_DEFAULT_DURATION : Number(duration);
  const effectivePaddingY = (paddingY == null || paddingY === '') ? MARQUEE_DEFAULT_PADDING_Y : Number(paddingY);
  // Approximate rendered strip height for the helper text. Icon dominates at 36px.
  const approxStripHeight = 36 + 2 * effectivePaddingY;
  const iconFileRef = useRef(null);
  const [pendingName, setPendingName] = useState('');
  const [managePresetsOpen, setManagePresetsOpen] = useState(false);

  // Custom icon is stored as "url:<full URL>" in the item.icon value so the
  // storefront doesn't need a secondary lookup to render it.
  const URL_PREFIX = 'url:';
  const isUrlIcon = (key) => typeof key === 'string' && key.startsWith(URL_PREFIX);
  const urlOf = (key) => isUrlIcon(key) ? key.slice(URL_PREFIX.length) : '';

  // Resolved colors for the live preview — chosen presets fall back to the
  // theme's defaults so the preview always shows what the storefront will.
  const safeColors = colors || { bg: '', text: '', icon: '', flash: '' };
  const colorKeyFor = (slot) => safeColors[slot] || MARQUEE_DEFAULT_COLOR_KEYS[slot];
  const previewBg = MARQUEE_PALETTE[colorKeyFor('bg')]?.swatch || MARQUEE_PALETTE.gold.swatch;
  const previewText = MARQUEE_PALETTE[colorKeyFor('text')]?.color || MARQUEE_PALETTE.navy.color;
  const previewIcon = MARQUEE_PALETTE[colorKeyFor('icon')]?.color || MARQUEE_PALETTE.navy.color;

  // Icon preview: either an <img> (custom URL icons) or the built-in emoji.
  // colorOverride drives mask-based recoloring so the admin preview matches
  // whichever icon color is selected. Built-in svgUrls are single-color, so
  // the mask trick produces a clean recolor; emoji fallbacks ignore color.
  const IconPreview = ({ iconKey, size = 18, colorOverride = null }) => {
    const buildMaskStyle = (src) => colorOverride ? {
      width: size, height: size, display: 'inline-block', verticalAlign: 'middle',
      backgroundColor: colorOverride,
      WebkitMask: `url(${src}) center/contain no-repeat`,
      mask: `url(${src}) center/contain no-repeat`,
    } : null;
    if (isUrlIcon(iconKey)) {
      const src = urlOf(iconKey);
      if (colorOverride) return <span aria-hidden="true" style={buildMaskStyle(src)} />;
      return <img src={src} alt="" style={{ width: size, height: size, objectFit: 'contain', verticalAlign: 'middle' }} />;
    }
    const builtin = MARQUEE_ICON_MAP[iconKey];
    if (builtin?.svgUrl) {
      if (colorOverride) return <span aria-hidden="true" style={buildMaskStyle(builtin.svgUrl)} />;
      return <img src={builtin.svgUrl} alt="" style={{ width: size, height: size, objectFit: 'contain', verticalAlign: 'middle' }} />;
    }
    return <span style={{ fontSize: size, lineHeight: 1, display: 'inline-flex', alignItems: 'center' }}>{builtin?.emoji || '•'}</span>;
  };

  const setItem = (idx, patch) => {
    const next = items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    onChangeItems(next);
  };
  const removeItem = (idx) => {
    onChangeItems(items.filter((_, i) => i !== idx));
  };
  const moveItem = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const next = items.slice();
    [next[idx], next[target]] = [next[target], next[idx]];
    onChangeItems(next);
  };
  const addItem = () => {
    if (items.length >= MARQUEE_ITEM_LIMIT) return;
    onChangeItems([...items, { icon: 'crab-silhouette', text: '' }]);
  };

  // Snapshot model: pick a preset → copy values into the row. Edits to the
  // preset later don't propagate to PDPs that already used it.
  const insertPresetIntoRow = (idx, presetId) => {
    const p = presets.find(pp => String(pp.id) === String(presetId));
    if (!p) return;
    setItem(idx, {
      icon: p.icon || '',
      text: p.text || '',
      headline: p.banner_heading || '',
      description: p.banner_subhead || '',
    });
  };

  const saveRowAsPreset = async (idx) => {
    if (!savePreset) return;
    const it = items[idx] || {};
    const suggested = (it.text || '').trim().slice(0, 80);
    const label = window.prompt('Save this marquee item as a preset.\nName it (shown in the dropdown):', suggested);
    if (label == null) return; // cancel
    const trimmed = String(label).trim();
    if (!trimmed) return;
    await savePreset({
      label: trimmed,
      icon: it.icon || '',
      text: it.text || '',
      banner_heading: it.headline || '',
      banner_subhead: it.description || '',
    });
  };

  // Sort presets alphabetically for the dropdown — the list endpoint already
  // orders by label but be defensive in case future ordering changes.
  const presetsSorted = (presets || []).slice().sort((a, b) =>
    String(a.label || '').localeCompare(String(b.label || ''))
  );

  const openIconPicker = () => {
    if (iconFileRef.current) {
      iconFileRef.current.value = '';
      iconFileRef.current.click();
    }
  };
  const handleIconFileChosen = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    const name = pendingName || file.name.replace(/\.(svg|png|webp|jpe?g)$/i, '');
    const result = await uploadMarqueeIcon(file, name);
    if (result) setPendingName('');
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <SectionHeader
        title="PDP marquee"
        subtitle="Scrolling tag strip that renders under the PDP hero banner. When enabled, this replaces the pill-style tag row next to the price."
        rightSlot={dirty ? <span style={{ color: 'var(--orange)', fontWeight: 600, fontSize: 12 }}>unsaved</span> : null}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 12, flexWrap: 'wrap' }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={!!enabled}
            onChange={(e) => onToggleEnabled(e.target.checked)}
          />
          <span style={{ fontWeight: 600 }}>Enable marquee on this PDP</span>
        </label>

        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: enabled ? 'pointer' : 'not-allowed', opacity: enabled ? 1 : 0.45 }} title={enabled ? 'Pin the marquee to the top of the viewport as the shopper scrolls' : 'Enable marquee first'}>
          <input
            type="checkbox"
            checked={!!sticky}
            disabled={!enabled}
            onChange={(e) => onToggleSticky(e.target.checked)}
          />
          <span style={{ fontWeight: 600 }}>Sticky on scroll</span>
        </label>

        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <span style={{ fontWeight: 600 }}>Scroll speed</span>
          <input
            type="number"
            min={MARQUEE_MIN_DURATION}
            max={MARQUEE_MAX_DURATION}
            step={1}
            value={duration ?? ''}
            placeholder={String(MARQUEE_DEFAULT_DURATION)}
            onChange={(e) => {
              const v = e.target.value;
              onChangeDuration(v === '' ? null : v);
            }}
            style={{ ...input, width: 70, padding: '4px 6px' }}
          />
          <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>
            sec per loop. Lower = faster. Blank = default ({MARQUEE_DEFAULT_DURATION}s). Preview mirrors live.
          </span>
        </label>

        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13 }} title="Vertical padding above and below the marquee text. Higher = taller strip.">
          <span style={{ fontWeight: 600 }}>Strip padding</span>
          <input
            type="number"
            min={MARQUEE_MIN_PADDING_Y}
            max={MARQUEE_MAX_PADDING_Y}
            step={1}
            value={paddingY ?? ''}
            placeholder={String(MARQUEE_DEFAULT_PADDING_Y)}
            onChange={(e) => {
              const v = e.target.value;
              onChangePaddingY(v === '' ? null : v);
            }}
            style={{ ...input, width: 70, padding: '4px 6px' }}
          />
          <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>
            px top/bottom. Blank = default ({MARQUEE_DEFAULT_PADDING_Y}). ≈ {approxStripHeight}px tall.
          </span>
        </label>

        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13 }} title="How an item highlights when it scrolls past the strip's center">
          <span style={{ fontWeight: 600 }}>Highlight effect</span>
          <select
            value={flashEffect || ''}
            onChange={(e) => onChangeFlashEffect && onChangeFlashEffect(e.target.value)}
            style={{ ...input, padding: '4px 6px' }}
          >
            <option value="">Default ({MARQUEE_EFFECTS.find(e => e.key === MARQUEE_EFFECT_DEFAULT)?.label})</option>
            {MARQUEE_EFFECTS.map((e) => (
              <option key={e.key} value={e.key} title={e.hint}>{e.label}</option>
            ))}
          </select>
          <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>
            {(MARQUEE_EFFECTS.find(e => e.key === (flashEffect || MARQUEE_EFFECT_DEFAULT))?.hint) || ''}
          </span>
        </label>

        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: enabled ? 'pointer' : 'not-allowed', opacity: enabled ? 1 : 0.45 }} title="When a tag highlights, swap the banner heading + subhead to that tag's overrides">
          <input
            type="checkbox"
            checked={!!linkBanner}
            disabled={!enabled}
            onChange={(e) => onToggleLinkBanner && onToggleLinkBanner(e.target.checked)}
          />
          <span style={{ fontWeight: 600 }}>Sync banner text to active tag</span>
          <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>
            Per-item Heading/Subhead inputs appear below.
          </span>
        </label>

        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, opacity: enabled ? 1 : 0.45 }}>
          <span style={{ fontWeight: 600 }}>Mode</span>
          <select
            value={isRotateMode ? 'rotate' : isStaticMode ? 'static' : ''}
            disabled={!enabled}
            onChange={(e) => onChangeScrollMode && onChangeScrollMode(e.target.value)}
            style={{ ...input, padding: '4px 8px', fontSize: 13 }}
          >
            <option value="">Scroll (default)</option>
            <option value="rotate">Don't scroll — rotate tags</option>
            <option value="static">Don't scroll — show one tag</option>
          </select>
        </label>

        {isRotateMode && (
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13 }} title="Seconds between active-tag rotations when scrolling is disabled.">
            <span style={{ fontWeight: 600 }}>Rotate interval</span>
            <input
              type="number"
              min={MARQUEE_ROTATE_INTERVAL_MIN}
              max={MARQUEE_ROTATE_INTERVAL_MAX}
              step={1}
              value={rotateIntervalSec ?? ''}
              placeholder={String(MARQUEE_ROTATE_INTERVAL_DEFAULT)}
              onChange={(e) => {
                const v = e.target.value;
                onChangeRotateInterval && onChangeRotateInterval(v === '' ? null : v);
              }}
              style={{ ...input, width: 70, padding: '4px 6px' }}
            />
            <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>
              sec between rotations. Blank = default ({MARQUEE_ROTATE_INTERVAL_DEFAULT}s).
            </span>
          </label>
        )}

        {/* Mobile behavior — separate mode override for narrow viewports (<1024px) */}
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--gray-200)' }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, opacity: enabled ? 1 : 0.45 }}>
            <span style={{ fontWeight: 600 }}>Mobile mode</span>
            <select
              value={mobileScrollMode || ''}
              disabled={!enabled}
              onChange={(e) => onChangeMobileScrollMode && onChangeMobileScrollMode(e.target.value)}
              style={{ ...input, padding: '4px 8px', fontSize: 13 }}
            >
              <option value="">Same as desktop</option>
              <option value="scroll">Always scroll</option>
              <option value="rotate">Rotate tags</option>
              <option value="static">Show one tag (static)</option>
            </select>
          </label>
          {mobileScrollMode === 'static' && items && items.length > 0 && (
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, marginTop: 6 }}>
              <span style={{ fontWeight: 600 }}>Tag to show</span>
              <select
                value={mobileStaticItemIndex ?? 0}
                disabled={!enabled}
                onChange={(e) => onChangeMobileStaticItemIndex && onChangeMobileStaticItemIndex(e.target.value)}
                style={{ ...input, padding: '4px 8px', fontSize: 13, maxWidth: 260 }}
              >
                {items.map((item, i) => (
                  <option key={i} value={i}>{item.text || `Item ${i + 1}`}</option>
                ))}
              </select>
            </label>
          )}
        </div>
      </div>

      {/* Color presets — pick a palette swatch for each slot (background / text /
          icon / flash). Empty selection falls back to the theme's SCSS defaults,
          which match the "VIP Gold" preset for bg and "Brand Navy" for text+icon. */}
      <div style={{ marginBottom: 12, display: 'grid', gridTemplateColumns: 'repeat(4, minmax(180px, 1fr))', gap: 12 }}>
        {[
          { slot: 'bg',    label: 'Background', defaultKey: MARQUEE_DEFAULT_COLOR_KEYS.bg,    allowNone: false },
          { slot: 'text',  label: 'Tag text',   defaultKey: MARQUEE_DEFAULT_COLOR_KEYS.text,  allowNone: false },
          { slot: 'icon',  label: 'Icon color', defaultKey: MARQUEE_DEFAULT_COLOR_KEYS.icon,  allowNone: false },
          // Highlight only — "None" keeps the motion effect (Flash / Wave / Bounce)
          // but suppresses the color flip + glow.
          { slot: 'flash', label: 'Highlight',  defaultKey: MARQUEE_DEFAULT_COLOR_KEYS.flash, allowNone: true  },
        ].map(({ slot, label, defaultKey, allowNone }) => (
          <ColorPresetPicker
            key={slot}
            label={label}
            defaultKey={defaultKey}
            allowNone={allowNone}
            value={safeColors[slot] || ''}
            onChange={(v) => onChangeColor && onChangeColor(slot, v)}
          />
        ))}
      </div>

      {/* Live preview of the strip — mirrors storefront animation + colors */}
      <style>{`
        @keyframes marquee-admin-scroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }
      `}</style>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>Preview ({effectiveDuration}s loop · {effectivePaddingY}px padding)</div>
        <div style={{
          background: previewBg,
          color: previewText,
          padding: `${effectivePaddingY}px 0`,
          borderRadius: 4,
          overflow: 'hidden',
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: 0.3,
          opacity: enabled ? 1 : 0.5,
        }}>
          {items.length === 0 ? (
            <span style={{ padding: '0 20px', opacity: 0.7 }}>(add items below to preview)</span>
          ) : (
            // Animate the preview the same way the storefront does: an
            // inline-flex track translated -50% in `effectiveDuration` seconds,
            // with the items rendered twice so the wrap is seamless. Changing
            // the Scroll speed input updates the animation duration live.
            <div
              key={`marquee-preview-${effectiveDuration}`}
              style={{
                display: 'inline-flex',
                whiteSpace: 'nowrap',
                animation: `marquee-admin-scroll ${effectiveDuration}s linear infinite`,
                willChange: 'transform',
                paddingLeft: 20,
              }}
            >
              {[0, 1].map((copy) => (
                <React.Fragment key={copy}>
                  {items.map((it, i) => (
                    <span key={`${copy}-${i}`} style={{ marginRight: 28, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <IconPreview iconKey={it.icon} size={16} colorOverride={previewIcon} />
                      {it.text || <em style={{ opacity: 0.6 }}>(empty)</em>}
                      <span style={{ marginLeft: 20, opacity: 0.7 }}>-</span>
                    </span>
                  ))}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '80px 170px 1fr 110px', gap: 8, alignItems: 'center', fontSize: 11, color: 'var(--gray-600)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, padding: '0 4px 6px' }}>
        <div>Order</div>
        <div>Icon</div>
        <div>Text</div>
        <div></div>
      </div>

      {items.length === 0 ? (
        <div style={{ padding: '16px', border: '1px dashed var(--gray-300)', borderRadius: 6, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>
          No items yet. Click "Add item" below.
        </div>
      ) : (
        items.map((it, idx) => (
          <div key={idx} style={{ padding: '6px 4px', borderTop: idx === 0 ? '1px solid var(--gray-200)' : 'none', borderBottom: '1px solid var(--gray-200)' }}>
            {/* Preset toolbar — pick a saved preset to fill this row, or save the
                current row as a new preset for use on other PDPs. Snapshot model:
                later edits to a preset don't change PDPs that already used it. */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginBottom: 4, fontSize: 11, color: 'var(--gray-600)' }}>
              <span style={{ textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Preset</span>
              <select
                value=""
                onChange={(e) => {
                  if (e.target.value) insertPresetIntoRow(idx, e.target.value);
                }}
                disabled={presetsLoading || presetsSorted.length === 0}
                title={presetsSorted.length === 0 ? 'No presets yet — click "Save as preset" to create one.' : 'Insert a saved preset into this row'}
                style={{ ...input, padding: '3px 6px', fontSize: 11, height: 24, minWidth: 180 }}
              >
                <option value="">{presetsLoading ? 'Loading…' : (presetsSorted.length === 0 ? '— No presets yet —' : '— Insert preset —')}</option>
                {presetsSorted.map(p => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => saveRowAsPreset(idx)}
                disabled={!savePreset || (!it.text && !it.icon && !it.headline && !it.description)}
                title="Save this row's icon, text, and banner override as a reusable preset"
                style={{ padding: '3px 8px', background: '#fff', color: 'var(--navy)', border: '1px solid var(--gray-300)', borderRadius: 3, cursor: 'pointer', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                <Save size={11} /> Save as preset
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '80px 170px 1fr 110px', gap: 8, alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={() => moveItem(idx, -1)}
                  disabled={idx === 0}
                  title="Move up"
                  style={{ padding: 4, background: '#fff', border: '1px solid var(--gray-300)', borderRadius: 3, cursor: idx === 0 ? 'not-allowed' : 'pointer', display: 'inline-flex', opacity: idx === 0 ? 0.4 : 1 }}
                ><ArrowUp size={12} /></button>
                <button
                  type="button"
                  onClick={() => moveItem(idx, 1)}
                  disabled={idx === items.length - 1}
                  title="Move down"
                  style={{ padding: 4, background: '#fff', border: '1px solid var(--gray-300)', borderRadius: 3, cursor: idx === items.length - 1 ? 'not-allowed' : 'pointer', display: 'inline-flex', opacity: idx === items.length - 1 ? 0.4 : 1 }}
                ><ArrowDown size={12} /></button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 22, textAlign: 'center', display: 'inline-flex', justifyContent: 'center' }}>
                  <IconPreview iconKey={it.icon} size={18} />
                </span>
                <select
                  value={it.icon || ''}
                  onChange={(e) => setItem(idx, { icon: e.target.value })}
                  style={{ ...input, padding: '4px 6px', flex: 1 }}
                >
                  <option value="">— none —</option>
                  <optgroup label="Built-in">
                    {MARQUEE_ICONS.map(ic => (
                      <option key={ic.key} value={ic.key}>{ic.label}</option>
                    ))}
                  </optgroup>
                  {customIcons.length > 0 && (
                    <optgroup label="Custom (library)">
                      {customIcons.map(ci => (
                        <option key={ci.slug} value={URL_PREFIX + ci.url}>{ci.name}</option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>
              <input
                type="text"
                value={it.text || ''}
                placeholder="e.g. Authentic Blue Crabs"
                onChange={(e) => setItem(idx, { text: e.target.value })}
                style={input}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => removeItem(idx)}
                  title="Remove"
                  style={{ padding: '6px 10px', background: '#fff', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 4, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}
                ><Trash2 size={12} /> Remove</button>
              </div>
            </div>
            {linkBanner && (
              <div style={{ display: 'grid', gridTemplateColumns: '80px 170px 1fr 110px', gap: 8, alignItems: 'flex-start', marginTop: 6 }}>
                <div style={{ fontSize: 10, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600, paddingTop: 8, gridColumn: '1 / 3', textAlign: 'right', paddingRight: 8 }}>Banner override</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <input
                    type="text"
                    value={it.headline || ''}
                    placeholder="Heading override (blank = keep banner heading)"
                    onChange={(e) => setItem(idx, { headline: e.target.value })}
                    style={{ ...input, fontSize: 12 }}
                  />
                  <textarea
                    value={it.description || ''}
                    placeholder="Subhead override — extra info shown when this tag highlights (blank = keep banner subhead)"
                    onChange={(e) => setItem(idx, { description: e.target.value })}
                    rows={2}
                    style={{ ...input, fontSize: 12, fontFamily: 'inherit', resize: 'vertical', minHeight: 38 }}
                  />
                </div>
                <div />
              </div>
            )}
          </div>
        ))
      )}

      <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            type="button"
            onClick={addItem}
            disabled={items.length >= MARQUEE_ITEM_LIMIT}
            style={{ padding: '6px 12px', background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 4, cursor: items.length >= MARQUEE_ITEM_LIMIT ? 'not-allowed' : 'pointer', fontSize: 12, opacity: items.length >= MARQUEE_ITEM_LIMIT ? 0.5 : 1 }}
          >+ Add item</button>
          <button
            type="button"
            onClick={() => setManagePresetsOpen(true)}
            title="Rename or delete saved presets. Changes don't propagate to PDPs already using a preset."
            style={{ padding: '6px 12px', background: '#fff', color: 'var(--navy)', border: '1px solid var(--gray-300)', borderRadius: 4, cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            Manage presets ({presets.length})
          </button>
        </div>
        <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>
          {items.length} / {MARQUEE_ITEM_LIMIT} items
        </span>
      </div>

      {managePresetsOpen && (
        <ManageMarqueePresetsModal
          presets={presetsSorted}
          loading={presetsLoading}
          onSave={savePreset}
          onDelete={deletePreset}
          onClose={() => setManagePresetsOpen(false)}
          IconPreview={IconPreview}
        />
      )}

      {/* Custom icon library — shared across all products. Icons uploaded
          here appear in every marquee icon dropdown as "Custom (library)".
          Stored in R2 + Neon registry. */}
      <div style={{ marginTop: 24, padding: 14, background: 'var(--gray-50, #fafafa)', border: '1px solid var(--gray-200)', borderRadius: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>Custom icon library</div>
            <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>Shared across all products. SVG / PNG / WebP, max {MAX_MARQUEE_ICON_BYTES / 1024} KB. Deleting from the library doesn't remove it from products already using it.</div>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Name (optional)"
              value={pendingName}
              onChange={(e) => setPendingName(e.target.value)}
              style={{ ...input, padding: '6px 8px', width: 180, fontSize: 12 }}
              disabled={iconUploading}
            />
            <button
              type="button"
              onClick={openIconPicker}
              disabled={iconUploading}
              style={{ padding: '6px 12px', background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 4, cursor: iconUploading ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}
            >
              <Upload size={12} /> {iconUploading ? 'Uploading...' : 'Upload icon'}
            </button>
            <input
              ref={iconFileRef}
              type="file"
              accept="image/svg+xml,image/png,image/webp,image/jpeg"
              onChange={handleIconFileChosen}
              style={{ display: 'none' }}
            />
          </div>
        </div>

        {customIconsLoading ? (
          <div style={{ fontSize: 12, color: 'var(--gray-500)', padding: '10px 0' }}>Loading library...</div>
        ) : customIcons.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--gray-500)', padding: '10px 0', fontStyle: 'italic' }}>No custom icons yet. Upload an SVG above to add one.</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10 }}>
            {customIcons.map(ci => (
              <div key={ci.slug} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 8, background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 4 }}>
                <div style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#dc582a', borderRadius: 3, flexShrink: 0 }}>
                  <img src={ci.url} alt={ci.name} style={{ width: 26, height: 26, objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={ci.name}>{ci.name}</div>
                  <div style={{ fontSize: 10, color: 'var(--gray-500)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={ci.slug}>{ci.slug}</div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm(`Remove "${ci.name}" from the library? Products already using this icon will keep rendering it (the asset isn't deleted from storage).`)) {
                      deleteMarqueeIcon(ci.slug);
                    }
                  }}
                  title="Remove from library"
                  style={{ padding: 4, background: 'none', color: '#b91c1c', border: 'none', borderRadius: 3, cursor: 'pointer', display: 'inline-flex' }}
                ><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Modal for renaming and deleting saved marquee presets. Snapshot model means
// edits here don't propagate to PDPs already using the preset — the modal makes
// that explicit in its subhead.
function ManageMarqueePresetsModal({ presets, loading, onSave, onDelete, onClose, IconPreview }) {
  // Local label edits keyed by preset id. Empty/undefined = no edit.
  const [labelEdits, setLabelEdits] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Esc to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleRename = async (p) => {
    const nextLabel = labelEdits[p.id];
    if (nextLabel == null || nextLabel === p.label) return;
    if (!String(nextLabel).trim()) return;
    setSavingId(p.id);
    try {
      await onSave({
        id: p.id,
        label: nextLabel,
        icon: p.icon,
        text: p.text,
        banner_heading: p.banner_heading,
        banner_subhead: p.banner_subhead,
      });
      // The list reload happens in the parent — clear the local edit so the
      // input snaps back to displaying the persisted value.
      setLabelEdits(prev => {
        const next = { ...prev };
        delete next[p.id];
        return next;
      });
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete preset "${p.label}"?\n\nPDPs already using this preset keep their copied values — only the library entry is removed.`)) return;
    setDeletingId(p.id);
    try {
      await onDelete(p.id);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(10, 15, 25, 0.5)', zIndex: 9998, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 32, overflow: 'auto' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: '100%', maxWidth: 720, background: '#fff', borderRadius: 8, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden', marginTop: 40 }}
      >
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--gray-200)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--navy)' }}>Manage marquee presets</div>
            <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>
              Rename or delete saved presets. Edits don't propagate to PDPs already using a preset (snapshot model).
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 6, display: 'inline-flex' }}
          ><X size={18} /></button>
        </div>

        <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
          {loading ? (
            <div style={{ padding: 20, fontSize: 13, color: 'var(--gray-500)' }}>Loading presets…</div>
          ) : presets.length === 0 ? (
            <div style={{ padding: 20, fontSize: 13, color: 'var(--gray-500)', fontStyle: 'italic' }}>
              No presets yet. From a marquee item row, click "Save as preset" to add one.
            </div>
          ) : (
            <div>
              {presets.map(p => {
                const edited = labelEdits[p.id];
                const isDirty = edited != null && edited !== p.label && String(edited).trim() !== '';
                return (
                  <div key={p.id} style={{ padding: '12px 18px', borderBottom: '1px solid var(--gray-100)', display: 'grid', gridTemplateColumns: '32px 1fr auto', gap: 12, alignItems: 'flex-start' }}>
                    <div style={{ paddingTop: 6, display: 'flex', justifyContent: 'center' }}>
                      <IconPreview iconKey={p.icon || ''} size={20} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <input
                        type="text"
                        value={edited != null ? edited : (p.label || '')}
                        onChange={(e) => setLabelEdits(prev => ({ ...prev, [p.id]: e.target.value }))}
                        placeholder="Preset name"
                        style={{ ...input, padding: '5px 8px', fontSize: 13, fontWeight: 600, width: '100%' }}
                      />
                      <div style={{ marginTop: 6, fontSize: 12, color: 'var(--gray-700)', display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <div><span style={{ color: 'var(--gray-500)', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4, marginRight: 6 }}>Text</span>{p.text || <em style={{ color: 'var(--gray-400)' }}>(empty)</em>}</div>
                        {p.banner_heading && (
                          <div><span style={{ color: 'var(--gray-500)', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4, marginRight: 6 }}>Heading</span>{p.banner_heading}</div>
                        )}
                        {p.banner_subhead && (
                          <div style={{ color: 'var(--gray-600)' }}><span style={{ color: 'var(--gray-500)', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4, marginRight: 6 }}>Subhead</span>{p.banner_subhead}</div>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <button
                        type="button"
                        onClick={() => handleRename(p)}
                        disabled={!isDirty || savingId === p.id}
                        style={{ padding: '5px 10px', background: isDirty ? 'var(--orange)' : '#fff', color: isDirty ? '#fff' : 'var(--gray-500)', border: '1px solid ' + (isDirty ? 'var(--orange)' : 'var(--gray-300)'), borderRadius: 4, cursor: isDirty ? 'pointer' : 'not-allowed', fontSize: 12, opacity: savingId === p.id ? 0.6 : 1 }}
                      >{savingId === p.id ? 'Saving…' : 'Rename'}</button>
                      <button
                        type="button"
                        onClick={() => handleDelete(p)}
                        disabled={deletingId === p.id}
                        title="Remove this preset from the library"
                        style={{ padding: '5px 8px', background: '#fff', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 4, cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4, opacity: deletingId === p.id ? 0.6 : 1 }}
                      ><Trash2 size={12} /> {deletingId === p.id ? 'Deleting…' : 'Delete'}</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ padding: '12px 18px', borderTop: '1px solid var(--gray-200)', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            style={{ padding: '7px 16px', background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}
          >Close</button>
        </div>
      </div>
    </div>
  );
}

function ThumbsPositionSection({ value, onChange, dirty }) {
  return (
    <section style={{ background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8, padding: 16, marginBottom: 14 }}>
      <SectionHeader
        title="Thumbnail Layout"
        subtitle="Where the thumbnail strip renders relative to the main image. Left puts thumbs in a vertical column beside the main image — gives the main photo more vertical room above the fold."
        rightSlot={dirty ? <span style={{ fontSize: 11, color: 'var(--orange)', fontWeight: 700 }}>Unsaved</span> : null}
      />
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ fontSize: 13, color: 'var(--gray-700)' }}>Position:</label>
        <select
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          style={{ padding: '7px 10px', fontSize: 13, border: '1px solid var(--gray-300)', borderRadius: 4, background: '#fff', minWidth: 220 }}
        >
          {THUMBS_POSITIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
        <span style={{ color: 'var(--gray-500)', fontSize: 11 }}>
          Stored as <code>pdp.thumbs_position</code> metafield. Blank = bottom default.
        </span>
      </div>
    </section>
  );
}

// Inline color-preset picker for the marquee Background / Text / Icon / Highlight
// slots. A row of swatches rather than a <select> so the brand palette is
// visible at a glance — matters when team members are eyeballing variations.
// Empty value (the leading "Default" chip) clears the metafield → SCSS default.
function ColorPresetPicker({ label, defaultKey, allowNone = false, value, onChange }) {
  const defaultPreset = defaultKey ? MARQUEE_PALETTE[defaultKey] : null;
  // Currently-applied preset for the human-readable status line. 'none' is a
  // special marker only valid on the Highlight slot — it has no palette
  // entry but reads as "None — no color tint" in the status line.
  const isNone = value === 'none';
  const activePreset = (!value || isNone) ? defaultPreset : MARQUEE_PALETTE[value];
  let activeLabel = '';
  if (isNone) activeLabel = 'None — motion only';
  else if (value && activePreset) activeLabel = activePreset.label;
  else if (!value && activePreset) activeLabel = `default = ${activePreset.label}`;
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
        {label}
        {activeLabel && (
          <span style={{ marginLeft: 6, color: 'var(--gray-400)', fontWeight: 500, textTransform: 'none', letterSpacing: 0, fontStyle: 'italic' }}>
            {activeLabel}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
        <button
          type="button"
          onClick={() => onChange('')}
          title={defaultPreset ? `Use theme default (${defaultPreset.label})` : 'Use theme default'}
          style={{
            height: 26, padding: '0 8px', borderRadius: 4,
            border: value === '' ? '2px solid var(--orange)' : '1px solid var(--gray-300)',
            background: '#fff', cursor: 'pointer', fontSize: 11, fontWeight: 600, color: 'var(--gray-600)',
          }}
        >
          Default
        </button>
        {MARQUEE_PALETTE_ORDER.map((presetKey) => {
          const preset = MARQUEE_PALETTE[presetKey];
          const selected = value === presetKey;
          // Mark the swatch the theme would fall back to with a small "D"
          // badge in the corner — answers "what's Default?" at a glance
          // without making the picker row taller.
          const isDefaultMatch = presetKey === defaultKey;
          return (
            <button
              key={presetKey}
              type="button"
              onClick={() => onChange(presetKey)}
              title={isDefaultMatch ? `${preset.label} (theme default)` : preset.label}
              aria-label={preset.label}
              style={{
                position: 'relative',
                width: 26, height: 26, borderRadius: 4,
                border: selected ? '2px solid var(--orange)' : '1px solid var(--gray-300)',
                background: preset.swatch,
                cursor: 'pointer',
                padding: 0,
              }}
            >
              {isDefaultMatch && (
                <span aria-hidden="true" style={{
                  position: 'absolute', top: -5, right: -5,
                  minWidth: 13, height: 13, padding: '0 3px', borderRadius: 7,
                  background: 'var(--gray-700)', color: '#fff',
                  fontSize: 9, fontWeight: 700, lineHeight: '13px', textAlign: 'center',
                  border: '1px solid #fff',
                  letterSpacing: 0,
                }}>D</span>
              )}
            </button>
          );
        })}
        {allowNone && (
          // "None" chip — only on the Highlight picker. Saves flash_color =
          // "none" so the storefront keeps whichever motion effect is active
          // (Flash / Wave / Bounce) but flattens the color flip + glow vars.
          // Visual: white square with a diagonal slash so it reads as
          // "no color" rather than "white".
          <button
            type="button"
            onClick={() => onChange('none')}
            title="None — keep the motion effect, skip the color tint and glow"
            aria-label="None (no color tint)"
            style={{
              width: 26, height: 26, borderRadius: 4,
              border: value === 'none' ? '2px solid var(--orange)' : '1px solid var(--gray-300)',
              background: 'linear-gradient(to top right, #fff calc(50% - 1px), #c44 calc(50% - 1px), #c44 calc(50% + 1px), #fff calc(50% + 1px))',
              cursor: 'pointer',
              padding: 0,
            }}
          />
        )}
      </div>
    </div>
  );
}

function UgcSection({ enabled, heading, subhead, layout, items, onToggleEnabled, onChangeHeading, onChangeSubhead, onChangeLayout, onChangeItems, dirty }) {
  const [pickerOpen, setPickerOpen] = useState(false);

  const onPick = (assets) => {
    setPickerOpen(false);
    if (!assets || assets.length === 0) return;
    const r2Url = (key) => `https://cpx-prototype.netlify.app/.netlify/functions/r2-image?key=${encodeURIComponent(key)}`;
    const newItems = assets.map((a) => ({
      url: a.r2_original_key ? r2Url(a.r2_original_key) : '',
      thumbUrl: a.r2_thumb_key ? r2Url(a.r2_thumb_key) : '',
      caption: a.alt_text || '',
      handle: '',
      platform: '',
      sourceUrl: '',
      mediaAssetId: a.id,
    }));
    const merged = [...items, ...newItems].slice(0, UGC_ITEM_LIMIT);
    onChangeItems(merged);
  };

  const setItem = (idx, patch) => {
    onChangeItems(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };
  const removeItem = (idx) => onChangeItems(items.filter((_, i) => i !== idx));
  const moveItem = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const next = items.slice();
    [next[idx], next[target]] = [next[target], next[idx]];
    onChangeItems(next);
  };

  const remainingSlots = UGC_ITEM_LIMIT - items.length;

  return (
    <section style={{ background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8, padding: 16, marginBottom: 14 }}>
      <SectionHeader
        title="UGC Gallery"
        subtitle="Customer photos shown on the PDP between the VIP Club block and the Original Source content. Upload images to the Media Library first, then pick from there."
        rightSlot={dirty ? <span style={{ fontSize: 11, color: 'var(--orange)', fontWeight: 700 }}>Unsaved</span> : null}
      />

      <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input
            type="checkbox"
            checked={!!enabled}
            onChange={(e) => onToggleEnabled(e.target.checked)}
            style={{ accentColor: 'var(--orange)' }}
          />
          Show UGC panel on this product's PDP
        </label>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <span style={{ color: 'var(--gray-600)', fontWeight: 600 }}>Layout:</span>
          <select
            value={layout || UGC_LAYOUT_DEFAULT}
            onChange={(e) => onChangeLayout(e.target.value)}
            style={{ padding: '5px 8px', border: '1px solid var(--gray-300)', borderRadius: 4, fontSize: 13, background: '#fff' }}
          >
            <option value="grid">2-row Grid</option>
            <option value="carousel">1-row Carousel</option>
          </select>
        </label>
        <span style={{ color: 'var(--gray-500)', fontSize: 11 }}>
          {items.length} / {UGC_ITEM_LIMIT} items
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', display: 'block', marginBottom: 4 }}>
            Heading <span style={{ color: 'var(--gray-400)', fontWeight: 400 }}>(optional, theme has a default)</span>
          </label>
          <input
            type="text"
            value={heading || ''}
            onChange={(e) => onChangeHeading(e.target.value)}
            placeholder="CrabPlace.com fans in the wild"
            style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--gray-300)', borderRadius: 4, fontSize: 13, boxSizing: 'border-box' }}
          />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', display: 'block', marginBottom: 4 }}>
            Subhead <span style={{ color: 'var(--gray-400)', fontWeight: 400 }}>(optional)</span>
          </label>
          <input
            type="text"
            value={subhead || ''}
            onChange={(e) => onChangeSubhead(e.target.value)}
            placeholder="Real photos. Real customers. Real claws."
            style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--gray-300)', borderRadius: 4, fontSize: 13, boxSizing: 'border-box' }}
          />
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          disabled={remainingSlots <= 0}
          style={{
            padding: '7px 14px', background: remainingSlots <= 0 ? 'var(--gray-300)' : 'var(--orange)',
            color: '#fff', border: 'none', borderRadius: 4, cursor: remainingSlots <= 0 ? 'not-allowed' : 'pointer',
            fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6,
          }}
        >
          <ImageIcon size={14} /> Pick from Media Library
        </button>
        <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>
          Tip: tag UGC uploads with category=ugc in the Media Library so they're easy to find.
        </span>
      </div>

      {items.length === 0 ? (
        <div style={{ padding: 30, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13, border: '1px dashed var(--gray-300)', borderRadius: 6 }}>
          No items yet. Pick from the Media Library to add customer photos.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map((item, idx) => (
            <div key={idx} style={{ display: 'grid', gridTemplateColumns: '90px 1fr auto', gap: 10, alignItems: 'flex-start', padding: 10, border: '1px solid var(--gray-200)', borderRadius: 6, background: '#fafafa' }}>
              <div style={{ width: 90, height: 90, borderRadius: 4, overflow: 'hidden', background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {item.thumbUrl || item.url ? (
                  <img src={item.thumbUrl || item.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <span style={{ color: 'var(--gray-400)', fontSize: 11 }}>no image</span>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-600)', display: 'block', marginBottom: 2 }}>Caption</label>
                  <input
                    type="text"
                    value={item.caption || ''}
                    onChange={(e) => setItem(idx, { caption: e.target.value })}
                    placeholder="Best crab feast ever!"
                    style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--gray-300)', borderRadius: 4, fontSize: 12, boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-600)', display: 'block', marginBottom: 2 }}>Handle</label>
                  <input
                    type="text"
                    value={item.handle || ''}
                    onChange={(e) => setItem(idx, { handle: e.target.value })}
                    placeholder="@johndoe"
                    style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--gray-300)', borderRadius: 4, fontSize: 12, boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-600)', display: 'block', marginBottom: 2 }}>Platform</label>
                  <select
                    value={item.platform || ''}
                    onChange={(e) => setItem(idx, { platform: e.target.value })}
                    style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--gray-300)', borderRadius: 4, fontSize: 12, boxSizing: 'border-box', background: '#fff' }}
                  >
                    {UGC_PLATFORMS.map(p => <option key={p} value={p}>{p || '— select —'}</option>)}
                  </select>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-600)', display: 'block', marginBottom: 2 }}>
                    Source URL <span style={{ color: 'var(--gray-400)', fontWeight: 400 }}>(link back to original post — optional)</span>
                  </label>
                  <input
                    type="url"
                    value={item.sourceUrl || ''}
                    onChange={(e) => setItem(idx, { sourceUrl: e.target.value })}
                    placeholder="https://instagram.com/p/abc123"
                    style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--gray-300)', borderRadius: 4, fontSize: 12, boxSizing: 'border-box' }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <button
                  type="button" onClick={() => moveItem(idx, -1)} disabled={idx === 0}
                  title="Move up"
                  style={{ padding: 4, background: 'transparent', border: '1px solid var(--gray-300)', borderRadius: 3, cursor: idx === 0 ? 'not-allowed' : 'pointer', opacity: idx === 0 ? 0.4 : 1 }}
                ><ArrowUp size={12} /></button>
                <button
                  type="button" onClick={() => moveItem(idx, 1)} disabled={idx === items.length - 1}
                  title="Move down"
                  style={{ padding: 4, background: 'transparent', border: '1px solid var(--gray-300)', borderRadius: 3, cursor: idx === items.length - 1 ? 'not-allowed' : 'pointer', opacity: idx === items.length - 1 ? 0.4 : 1 }}
                ><ArrowDown size={12} /></button>
                <button
                  type="button" onClick={() => removeItem(idx)}
                  title="Remove"
                  style={{ padding: 4, background: 'transparent', border: '1px solid #fca5a5', color: '#b91c1c', borderRadius: 3, cursor: 'pointer' }}
                ><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {pickerOpen && (
        <MediaPicker
          multiSelect={true}
          onClose={() => setPickerOpen(false)}
          onPick={onPick}
        />
      )}
    </section>
  );
}

// Color picker for the recipe button — brand swatches + a free custom color,
// with a "Default" chip that clears the value back to the storefront default.
function RecipeColorField({ label, value, defaultHex, onChange }) {
  const v = (value || '').trim();
  const matches = (hex) => v.toLowerCase() === hex.toLowerCase();
  return (
    <div>
      <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', display: 'block', marginBottom: 4 }}>{label}</label>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => onChange('')}
          title="Use the storefront default color"
          style={{
            height: 26, padding: '0 9px', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer',
            color: 'var(--gray-600)', background: '#fff',
            border: !v ? '2px solid var(--orange)' : '1px solid var(--gray-300)',
          }}
        >
          Default
        </button>
        {RECIPE_COLOR_SWATCHES.map(hex => (
          <button
            key={hex}
            type="button"
            onClick={() => onChange(hex)}
            title={hex}
            aria-label={hex}
            style={{
              width: 26, height: 26, borderRadius: 4, cursor: 'pointer', padding: 0, background: hex,
              border: matches(hex) ? '2px solid var(--orange)' : '1px solid var(--gray-300)',
            }}
          />
        ))}
        <input
          type="color"
          value={/^#[0-9a-f]{6}$/i.test(v) ? v : defaultHex}
          onChange={(e) => onChange(e.target.value)}
          title="Custom color"
          style={{ width: 34, height: 28, padding: 1, border: '1px solid var(--gray-300)', borderRadius: 4, cursor: 'pointer' }}
        />
        <span style={{ fontSize: 11, color: 'var(--gray-500)', fontFamily: 'monospace' }}>{v || `default (${defaultHex})`}</span>
      </div>
    </div>
  );
}

// Searchable recipe picker — used per photo when tagging recipes. A plain
// <select> is unworkable once there are dozens of recipes, so this is a
// type-to-filter combobox. value is the recipe slug ('' = auto-match by SKU).
function RecipeSelect({ value, recipes, recipesLoading, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const current = recipes.find(r => r.slug === value);
  const label = value ? (current ? current.name : value) : 'Auto — best match for this product';
  const ql = q.trim().toLowerCase();
  const filtered = ql ? recipes.filter(r => String(r.name || '').toLowerCase().includes(ql)) : recipes;
  const pick = (slug) => { onChange(slug); setOpen(false); setQ(''); };

  const optStyle = (active) => ({
    display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', fontSize: 12.5,
    border: 'none', cursor: 'pointer', background: active ? 'var(--peach)' : '#fff',
    color: active ? 'var(--navy)' : 'var(--gray-700)', fontWeight: active ? 700 : 400,
  });

  return (
    <div ref={rootRef} style={{ position: 'relative', maxWidth: 360 }}>
      <button
        type="button"
        onClick={() => { setOpen(o => !o); setQ(''); }}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
          padding: '6px 8px', border: '1px solid var(--gray-300)', borderRadius: 4, fontSize: 12.5,
          background: '#fff', cursor: 'pointer', boxSizing: 'border-box',
          color: value ? 'var(--gray-700)' : 'var(--gray-500)',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <ChevronDown size={14} style={{ flexShrink: 0, color: 'var(--gray-400)' }} />
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 3, zIndex: 30,
          background: '#fff', border: '1px solid var(--gray-300)', borderRadius: 6,
          boxShadow: '0 8px 24px rgba(0,0,0,0.18)', overflow: 'hidden',
        }}>
          <div style={{ padding: 6, borderBottom: '1px solid var(--gray-200)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Search size={13} style={{ color: 'var(--gray-400)', flexShrink: 0 }} />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search recipes…"
              style={{ flex: 1, border: 'none', outline: 'none', fontSize: 12.5, padding: '2px 0' }}
            />
          </div>
          <div style={{ maxHeight: 230, overflowY: 'auto' }}>
            <button type="button" onClick={() => pick('')} style={optStyle(!value)}>
              Auto — best match for this product
            </button>
            {recipesLoading && <div style={{ padding: '7px 10px', fontSize: 12, color: 'var(--gray-500)' }}>Loading recipes…</div>}
            {filtered.map(r => (
              <button key={r.slug} type="button" onClick={() => pick(r.slug)} style={optStyle(r.slug === value)}>
                {r.name}
              </button>
            ))}
            {!recipesLoading && filtered.length === 0 && (
              <div style={{ padding: '8px 10px', fontSize: 12, color: 'var(--gray-500)' }}>No recipes match “{q}”.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// PDP image overlay — expand/collapse text panel anchored to the TOP of the
// Price Guarantee modal — per-PDP copy for the "Best Seafood, Best Price — We
// Guarantee It" popup that opens from the guarantee link under the price. Every
// field is optional: leave one blank and the storefront uses the shared,
// product-agnostic default (so a lobster page never says "crabs"). Use this to
// tell each product's price story in its own words.
function GuaranteeSection({ guarantee, patchGuarantee }) {
  const {
    title = '', lede = '', body = '', cardTitle = '', cardBody = '', dirty = false,
  } = guarantee || {};

  const labelStyle = { fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', display: 'block', marginBottom: 4 };
  const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid var(--gray-300)', borderRadius: 4, fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit' };
  const areaStyle = { ...inputStyle, resize: 'vertical' };

  return (
    <section style={{ background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8, padding: 16, marginBottom: 14 }}>
      <SectionHeader
        title="Price guarantee popup"
        subtitle={'The "Best Seafood, Best Price — We Guarantee It" modal that opens under the price. Write copy specific to this product; leave any field blank to use the site-wide default.'}
        rightSlot={dirty ? <span style={{ fontSize: 11, color: 'var(--orange)', fontWeight: 700 }}>Unsaved</span> : null}
      />

      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Modal title</label>
        <input
          type="text"
          value={title}
          onChange={(e) => patchGuarantee({ title: e.target.value })}
          placeholder="Best Seafood, Best Price — We Guarantee It"
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Intro line <span style={{ color: 'var(--gray-400)', fontWeight: 400 }}>(the bold promise under the title)</span></label>
        <textarea
          value={lede}
          onChange={(e) => patchGuarantee({ lede: e.target.value })}
          placeholder="Find the same Maine lobster tails — same size and count — delivered for less anywhere online and we’ll match it."
          rows={2}
          style={areaStyle}
        />
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Body paragraph <span style={{ color: 'var(--gray-400)', fontWeight: 400 }}>(the why-we’re-the-best-price story)</span></label>
        <textarea
          value={body}
          onChange={(e) => patchGuarantee({ body: e.target.value })}
          placeholder="Thirty years in e-commerce, relationships at the source, hard-won UPS rates, right-sized packaging, no middleman, hand-graded and packed in Crisfield…"
          rows={5}
          style={areaStyle}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12, borderTop: '1px solid var(--gray-200)', paddingTop: 12 }}>
        <div>
          <label style={labelStyle}>Guarantee card heading</label>
          <input
            type="text"
            value={cardTitle}
            onChange={(e) => patchGuarantee({ guaranteeTitle: e.target.value })}
            placeholder="Lowest Delivered Price Guarantee"
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Guarantee card text</label>
          <textarea
            value={cardBody}
            onChange={(e) => patchGuarantee({ guaranteeBody: e.target.value })}
            placeholder="Find the same item, size, and quantity delivered for less anywhere online, and we’ll match it."
            rows={2}
            style={areaStyle}
          />
        </div>
      </div>
    </section>
  );
}

// PDP Live Viewers badge -- per-product override of the site-wide "N people are
// viewing this" social-proof badge (site default lives in PdpDefaultsPanel above).
// Enabled is tri-state: inherit the store default, or force On/Off for this
// product. Minimum count blank = inherit the store default; otherwise stored as
// an integer-as-string in the pdp_live_viewers metafield.
function LiveViewersSection({ liveViewers, patchLiveViewers }) {
  const { enabled = null, minCount = null, dirty = false } = liveViewers || {};
  const labelStyle = { fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', display: 'block', marginBottom: 4 };
  const enabledMode = enabled === true ? 'on' : (enabled === false ? 'off' : 'inherit');
  const onEnabledChange = (mode) => {
    patchLiveViewers({ enabled: mode === 'on' ? true : (mode === 'off' ? false : null) });
  };
  const segBtn = (active, radius) => ({
    padding: '6px 14px', fontSize: 12, fontWeight: active ? 700 : 500, cursor: 'pointer',
    border: '1px solid ' + (active ? 'var(--orange)' : 'var(--gray-300)'),
    background: active ? 'rgba(220, 88, 42, 0.08)' : '#fff',
    color: active ? 'var(--orange)' : 'var(--gray-600)',
    borderRadius: radius,
  });
  return (
    <section style={{ background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8, padding: 16, marginBottom: 14 }}>
      <SectionHeader
        title="Live viewers badge"
        subtitle={'The "N people are viewing this" social-proof badge on the PDP. Inherit the store-wide default, or override it for this product.'}
        rightSlot={dirty ? <span style={{ fontSize: 11, color: 'var(--orange)', fontWeight: 700 }}>Unsaved</span> : null}
      />

      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Enabled</label>
        <div style={{ display: 'inline-flex' }}>
          <button type="button" onClick={() => onEnabledChange('inherit')} style={segBtn(enabledMode === 'inherit', '6px 0 0 6px')}>Inherit store default</button>
          <button type="button" onClick={() => onEnabledChange('on')} style={{ ...segBtn(enabledMode === 'on', '0'), borderLeft: 'none' }}>On</button>
          <button type="button" onClick={() => onEnabledChange('off')} style={{ ...segBtn(enabledMode === 'off', '0 6px 6px 0'), borderLeft: 'none' }}>Off</button>
        </div>
      </div>

      <div style={{ maxWidth: 260 }}>
        <label style={labelStyle}>Minimum viewer count <span style={{ color: 'var(--gray-400)', fontWeight: 400 }}>(override)</span></label>
        <input
          type="number"
          min={0}
          value={minCount == null ? '' : minCount}
          onChange={(e) => patchLiveViewers({ minCount: e.target.value === '' ? null : Number(e.target.value) })}
          placeholder="Store default"
          style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--gray-300)', borderRadius: 4, fontSize: 13, boxSizing: 'border-box' }}
        />
        <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 4 }}>
          Badge hides when the viewer count is at or below this. Blank = use the store default.
        </div>
      </div>
    </section>
  );
}

// main PDP image. Per-product enable + body text + font size + text/bg color
// presets + bg opacity + start-expanded toggle. Site-wide default toggle in
// PdpDefaultsPanel above lets every PDP carry a baseline message that a
// per-product override can flip off.
function OverlaySection({ overlay, patchOverlay, resetOverlayEnabled, siteDefaultEnabled }) {
  const {
    enabled = false, text = '', teaser = '', fontSize = OVERLAY_FONT_SIZE_DEFAULT,
    textColor = '', bgColor = '', bgOpacity = OVERLAY_OPACITY_DEFAULT,
    startExpanded = false, enabledExplicit = false, dirty = false,
  } = overlay || {};

  const resolvedTextColor = MARQUEE_PALETTE[textColor]?.color || '#ffffff';
  const resolvedBgHex = MARQUEE_PALETTE[bgColor]?.color || '#003b5c';
  // Admin MARQUEE_PALETTE entries don't carry an `rgb` triplet (only the
  // Netlify-fn copy does), so compute it from the hex for the live preview.
  const hexToRgb = (hex) => {
    const h = String(hex || '').replace('#', '');
    if (h.length !== 6) return '0, 0, 0';
    return [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16)).join(', ');
  };
  const previewBg = `rgba(${hexToRgb(resolvedBgHex)}, ${Math.max(0, Math.min(100, Number(bgOpacity) || 0)) / 100})`;
  const teaserPreview = teaser || (text ? (text.length > 80 ? text.slice(0, 80) + '…' : text) : 'Drop a teaser line or full paragraph below');

  return (
    <section style={{ background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8, padding: 16, marginBottom: 14 }}>
      <SectionHeader
        title="PDP image overlay"
        subtitle="Collapsed strip at the top of the main image expands on click to show a paragraph above the fold. Use it for shipping cutoffs, sale flags, or product story copy."
        rightSlot={dirty ? <span style={{ fontSize: 11, color: 'var(--orange)', fontWeight: 700 }}>Unsaved</span> : null}
      />

      <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input
            type="checkbox"
            checked={!!enabled}
            onChange={(e) => patchOverlay({ enabled: e.target.checked })}
            style={{ accentColor: 'var(--orange)' }}
          />
          Show overlay on this product's PDP
        </label>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input
            type="checkbox"
            checked={!!startExpanded}
            onChange={(e) => patchOverlay({ startExpanded: e.target.checked })}
            style={{ accentColor: 'var(--orange)' }}
          />
          Start expanded on first paint
        </label>
        {enabledExplicit && (
          <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>
            Site default: {siteDefaultEnabled ? 'ON' : 'OFF'} · this product overrides to {enabled ? 'ON' : 'OFF'} ·
            <button
              type="button"
              onClick={() => resetOverlayEnabled()}
              style={{ marginLeft: 6, border: 'none', background: 'transparent', color: 'var(--orange)', cursor: 'pointer', padding: 0, fontSize: 11, textDecoration: 'underline' }}
            >
              Reset to default
            </button>
          </span>
        )}
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', display: 'block', marginBottom: 4 }}>
          Body text <span style={{ color: 'var(--gray-400)', fontWeight: 400 }}>(shown when expanded)</span>
        </label>
        <textarea
          value={text}
          onChange={(e) => patchOverlay({ text: e.target.value })}
          placeholder="Order today for delivery this week. Our crabs are caught fresh from Maryland waters and steamed to perfection in Old Bay seasoning…"
          rows={4}
          style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--gray-300)', borderRadius: 4, fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', display: 'block', marginBottom: 4 }}>
            Teaser <span style={{ color: 'var(--gray-400)', fontWeight: 400 }}>(collapsed-state preview; blank = first ~80 chars of body)</span>
          </label>
          <input
            type="text"
            value={teaser}
            onChange={(e) => patchOverlay({ teaser: e.target.value })}
            placeholder="Order by Wed for weekend delivery →"
            style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--gray-300)', borderRadius: 4, fontSize: 13, boxSizing: 'border-box' }}
          />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', display: 'block', marginBottom: 4 }}>
            Font size (px)
          </label>
          <input
            type="number"
            min={OVERLAY_FONT_SIZE_MIN}
            max={OVERLAY_FONT_SIZE_MAX}
            value={fontSize}
            onChange={(e) => patchOverlay({ fontSize: (e.target.value === '' ? null : Number(e.target.value)) })}
            style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--gray-300)', borderRadius: 4, fontSize: 13, boxSizing: 'border-box' }}
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <ColorPresetPicker
          label="Text color"
          defaultKey="white"
          value={textColor}
          onChange={(v) => patchOverlay({ textColor: v })}
        />
        <ColorPresetPicker
          label="Background color"
          defaultKey="navy"
          value={bgColor}
          onChange={(v) => patchOverlay({ bgColor: v })}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'center', marginBottom: 14 }}>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', display: 'block', marginBottom: 4 }}>
            Background opacity <span style={{ color: 'var(--gray-400)', fontWeight: 400 }}>({bgOpacity}%)</span>
          </label>
          <input
            type="range"
            min={0}
            max={100}
            value={bgOpacity}
            onChange={(e) => patchOverlay({ bgOpacity: Number(e.target.value) })}
            style={{ width: '100%' }}
          />
        </div>
      </div>

      <div style={{ marginTop: 8 }}>
        <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
          Live preview
        </div>
        <div style={{ position: 'relative', width: '100%', height: 140, borderRadius: 6, overflow: 'hidden', background: 'linear-gradient(135deg, #b58866 0%, #8b6347 50%, #5c3f2d 100%)' }}>
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0,
            padding: '10px 14px',
            background: previewBg,
            color: resolvedTextColor,
            fontSize: Math.max(11, Math.min(18, Number(fontSize) || 14)),
            lineHeight: 1.35,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          }}>
            <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{teaserPreview}</span>
            <span style={{ fontSize: 10, opacity: 0.85, textTransform: 'uppercase', letterSpacing: 0.5 }}>Read more ▾</span>
          </div>
        </div>
      </div>
    </section>
  );
}

// True Blue Maryland Crab certified — overlay badge on the PDP hero photo
// + floating sticky badge in the right column. Hardcoded historically to
// product 132 (Blue Crabs Steamed); the parent gates this section on
// selectedId === TRUEBLUE_PRODUCT_ID so no other product ever sees it.
// Two BC custom fields drive the visible behavior:
//   pdp-trueblue-enabled  ('false' hides both badges; absent = on)
//   pdp-trueblue-position (corner key; absent = bottom-left default)
function TruebluePlacementSection({ enabled, position, dirty, onToggleEnabled, onChangePosition }) {
  const POS_LABEL = {
    'top-left': 'Top left',
    'top-right': 'Top right',
    'bottom-left': 'Bottom left',
    'bottom-right': 'Bottom right',
  };

  // Tiny inline preview — image area with a colored chip placed at the
  // chosen corner so the admin sees where the badge lands.
  const cornerStyle = (key) => {
    const isLeft = key.endsWith('-left');
    const isTop = key.startsWith('top-');
    return {
      position: 'absolute',
      [isTop ? 'top' : 'bottom']: 8,
      [isLeft ? 'left' : 'right']: 8,
      width: 36,
      height: 22,
      borderRadius: 3,
      background: '#003b5c',
      color: '#fff',
      fontSize: 10,
      fontWeight: 700,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      letterSpacing: 0.4,
      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
    };
  };

  return (
    <section style={{ background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8, padding: 16, marginBottom: 14 }}>
      <SectionHeader
        title="True Blue badge"
        subtitle="Maryland Blue Crab certified overlay on the main PDP image (plus the floating sticky badge in the right column). Available on this product only."
        rightSlot={dirty ? <span style={{ fontSize: 11, color: 'var(--orange)', fontWeight: 700 }}>Unsaved</span> : null}
      />

      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 260px', minWidth: 240 }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 14 }}>
            <input
              type="checkbox"
              checked={!!enabled}
              onChange={(e) => onToggleEnabled(e.target.checked)}
              style={{ accentColor: 'var(--orange)' }}
            />
            Show True Blue badge on this PDP
          </label>

          <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
            Placement
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            {TRUEBLUE_POSITIONS.map((key) => {
              const selected = position === key;
              return (
                <button
                  key={key}
                  type="button"
                  disabled={!enabled}
                  onClick={() => onChangePosition(key)}
                  style={{
                    padding: '7px 10px',
                    border: selected ? '2px solid var(--orange)' : '1px solid var(--gray-300)',
                    background: selected ? 'rgba(220, 88, 42, 0.08)' : '#fff',
                    borderRadius: 4,
                    cursor: enabled ? 'pointer' : 'not-allowed',
                    opacity: enabled ? 1 : 0.5,
                    fontSize: 13,
                    color: selected ? 'var(--orange)' : 'var(--navy)',
                    fontWeight: selected ? 700 : 500,
                    textAlign: 'left',
                  }}
                >
                  {POS_LABEL[key]}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ flex: '0 0 200px' }}>
          <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
            Preview
          </div>
          <div style={{
            position: 'relative', width: 200, height: 140, borderRadius: 6, overflow: 'hidden',
            background: 'linear-gradient(135deg, #b58866 0%, #8b6347 50%, #5c3f2d 100%)',
            opacity: enabled ? 1 : 0.45,
          }}>
            {enabled && <span style={cornerStyle(position || TRUEBLUE_POSITION_DEFAULT)}>TRUE</span>}
          </div>
          {!enabled && (
            <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 6, fontStyle: 'italic' }}>
              Badge hidden — flip the toggle to choose a corner.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// Per-product "Send me this Recipe" button. Pick the plated photos that get
// the button, tie EACH photo to its own recipe, set the button colors, and
// toggle the thumbnail badge. Writes the pdp_recipe metafield namespace; the
// storefront overlays the button via pdp-banner.js.
function RecipeCtaSection({ recipe, patchRecipe }) {
  const { enabled, photos = [], buttonText = '', bgColor = '', textColor = '', showOnThumbnails = true, icon = RECIPE_ICON_DEFAULT, iconColor = '', thumbIconOnly = false, dirty } = recipe || {};
  const selectedCount = (photos || []).length;

  const previewBg = bgColor || RECIPE_BG_DEFAULT;
  const previewText = textColor || RECIPE_TEXT_DEFAULT;
  const SelIcon = (RECIPE_ICON_BY_KEY[icon] || RECIPE_ICONS[0]).Icon;
  const previewIcon = iconColor || previewText;

  return (
    <section style={{ background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8, padding: 16, marginBottom: 14 }}>
      <SectionHeader
        title="Recipe Button"
        subtitle="To add a “Send me this Recipe” button to a photo, check “Recipe button” on that photo in the Media section above (and pick a recipe, or leave Auto). Shoppers enter their email, get the recipe instantly, and join your Dotdigital list. The options here just style the button; email design lives in Site Content → Recipe Email."
        rightSlot={dirty ? <span style={{ fontSize: 11, color: 'var(--orange)', fontWeight: 700 }}>Unsaved</span> : null}
      />

      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 4 }}>
        <input
          type="checkbox"
          checked={!!enabled}
          onChange={(e) => patchRecipe({ enabled: e.target.checked })}
          style={{ accentColor: 'var(--orange)' }}
        />
        Show the “Send me this Recipe” button on this product
      </label>
      <div style={{ fontSize: 11.5, color: 'var(--gray-500)', marginBottom: enabled ? 14 : 0 }}>
        Turns on automatically when you flag a photo above. Uncheck to hide the button on this product without clearing your photo selections.
      </div>

      {enabled && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', display: 'block', marginBottom: 4 }}>
                Button label <span style={{ color: 'var(--gray-400)', fontWeight: 400 }}>(optional)</span>
              </label>
              <input
                type="text"
                value={buttonText || ''}
                onChange={(e) => patchRecipe({ buttonText: e.target.value })}
                placeholder={RECIPE_BUTTON_DEFAULT}
                style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--gray-300)', borderRadius: 4, fontSize: 13, boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', display: 'block', marginBottom: 4 }}>
                Live preview
              </label>
              <div style={{ padding: 10, background: '#f1ece3', borderRadius: 6, display: 'flex', justifyContent: 'center' }}>
                <span style={{
                  background: previewBg, color: previewText, padding: '9px 17px', borderRadius: 40,
                  fontSize: 13, fontWeight: 700, display: 'inline-flex', gap: 7, alignItems: 'center',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                }}>
                  <SelIcon size={16} color={previewIcon} aria-hidden="true" />{buttonText || RECIPE_BUTTON_DEFAULT}
                </span>
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <RecipeColorField label="Button background color" value={bgColor} defaultHex={RECIPE_BG_DEFAULT} onChange={(v) => patchRecipe({ bgColor: v })} />
            <RecipeColorField label="Button text color" value={textColor} defaultHex={RECIPE_TEXT_DEFAULT} onChange={(v) => patchRecipe({ textColor: v })} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', display: 'block', marginBottom: 4 }}>Button icon</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {RECIPE_ICONS.map(({ key, label, Icon }) => {
                  const sel = icon === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => patchRecipe({ icon: key })}
                      title={label}
                      aria-label={label}
                      style={{
                        width: 34, height: 34, borderRadius: 6, cursor: 'pointer', padding: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: sel ? 'var(--orange)' : '#fff',
                        border: sel ? '2px solid var(--orange)' : '1px solid var(--gray-300)',
                      }}
                    >
                      <Icon size={18} color={sel ? '#fff' : 'var(--navy)'} />
                    </button>
                  );
                })}
              </div>
            </div>
            <RecipeColorField label="Icon color" value={iconColor} defaultHex={RECIPE_TEXT_DEFAULT} onChange={(v) => patchRecipe({ iconColor: v })} />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={showOnThumbnails !== false}
                onChange={(e) => patchRecipe({ showOnThumbnails: e.target.checked })}
                style={{ accentColor: 'var(--orange)' }}
              />
              Show a recipe badge on the thumbnails of recipe photos
            </label>
            {showOnThumbnails !== false && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--gray-600)', marginTop: 6, marginLeft: 22 }}>
                <input
                  type="checkbox"
                  checked={thumbIconOnly === true}
                  onChange={(e) => patchRecipe({ thumbIconOnly: e.target.checked })}
                  style={{ accentColor: 'var(--orange)' }}
                />
                Show only the icon on thumbnails (hide the “Recipe” text)
              </label>
            )}
          </div>

          <div style={{
            padding: '10px 12px', borderRadius: 6, fontSize: 12, display: 'flex', gap: 8, alignItems: 'flex-start',
            background: selectedCount > 0 ? '#eff6ff' : '#fffbeb',
            border: `1px solid ${selectedCount > 0 ? '#bfdbfe' : '#fde68a'}`,
            color: selectedCount > 0 ? '#1e3a8a' : '#92400e',
          }}>
            {selectedCount > 0
              ? <Info size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              : <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />}
            <div>
              {selectedCount > 0
                ? <><strong>{selectedCount} photo{selectedCount === 1 ? '' : 's'}</strong> show the button.</>
                : <>No photos show the button yet.</>}
              {' '}Pick which photos get the button — and the recipe each one sends — on the photos in the <strong>Media</strong> section below.
            </div>
          </div>
        </>
      )}
    </section>
  );
}

function ImagesSection({ images, imageEdits, patchImageEdit, getImageField, moveImage, toggleDeleteImage, setThumbnail, uploading, openFilePicker, uploadGalleryFile, setPreview, replacingId, openReplacePicker }) {
  // Hide banner-tagged images from the gallery editor. They're managed in
  // the Banner section above; surfacing them here confused admins into
  // thinking banner uploads landed in the product carousel.
  const visibleImages = images
    .filter(img => (img.description || '') !== PDP_BANNER_MARKER)
    .filter(img => !isVariantLibraryImage(img))
    .map(img => ({ img, sort: getImageField(img, 'sort_order') ?? 0 }))
    .sort((a, b) => a.sort - b.sort || a.img.id - b.img.id)
    .map(x => x.img);

  return (
    <div style={{ marginBottom: 24 }}>
      <SectionHeader
        title="Images"
        subtitle="Reorder with arrows; set alt text for accessibility; click the star to set the primary thumbnail."
        rightSlot={
          <button
            onClick={openFilePicker}
            disabled={uploading}
            style={{ padding: '6px 12px', background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 4, cursor: uploading ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}
          >
            <Upload size={12} /> {uploading ? 'Uploading...' : 'Upload image'}
          </button>
        }
      />

      <div style={{ padding: '8px 10px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, color: '#1e3a8a', fontSize: 11.5, display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 10 }}>
        <Info size={14} style={{ flexShrink: 0, marginTop: 1 }} />
        <div>
          BigCommerce stores a square thumbnail + standard + zoom per image. Uploading commits immediately; alt-text / order / delete / thumbnail changes require Save at the top of the editor. Max 5 MB. <strong>Tip:</strong> drag &amp; drop an image anywhere in this section to upload.
        </div>
      </div>

      <DropZone
        disabled={uploading}
        onFile={(file) => uploadGalleryFile && uploadGalleryFile(file)}
        label={uploading ? 'Uploading...' : 'Drop image to add to this product'}
        overlayStyle={{ minHeight: 120 }}
      >
      {visibleImages.length === 0 ? (
        <div style={{ color: 'var(--gray-500)', fontSize: 13, padding: '28px 16px', textAlign: 'center', border: '1px dashed var(--gray-300)', borderRadius: 6, background: '#fafafa' }}>
          No images yet — click Upload image above or drag one here.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead style={{ background: 'var(--gray-100)', textAlign: 'left' }}>
            <tr>
              <th style={{ ...thSm, width: 80 }}>Order</th>
              <th style={{ ...thSm, width: 90 }}>Thumb</th>
              <th style={thSm}>Alt text</th>
              <th style={{ ...thSm, width: 90 }}>Primary</th>
              <th style={{ ...thSm, width: 180 }}></th>
            </tr>
          </thead>
          <tbody>
            {visibleImages.map((img, idx) => {
              const edit = imageEdits[img.id];
              const deleted = edit?.deleted;
              const altVal = 'description' in (edit || {}) ? (edit.description ?? '') : (img.description || '');
              const isThumb = 'is_thumbnail' in (edit || {}) ? !!edit.is_thumbnail : !!img.is_thumbnail;
              const dirty = !!edit;
              return (
                <tr key={img.id} style={{ borderTop: '1px solid var(--gray-200)', background: deleted ? '#fef2f2' : dirty ? '#fff8e1' : '#fff', opacity: deleted ? 0.6 : 1 }}>
                  <td style={tdSm}>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      <button
                        onClick={() => moveImage(img.id, 'up')}
                        disabled={idx === 0 || deleted}
                        title="Move up"
                        style={arrowBtnStyle}
                      >
                        <ArrowUp size={12} />
                      </button>
                      <button
                        onClick={() => moveImage(img.id, 'down')}
                        disabled={idx === visibleImages.length - 1 || deleted}
                        title="Move down"
                        style={arrowBtnStyle}
                      >
                        <ArrowDown size={12} />
                      </button>
                    </div>
                  </td>
                  <td style={tdSm}>
                    <button
                      type="button"
                      onClick={() => setPreview({ url: img.url_zoom || img.url_standard || img.url_thumbnail, name: altVal || `Image ${img.id}` })}
                      style={{ width: 70, height: 70, padding: 0, border: '1px solid var(--gray-200)', borderRadius: 4, background: '#f7f8fa', cursor: 'zoom-in', overflow: 'hidden' }}
                    >
                      <img src={img.url_thumbnail || img.url_standard} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    </button>
                  </td>
                  <td style={tdSm}>
                    <input
                      type="text"
                      value={altVal}
                      disabled={deleted}
                      onChange={(e) => patchImageEdit(img.id, { description: e.target.value })}
                      placeholder="Describe the image for screen readers"
                      style={input}
                    />
                  </td>
                  <td style={{ ...tdSm, textAlign: 'center' }}>
                    <button
                      onClick={() => setThumbnail(img.id)}
                      disabled={deleted}
                      title={isThumb ? 'Primary thumbnail' : 'Make primary'}
                      style={{
                        background: 'none', border: 'none', cursor: deleted ? 'not-allowed' : 'pointer',
                        color: isThumb ? 'var(--orange)' : 'var(--gray-300)',
                      }}
                    >
                      <Star size={18} fill={isThumb ? 'var(--orange)' : 'none'} />
                    </button>
                  </td>
                  <td style={tdSm}>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => openReplacePicker && openReplacePicker(img.id)}
                        disabled={deleted || replacingId === img.id}
                        title="Replace this image with a new file"
                        style={{ background: 'none', border: '1px solid var(--gray-300)', borderRadius: 4, padding: '4px 8px', cursor: deleted || replacingId === img.id ? 'not-allowed' : 'pointer', color: 'var(--navy)', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      >
                        {replacingId === img.id ? (
                          <RefreshCw size={12} className="spin" />
                        ) : (
                          <><Upload size={12} /> Replace</>
                        )}
                      </button>
                      <button
                        onClick={() => toggleDeleteImage(img.id)}
                        title={deleted ? 'Restore' : 'Delete on save'}
                        style={{ background: 'none', border: '1px solid var(--gray-300)', borderRadius: 4, padding: '4px 8px', cursor: 'pointer', color: deleted ? 'var(--gray-500)' : '#c53030', fontSize: 11 }}
                      >
                        {deleted ? 'Restore' : <Trash2 size={12} />}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      </DropZone>
    </div>
  );
}

function VariantLibrarySection({
  images,
  imageEdits,
  patchImageEdit,
  getImageField,
  toggleDeleteImage,
  setPreview,
  uploadingLibrary,
  openLibraryFilePicker,
  uploadLibraryFile,
  onAddFromMediaLibrary,
  replacingId,
  openReplacePicker,
}) {
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  const all = (images || []).filter(img => (img.description || '') !== PDP_BANNER_MARKER);
  const galleryImages = all.filter(img => !isVariantLibraryImage(img));
  const libraryImages = all.filter(isVariantLibraryImage);

  const renderThumb = (img, size = 56, previewable = true) => (
    <button
      type="button"
      onClick={() => previewable && setPreview && setPreview({ url: img.url_zoom || img.url_standard || img.url_thumbnail, name: img.description || `Image ${img.id}` })}
      style={{ width: size, height: size, padding: 0, border: '1px solid var(--gray-200)', borderRadius: 4, background: '#f7f8fa', cursor: previewable ? 'zoom-in' : 'default', overflow: 'hidden' }}
      title="Preview"
    >
      <img src={img.url_thumbnail || img.url_standard} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
    </button>
  );

  return (
    <div style={{ marginBottom: 24 }}>
      <SectionHeader
        title="Variant library"
        subtitle="Pool of images available for variant-photo assignments below. Main gallery images are auto-included. Upload variant-only images here — they won't appear in the storefront's main carousel."
        rightSlot={
          <div style={{ display: 'inline-flex', gap: 8 }}>
            <button
              type="button"
              onClick={() => setMediaPickerOpen(true)}
              disabled={uploadingLibrary}
              style={{ padding: '6px 12px', background: '#fff', color: 'var(--navy)', border: '1px solid var(--navy)', borderRadius: 4, cursor: uploadingLibrary ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}
            >
              <ImageIcon size={12} /> Pick from Media Library
            </button>
            <button
              type="button"
              onClick={openLibraryFilePicker}
              disabled={uploadingLibrary}
              style={{ padding: '6px 12px', background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 4, cursor: uploadingLibrary ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}
            >
              <Upload size={12} /> {uploadingLibrary ? 'Uploading...' : 'Upload to library'}
            </button>
          </div>
        }
      />
      <DropZone
        disabled={uploadingLibrary}
        onFile={(file) => uploadLibraryFile && uploadLibraryFile(file)}
        label={uploadingLibrary ? 'Uploading...' : 'Drop image to add variant-only to library'}
        overlayStyle={{ minHeight: 80 }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {/* From main gallery — read-only */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.3 }}>
              From main gallery ({galleryImages.length})
            </div>
            {galleryImages.length === 0 ? (
              <div style={{ color: 'var(--gray-500)', fontSize: 12 }}>No gallery images yet.</div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {galleryImages.map((img, idx) => {
                  const deleted = imageEdits?.[img.id]?.deleted;
                  return (
                    <div key={img.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 80, opacity: deleted ? 0.5 : 1 }}>
                      <div style={{ position: 'relative' }}>
                        {renderThumb(img, 56)}
                        <button
                          type="button"
                          onClick={() => toggleDeleteImage && toggleDeleteImage(img.id)}
                          title={deleted ? 'Restore on save' : 'Delete on save'}
                          style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', border: '1px solid var(--gray-300)', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, color: deleted ? 'var(--gray-500)' : '#c53030' }}
                        >
                          {deleted ? <RefreshCw size={10} /> : <Trash2 size={10} />}
                        </button>
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--gray-600)', marginTop: 2 }}>#{idx + 1}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Variant-only uploads — editable */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.3 }}>
              Variant-only uploads ({libraryImages.length})
            </div>
            {libraryImages.length === 0 ? (
              <div style={{ color: 'var(--gray-500)', fontSize: 12 }}>No variant-only images yet — click Upload to library above.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <tbody>
                  {libraryImages.map((img, idx) => {
                    const edit = imageEdits?.[img.id];
                    const deleted = edit?.deleted;
                    const rawDesc = 'description' in (edit || {}) ? (edit.description ?? '') : (img.description || '');
                    const label = getVariantLibraryLabel({ description: rawDesc });
                    const dirty = !!edit;
                    return (
                      <tr key={img.id} style={{ borderTop: '1px solid var(--gray-200)', background: deleted ? '#fef2f2' : dirty ? '#fff8e1' : '#fff', opacity: deleted ? 0.6 : 1 }}>
                        <td style={{ padding: 6, width: 60 }}>
                          {renderThumb(img, 50)}
                        </td>
                        <td style={{ padding: 6, fontSize: 11, color: 'var(--gray-600)', whiteSpace: 'nowrap', width: 48 }}>VL-{idx + 1}</td>
                        <td style={{ padding: 6 }}>
                          <input
                            type="text"
                            value={label}
                            disabled={deleted}
                            onChange={(e) => patchImageEdit(img.id, { description: withVariantLibraryMarker(e.target.value) })}
                            placeholder="Label (shown in the Variant photos dropdown)"
                            style={input}
                          />
                        </td>
                        <td style={{ padding: 6, whiteSpace: 'nowrap' }}>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button
                              type="button"
                              onClick={() => openReplacePicker && openReplacePicker(img.id)}
                              disabled={deleted || replacingId === img.id}
                              title="Replace this image file"
                              style={{ background: 'none', border: '1px solid var(--gray-300)', borderRadius: 4, padding: '3px 6px', cursor: deleted || replacingId === img.id ? 'not-allowed' : 'pointer', color: 'var(--navy)', fontSize: 10, display: 'inline-flex', alignItems: 'center', gap: 3 }}
                            >
                              {replacingId === img.id ? <RefreshCw size={10} className="spin" /> : <><Upload size={10} /> Replace</>}
                            </button>
                            <button
                              type="button"
                              onClick={() => toggleDeleteImage && toggleDeleteImage(img.id)}
                              title={deleted ? 'Restore' : 'Delete on save'}
                              style={{ background: 'none', border: '1px solid var(--gray-300)', borderRadius: 4, padding: '3px 6px', cursor: 'pointer', color: deleted ? 'var(--gray-500)' : '#c53030', fontSize: 10 }}
                            >
                              {deleted ? 'Restore' : <Trash2 size={10} />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </DropZone>

      {mediaPickerOpen && (
        <MediaPicker
          multiSelect
          onClose={() => setMediaPickerOpen(false)}
          onPick={(assets) => {
            setMediaPickerOpen(false);
            if (onAddFromMediaLibrary) onAddFromMediaLibrary(assets);
          }}
        />
      )}
    </div>
  );
}

function VariantImagesSection({ options, variants, images, variantImageEdits, patchVariantImageEdit, currentVariantImageByValueId, dirty, setPreview }) {
  // Only consider visual-selectable option types.
  const VISUAL_TYPES = new Set(['swatch', 'radio_buttons', 'rectangles', 'dropdown']);

  // Hide banner-tagged images from the picker. Variant-library images ARE pickable
  // (that's their whole purpose) but we label them differently in the dropdown.
  const allImages = (images || []).filter(img => (img.description || '') !== PDP_BANNER_MARKER);
  const galleryImages = allImages.filter(img => !isVariantLibraryImage(img));
  const libraryImages = allImages.filter(isVariantLibraryImage);
  const pickableImages = [...galleryImages, ...libraryImages];
  const imgById = new Map(pickableImages.map(img => [img.id, img]));

  // Only surface an option if its values carry swatch images (value_data.image_url,
  // e.g. Type on scallops). Hides text-only options like "Serving Size" where no
  // image makes sense — those swatches never render a photo on the PDP anyway.
  const optionHasSwatchImages = (o) => (o.option_values || []).some(ov => !!(ov.value_data?.image_url));
  const relevantOptions = (options || [])
    .filter(o => VISUAL_TYPES.has(o.type) && (o.option_values || []).length > 0)
    .filter(optionHasSwatchImages);

  // Auto-match: for each option_value on a relevant option, find the product image
  // whose description equals the option_value's label (case-insensitive). Returns
  // { [ov.id]: imageId } for rows that have a clear match and aren't already bound.
  const autoMatchSuggestions = (() => {
    const out = {};
    const byDesc = new Map();
    for (const img of pickableImages) {
      const key = (img.description || '').trim().toLowerCase();
      if (key && !byDesc.has(key)) byDesc.set(key, img.id);
    }
    for (const opt of relevantOptions) {
      for (const ov of (opt.option_values || [])) {
        const current = currentVariantImageByValueId?.[ov.id];
        if (typeof current === 'number') continue; // already bound
        const match = byDesc.get((ov.label || '').trim().toLowerCase());
        if (match) out[ov.id] = match;
      }
    }
    return out;
  })();
  const autoMatchAvailable = Object.keys(autoMatchSuggestions).some(ovId => !(ovId in variantImageEdits));

  const applyAutoMatch = () => {
    for (const [ovIdStr, imageId] of Object.entries(autoMatchSuggestions)) {
      const ovId = Number(ovIdStr);
      if (ovId in variantImageEdits) continue;
      patchVariantImageEdit(ovId, imageId);
    }
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <SectionHeader
        title="Variant photos"
        subtitle="Pick a carousel image for each option value. When a shopper picks that value on the PDP, the main image swaps to the chosen photo. Applies to every variant sharing the value (e.g. 'Colossal' covers all sizes)."
        rightSlot={
          autoMatchAvailable ? (
            <button
              type="button"
              onClick={applyAutoMatch}
              title="Pre-fill rows by matching option-value labels to image descriptions"
              style={{ padding: '6px 12px', background: '#fff', color: 'var(--navy)', border: '1px solid var(--navy)', borderRadius: 4, cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              Auto-match from labels
            </button>
          ) : null
        }
      />
      {dirty && (
        <div style={{ fontSize: 11, color: 'var(--orange)', marginBottom: 8, fontWeight: 600 }}>
          Unsaved variant-image changes — click Save at the top to apply.
        </div>
      )}
      {!variants || variants.length === 0 ? (
        <div style={{ color: 'var(--gray-500)', fontSize: 12, padding: '12px 0' }}>
          This product has no variants — variant-image assignments don't apply.
        </div>
      ) : relevantOptions.length === 0 ? (
        <div style={{ color: 'var(--gray-500)', fontSize: 12, padding: '12px 0' }}>
          No selectable options (swatch / radio / dropdown) on this product.
        </div>
      ) : pickableImages.length === 0 ? (
        <div style={{ color: 'var(--gray-500)', fontSize: 12, padding: '12px 0' }}>
          Upload at least one image in the section above before assigning variant photos.
        </div>
      ) : (
        relevantOptions.map(opt => (
          <div key={opt.id} style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-600)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.3 }}>
              {opt.display_name}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead style={{ background: 'var(--gray-100)', textAlign: 'left' }}>
                <tr>
                  <th style={{ ...thSm, width: 180 }}>Option value</th>
                  <th style={{ ...thSm, width: 90 }}>Current</th>
                  <th style={thSm}>Assigned image</th>
                </tr>
              </thead>
              <tbody>
                {(opt.option_values || []).map(ov => {
                  const current = currentVariantImageByValueId?.[ov.id];
                  const hasEdit = ov.id in variantImageEdits;
                  const selected = hasEdit ? variantImageEdits[ov.id] : (typeof current === 'number' ? current : null);
                  const selectedImg = selected != null ? imgById.get(selected) : null;
                  const mixed = current === '__mixed__' && !hasEdit;
                  const external = current === '__external__' && !hasEdit;
                  const rowDirty = hasEdit;
                  const dropdownVal = hasEdit
                    ? (selected == null ? '__none__' : String(selected))
                    : (mixed ? '__mixed__' : external ? '__external__' : (selected == null ? '__none__' : String(selected)));
                  return (
                    <tr key={ov.id} style={{ borderTop: '1px solid var(--gray-200)', background: rowDirty ? '#fff8e1' : '#fff' }}>
                      <td style={tdSm}>{ov.label}</td>
                      <td style={tdSm}>
                        {selectedImg ? (
                          <button
                            type="button"
                            onClick={() => setPreview && setPreview({ url: selectedImg.url_zoom || selectedImg.url_standard || selectedImg.url_thumbnail, name: selectedImg.description || `Image ${selectedImg.id}` })}
                            style={{ width: 50, height: 50, padding: 0, border: '1px solid var(--gray-200)', borderRadius: 4, background: '#f7f8fa', cursor: 'zoom-in', overflow: 'hidden' }}
                            title="Preview"
                          >
                            <img src={selectedImg.url_thumbnail || selectedImg.url_standard} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                          </button>
                        ) : mixed ? (
                          <span style={{ fontSize: 11, color: 'var(--gray-500)', fontStyle: 'italic' }}>Mixed</span>
                        ) : external ? (
                          <span style={{ fontSize: 11, color: 'var(--gray-500)', fontStyle: 'italic' }} title="Assigned image is not in this product's gallery">External</span>
                        ) : (
                          <span style={{ fontSize: 11, color: 'var(--gray-400)' }}>—</span>
                        )}
                      </td>
                      <td style={tdSm}>
                        <select
                          value={dropdownVal}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v === '__mixed__' || v === '__external__') return; // read-only markers
                            patchVariantImageEdit(ov.id, v === '__none__' ? null : Number(v));
                          }}
                          style={{ ...input, maxWidth: 360 }}
                        >
                          {mixed && <option value="__mixed__">— Mixed across variants —</option>}
                          {external && <option value="__external__">— External URL (not in gallery) —</option>}
                          <option value="__none__">None (no variant image)</option>
                          {galleryImages.length > 0 && <optgroup label="From main gallery">
                            {galleryImages.map((img, idx) => (
                              <option key={img.id} value={String(img.id)}>
                                #{idx + 1}: {(img.description || `Image ${img.id}`).slice(0, 60)}
                              </option>
                            ))}
                          </optgroup>}
                          {libraryImages.length > 0 && <optgroup label="From variant library">
                            {libraryImages.map((img, idx) => {
                              const lbl = getVariantLibraryLabel(img) || `Library image ${img.id}`;
                              return (
                                <option key={img.id} value={String(img.id)}>
                                  VL-{idx + 1}: {lbl.slice(0, 60)}
                                </option>
                              );
                            })}
                          </optgroup>}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))
      )}
    </div>
  );
}

// Compact bg + text preset picker for a single option-value row in the
// Option Badges table. Each row has its own override pair; both fall back
// to the SCSS auto-detect (Most Popular → navy, Best Value → green, else
// dark gray) when blank. Preview pill on top renders the badge text with
// the resolved colors so admins see exactly what the storefront will paint.
function BadgeColorCell({ bgValue, textValue, badgeText, onChangeBg, onChangeText }) {
  // Auto-detect default — what the SCSS class produces when no override.
  let autoBg = '#1f2937';
  if (/best\s*value/i.test(badgeText)) autoBg = '#059669';
  else if (/most\s*popular/i.test(badgeText)) autoBg = MARQUEE_PALETTE.navy.color;
  const autoText = '#ffffff';

  const resolvedBg = bgValue && MARQUEE_PALETTE[bgValue] ? MARQUEE_PALETTE[bgValue].color : autoBg;
  const resolvedText = textValue && MARQUEE_PALETTE[textValue] ? MARQUEE_PALETTE[textValue].color : autoText;

  const Chip = ({ value, currentValue, onClick }) => {
    const preset = value && MARQUEE_PALETTE[value];
    const selected = currentValue === value;
    return (
      <button
        type="button"
        onClick={onClick}
        title={value ? preset?.label : 'Default (auto-detect)'}
        aria-label={value ? preset?.label : 'Default'}
        style={{
          width: 16, height: 16, borderRadius: 3, padding: 0, flexShrink: 0,
          border: selected ? '2px solid var(--orange)' : '1px solid var(--gray-300)',
          background: value ? (preset?.swatch || preset?.color) : 'linear-gradient(45deg, #fff 45%, #c44 47%, #c44 53%, #fff 55%)',
          cursor: 'pointer',
        }}
      />
    );
  };

  const Row = ({ label, value, onChange }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
      <span style={{ fontSize: 9, color: 'var(--gray-500)', width: 18, textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: 600 }}>{label}</span>
      <Chip value="" currentValue={value} onClick={() => onChange('')} />
      {MARQUEE_PALETTE_ORDER.map((k) => (
        <Chip key={k} value={k} currentValue={value} onClick={() => onChange(k)} />
      ))}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Live preview pill — renders the actual badge text in the resolved colors */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
        <span style={{
          display: 'inline-block', padding: '2px 8px', borderRadius: 8,
          fontSize: 9, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase',
          background: resolvedBg, color: resolvedText,
          minWidth: 50, textAlign: 'center',
          boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
        }}>
          {badgeText || ' '}
        </span>
      </div>
      <Row label="Bg" value={bgValue} onChange={onChangeBg} />
      <Row label="Tx" value={textValue} onChange={onChangeText} />
    </div>
  );
}

function OptionBadgesSection({ options, badges, patchOptionBadge, dirty }) {
  const hasOptions = options && options.length > 0;
  return (
    <div style={{ marginBottom: 24 }}>
      <SectionHeader
        title="Option badges & per-option subtext"
        subtitle='Per option-value overlays: "ON SALE" ribbon, "Most Popular" / "Best Value" top pill, "X% OFF" chip, "Great for…" subtext, and a "# of units" field that drives the auto-computed "$X.XX/ea" display.'
        rightSlot={dirty ? <span style={{ fontSize: 11, color: 'var(--orange)', fontWeight: 600 }}>unsaved</span> : null}
      />

      {!hasOptions ? (
        <div style={{ color: 'var(--gray-500)', fontSize: 13, padding: '14px 0' }}>
          This product has no options defined in BigCommerce. Badges apply to option values (swatches, radios, dropdowns), so nothing to configure here.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {options.map(opt => (
            <div key={opt.id} style={{ border: '1px solid var(--gray-200)', borderRadius: 6, overflow: 'hidden' }}>
              <div style={{ padding: '8px 12px', background: 'var(--gray-100)', fontSize: 12, color: 'var(--gray-600)', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <div>
                  <strong style={{ color: 'var(--navy)' }}>{opt.display_name}</strong>
                  <span style={{ marginLeft: 8, fontFamily: 'monospace', fontSize: 11 }}>{opt.type}</span>
                </div>
                <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>{(opt.option_values || []).length} value{(opt.option_values || []).length !== 1 ? 's' : ''}</span>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#fafafa' }}>
                    <th style={{ ...thSm, width: 170 }}>Option value</th>
                    <th style={{ ...thSm, width: 160 }}>Badge (top)</th>
                    <th style={{ ...thSm, width: 175 }} title="Override the auto-detected badge color (Most Popular → navy, Best Value → green). Blank = use default.">Badge color</th>
                    <th style={{ ...thSm, width: 70 }}>% OFF</th>
                    <th style={{ ...thSm, width: 70 }} title="Drives per-unit price display: variant price ÷ this count. Leave blank to hide.">Units</th>
                    <th style={thSm}>Great for…</th>
                  </tr>
                </thead>
                <tbody>
                  {(opt.option_values || []).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)).map(ov => {
                    const entry = badges[String(ov.id)] || {};
                    return (
                      <tr key={ov.id} style={{ borderTop: '1px solid var(--gray-200)' }}>
                        <td style={tdSm}>
                          <div style={{ fontWeight: 600, color: 'var(--navy)' }}>{ov.label}</div>
                          <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>id: {ov.id}</div>
                        </td>
                        <td style={tdSm}>
                          <input
                            type="text"
                            value={entry.badge || ''}
                            onChange={(e) => patchOptionBadge(ov.id, 'badge', e.target.value)}
                            placeholder="e.g. ON SALE, Most Popular, Best Value"
                            style={input}
                            list={`badge-suggestions-${ov.id}`}
                          />
                          <datalist id={`badge-suggestions-${ov.id}`}>
                            <option value="ON SALE" />
                            <option value="Most Popular" />
                            <option value="Best Value" />
                            <option value="NEW" />
                          </datalist>
                        </td>
                        <td style={tdSm}>
                          <BadgeColorCell
                            bgValue={entry.badgeBg || ''}
                            textValue={entry.badgeText || ''}
                            badgeText={entry.badge || ''}
                            onChangeBg={(v) => patchOptionBadge(ov.id, 'badgeBg', v)}
                            onChangeText={(v) => patchOptionBadge(ov.id, 'badgeText', v)}
                          />
                        </td>
                        <td style={tdSm}>
                          <input
                            type="number"
                            min="0" max="100" step="1"
                            value={entry.pctOff ?? ''}
                            onChange={(e) => patchOptionBadge(ov.id, 'pctOff', e.target.value)}
                            placeholder="10"
                            style={input}
                          />
                        </td>
                        <td style={tdSm}>
                          <input
                            type="number"
                            min="1" step="1"
                            value={entry.unitCount ?? ''}
                            onChange={(e) => patchOptionBadge(ov.id, 'unitCount', e.target.value)}
                            placeholder="e.g. 4"
                            title='Number of items in this option (e.g. 4 for a "4 Pack"). Storefront shows variant price ÷ this as "$X.XX/ea".'
                            style={input}
                          />
                        </td>
                        <td style={tdSm}>
                          <input
                            type="text"
                            value={entry.greatFor || ''}
                            onChange={(e) => patchOptionBadge(ov.id, 'greatFor', e.target.value)}
                            placeholder="e.g. Great for 2 people"
                            style={input}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PillCountsSection({ options, pillCounts, patchPillCounts, patchPillCount, dirty }) {
  const hasOptions = options && options.length > 0;
  const showOnOptionId = pillCounts?.showOnOptionId ?? null;
  const variesByOptionId = pillCounts?.variesByOptionId ?? null;
  const labelTemplate = pillCounts?.label || '';
  const matrix = pillCounts?.matrix || {};

  const showOnOption = options?.find(o => Number(o.id) === Number(showOnOptionId)) || null;
  const variesByOption = options?.find(o => Number(o.id) === Number(variesByOptionId)) || null;

  const rowOvs = (showOnOption?.option_values || []).slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  const colOvs = (variesByOption?.option_values || []).slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  const getCell = (rowOvId, colOvId) => {
    const row = matrix[String(rowOvId)] || {};
    const key = colOvId == null ? '_' : String(colOvId);
    return row[key] ?? '';
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <SectionHeader
        title="Per-pill counts"
        subtitle='Show a small "{count} crabs" line under specific option pills (e.g. 1/2 Bushel / Bushel). The count updates when the shopper switches the other option (e.g. crab size). Leave a cell blank to hide the line for that combo.'
        rightSlot={dirty ? <span style={{ fontSize: 11, color: 'var(--orange)', fontWeight: 600 }}>unsaved</span> : null}
      />

      {!hasOptions ? (
        <div style={{ color: 'var(--gray-500)', fontSize: 13, padding: '14px 0' }}>
          This product has no options defined in BigCommerce — nothing to configure.
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: 12, marginBottom: 12, alignItems: 'end' }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--gray-600)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, marginBottom: 4 }}>Show count on</label>
              <select
                value={showOnOptionId ?? ''}
                onChange={(e) => patchPillCounts({ showOnOptionId: e.target.value ? Number(e.target.value) : null })}
                style={input}
              >
                <option value="">— pick the option whose pills get a count line —</option>
                {options.map(o => <option key={o.id} value={o.id}>{o.display_name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--gray-600)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, marginBottom: 4 }}>Varies by</label>
              <select
                value={variesByOptionId ?? ''}
                onChange={(e) => patchPillCounts({ variesByOptionId: e.target.value ? Number(e.target.value) : null })}
                style={input}
                disabled={!showOnOptionId}
              >
                <option value="">— same count regardless —</option>
                {options.filter(o => Number(o.id) !== Number(showOnOptionId)).map(o => <option key={o.id} value={o.id}>{o.display_name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--gray-600)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, marginBottom: 4 }}>Label format</label>
              <input
                type="text"
                value={labelTemplate}
                onChange={(e) => patchPillCounts({ label: e.target.value })}
                placeholder={PILL_COUNTS_LABEL_DEFAULT}
                title='Use {count} as a placeholder for the number.'
                style={input}
              />
            </div>
          </div>

          {!showOnOption ? (
            <div style={{ color: 'var(--gray-500)', fontSize: 13, padding: '8px 0' }}>
              Pick the option whose pills should display a count line.
            </div>
          ) : (
            <div style={{ border: '1px solid var(--gray-200)', borderRadius: 6, overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#fafafa' }}>
                    <th style={{ ...thSm, width: 180 }}>{showOnOption.display_name}</th>
                    {variesByOption
                      ? colOvs.map(c => (
                          <th key={c.id} style={{ ...thSm, minWidth: 90 }} title={`id: ${c.id}`}>{c.label}</th>
                        ))
                      : <th style={thSm}>Count</th>}
                  </tr>
                </thead>
                <tbody>
                  {rowOvs.map(r => (
                    <tr key={r.id} style={{ borderTop: '1px solid var(--gray-200)' }}>
                      <td style={tdSm}>
                        <div style={{ fontWeight: 600, color: 'var(--navy)' }}>{r.label}</div>
                        <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>id: {r.id}</div>
                      </td>
                      {variesByOption ? colOvs.map(c => (
                        <td key={c.id} style={tdSm}>
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={getCell(r.id, c.id)}
                            onChange={(e) => patchPillCount(r.id, c.id, e.target.value)}
                            placeholder="—"
                            style={{ ...input, textAlign: 'center' }}
                          />
                        </td>
                      )) : (
                        <td style={tdSm}>
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={getCell(r.id, null)}
                            onChange={(e) => patchPillCount(r.id, null, e.target.value)}
                            placeholder="—"
                            style={{ ...input, textAlign: 'center' }}
                          />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function GridRowTagsSection({ options, tags, addTag, patchTag, removeTag, dirty }) {
  const { gradeOption, qtyOption } = detectGridTagAxes(options);
  const gradeValues = (gradeOption?.option_values || []).slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  const qtyValues = (qtyOption?.option_values || []).slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  const canConfigure = gradeOption && qtyOption;

  // Length preview against the metafield ceiling (matches the save guard). Tags
  // now live in a metafield (~65,535-char cap) so 4+ tags save fine — the counter
  // only warns if someone pastes something absurd.
  const cleaned = cleanGridRowTags(tags);
  const jsonLen = cleaned.length ? JSON.stringify(cleaned).length : 0;
  const overCap = jsonLen > GRID_ROW_TAGS_MAX_VALUE;

  // Warn (don't block) on duplicate crab+size combos — last one wins on the storefront.
  const dupKeys = new Set();
  const seen = new Set();
  for (const t of (tags || [])) {
    const c = String(t.crab || '').trim();
    const s = String(t.size || '').trim();
    if (!c || !s) continue;
    const k = `${c}||${s}`.toLowerCase();
    if (seen.has(k)) dupKeys.add(k); else seen.add(k);
  }

  const colorOf = (c) => GRID_ROW_TAG_COLORS.find(x => x.value === c) || GRID_ROW_TAG_COLORS[0];

  return (
    <div style={{ marginBottom: 24 }}>
      <SectionHeader
        title="Grid Row Tags"
        subtitle='Add a colored pill (like the PDP Most Popular / Best Value badge) under the Quantity value on a matching row in the "View all as a grid" overlay. Use it for "Hot Deal"-style call-outs.'
        rightSlot={dirty ? <span style={{ fontSize: 11, color: 'var(--orange)', fontWeight: 600 }}>unsaved</span> : null}
      />

      {!canConfigure ? (
        <div style={{ color: 'var(--gray-500)', fontSize: 13, padding: '14px 0' }}>
          This product needs a Crab Size / Crab Type option and a Quantity / Serving Size option for grid row tags — one of them is missing in BigCommerce.
        </div>
      ) : (
        <>
          {(tags || []).length === 0 ? (
            <div style={{ color: 'var(--gray-500)', fontSize: 13, padding: '4px 0 10px' }}>
              No tags yet. Add one to highlight a specific crab + quantity row.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
              {(tags || []).map((t, idx) => {
                const c = colorOf(t.color);
                const k = `${String(t.crab || '').trim()}||${String(t.size || '').trim()}`.toLowerCase();
                const isDup = t.crab && t.size && dupKeys.has(k);
                return (
                  <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1.4fr 1.2fr 1fr 0.9fr auto auto', gap: 8, alignItems: 'center' }}>
                    <select
                      value={t.crab || ''}
                      onChange={(e) => patchTag(idx, { crab: e.target.value })}
                      style={input}
                      title="Crab Size / Crab Type — matches as a substring of the grade label"
                    >
                      <option value="">— crab size —</option>
                      {gradeValues.map(v => <option key={v.id} value={v.label}>{v.label}</option>)}
                      {t.crab && !gradeValues.some(v => v.label === t.crab) && (
                        <option value={t.crab}>{t.crab} (saved value)</option>
                      )}
                    </select>
                    <select
                      value={t.size || ''}
                      onChange={(e) => patchTag(idx, { size: e.target.value })}
                      style={input}
                      title="Quantity / Serving Size — exact match"
                    >
                      <option value="">— quantity —</option>
                      {qtyValues.map(v => <option key={v.id} value={v.label}>{v.label}</option>)}
                      {t.size && !qtyValues.some(v => v.label === t.size) && (
                        <option value={t.size}>{t.size} (saved value)</option>
                      )}
                    </select>
                    <input
                      type="text"
                      value={t.label || ''}
                      onChange={(e) => patchTag(idx, { label: e.target.value })}
                      placeholder={GRID_ROW_TAG_LABEL_DEFAULT}
                      style={input}
                      title="Pill text"
                    />
                    <select
                      value={t.color || GRID_ROW_TAG_COLOR_DEFAULT}
                      onChange={(e) => patchTag(idx, { color: e.target.value })}
                      style={input}
                      title="Pill color"
                    >
                      {GRID_ROW_TAG_COLORS.map(col => <option key={col.value} value={col.value}>{col.label}</option>)}
                    </select>
                    <span
                      title={isDup ? 'Same crab + quantity as another tag — last one wins on the storefront.' : 'Preview'}
                      style={{
                        display: 'inline-block', padding: '3px 10px', borderRadius: 999,
                        fontSize: 11, fontWeight: 700, color: '#fff', background: c.swatch,
                        whiteSpace: 'nowrap', textAlign: 'center',
                        outline: isDup ? '2px solid #b45309' : 'none', outlineOffset: 1,
                      }}
                    >
                      {String(t.label || '').trim() || GRID_ROW_TAG_LABEL_DEFAULT}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeTag(idx)}
                      title="Remove tag"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-500)', padding: 4, lineHeight: 0 }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {dupKeys.size > 0 && (
            <div style={{ fontSize: 12, color: '#b45309', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
              <AlertTriangle size={12} /> Two tags share the same crab + quantity — only the last one shows on the storefront.
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              type="button"
              onClick={addTag}
              style={{ padding: '6px 12px', background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
            >+ Add tag</button>
            {overCap && (
              <span style={{ fontSize: 11, color: '#b45309', fontWeight: 600 }}>
                <AlertTriangle size={11} style={{ verticalAlign: -1 }} />{' '}
                {jsonLen} / {GRID_ROW_TAGS_MAX_VALUE} chars — remove a tag or shorten labels before saving
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function TagsSection({ currentTagsSet, tagEdits, toggleTag, originalApplied }) {
  const overCap = currentTagsSet.size > MAX_VISIBLE_TAGS;
  return (
    <div style={{ marginBottom: 8 }}>
      <SectionHeader
        title="Product tags"
        subtitle={`Stored as metafields in namespace "${TAG_NAMESPACE}". Storefront displays at most ${MAX_VISIBLE_TAGS} per PDP.`}
        rightSlot={tagEdits ? <span style={{ fontSize: 11, color: 'var(--orange)', fontWeight: 600 }}>unsaved</span> : null}
      />

      {overCap && (
        <div style={{ fontSize: 12, color: '#b45309', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
          <AlertTriangle size={12} /> {currentTagsSet.size} tags selected — only the first {MAX_VISIBLE_TAGS} render on the PDP.
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {PRODUCT_TAGS.map(t => {
          const on = currentTagsSet.has(t.key);
          const wasOn = originalApplied.has(t.key);
          const pending = wasOn !== on;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => toggleTag(t.key)}
              title={t.key}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '4px 10px', borderRadius: 999,
                fontSize: 12, fontWeight: on ? 600 : 500,
                cursor: 'pointer',
                background: on ? t.color : '#fff',
                color: on ? '#fff' : 'var(--gray-600)',
                border: `1.5px solid ${on ? t.color : 'var(--gray-300)'}`,
                outline: pending ? '2px solid var(--orange)' : 'none',
                outlineOffset: pending ? 1 : 0,
              }}
            >
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: on ? '#fff' : t.color, display: 'inline-block' }} />
              {t.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function VideosSection({ productId, primaryImageUrl, heroVideo, heroVideoFieldIds, bcVideos, reloadDetail, setStatus }) {
  const [url, setUrl] = useState(heroVideo?.url || '');
  const [thumb, setThumb] = useState(heroVideo?.thumb || '');
  const [label, setLabel] = useState(heroVideo?.label || '');
  const [saving, setSaving] = useState(false);
  const [uploadingThumb, setUploadingThumb] = useState(false);
  const thumbFileRef = useRef(null);

  const [addYtUrl, setAddYtUrl] = useState('');
  const [addingVideo, setAddingVideo] = useState(false);
  const [editingTitle, setEditingTitle] = useState({});
  const [savingVideoId, setSavingVideoId] = useState(null);
  const [deletingVideoId, setDeletingVideoId] = useState(null);

  useEffect(() => {
    setUrl(heroVideo?.url || '');
    setThumb(heroVideo?.thumb || '');
    setLabel(heroVideo?.label || '');
  }, [heroVideo?.url, heroVideo?.thumb, heroVideo?.label]);

  const heroDirty = (url || '') !== (heroVideo?.url || '')
    || (thumb || '') !== (heroVideo?.thumb || '')
    || (label || '') !== (heroVideo?.label || '');

  async function saveHero() {
    setSaving(true);
    try {
      const ops = [
        { name: HERO_VIDEO_URL_FIELD, value: url, prev: heroVideo?.url || '', id: heroVideoFieldIds?.url },
        { name: HERO_VIDEO_THUMB_FIELD, value: thumb, prev: heroVideo?.thumb || '', id: heroVideoFieldIds?.thumb },
        { name: HERO_VIDEO_LABEL_FIELD, value: label, prev: heroVideo?.label || '', id: heroVideoFieldIds?.label },
      ];
      for (const op of ops) {
        const cur = (op.value || '').trim();
        const prev = (op.prev || '').trim();
        if (cur === prev) continue;
        if (!cur && op.id) {
          await bcApi('DELETE', `/v3/catalog/products/${productId}/custom-fields/${op.id}`);
        } else if (op.id) {
          await bcApi('PUT', `/v3/catalog/products/${productId}/custom-fields/${op.id}`, { name: op.name, value: cur });
        } else if (cur) {
          await bcApi('POST', `/v3/catalog/products/${productId}/custom-fields`, { name: op.name, value: cur });
        }
      }
      setStatus({ type: 'success', msg: 'Hero video saved' });
      await reloadDetail();
    } catch (e) {
      setStatus({ type: 'error', msg: `Hero save failed: ${e.message}` });
    } finally {
      setSaving(false);
    }
  }

  async function removeHero() {
    if (!window.confirm('Remove hero video settings from this product?')) return;
    setSaving(true);
    try {
      for (const id of [heroVideoFieldIds?.url, heroVideoFieldIds?.thumb, heroVideoFieldIds?.label]) {
        if (!id) continue;
        await bcApi('DELETE', `/v3/catalog/products/${productId}/custom-fields/${id}`);
      }
      setStatus({ type: 'success', msg: 'Hero video removed' });
      await reloadDetail();
    } catch (e) {
      setStatus({ type: 'error', msg: `Remove failed: ${e.message}` });
    } finally {
      setSaving(false);
    }
  }

  async function uploadThumbFile(file) {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setStatus({ type: 'error', msg: 'Thumb must be under 5 MB' });
      if (thumbFileRef.current) thumbFileRef.current.value = '';
      return;
    }
    setUploadingThumb(true);
    try {
      const dataUrl = await readFileAsBase64(file);
      const base64 = dataUrl.split(',')[1];
      const res = await fetch('/.netlify/functions/r2-upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: file.name, contentType: file.type, data: base64 }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.url) throw new Error(data?.error || 'Upload failed');
      setThumb(data.url);
      setStatus({ type: 'success', msg: 'Thumb uploaded — click Save to apply' });
    } catch (e) {
      setStatus({ type: 'error', msg: `Thumb upload failed: ${e.message}` });
    } finally {
      setUploadingThumb(false);
      if (thumbFileRef.current) thumbFileRef.current.value = '';
    }
  }

  // Accepts full YouTube URLs (youtube.com/watch?v=…, youtu.be/…, /shorts/…, /embed/…)
  // or the bare 11-char video ID. Anything else returns ''.
  function parseYouTubeId(str) {
    if (!str) return '';
    const s = String(str).trim();
    if (/^[\w-]{11}$/.test(s)) return s;
    const m = s.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
    return m ? m[1] : '';
  }

  async function addVideo() {
    const ytId = parseYouTubeId(addYtUrl);
    if (!ytId) {
      setStatus({ type: 'error', msg: 'Paste a YouTube URL or 11-char video ID' });
      return;
    }
    setAddingVideo(true);
    try {
      await bcApi('POST', `/v3/catalog/products/${productId}/videos`, {
        title: '',
        description: '',
        type: 'youtube',
        video_id: ytId,
        sort_order: (bcVideos?.length || 0),
      });
      setAddYtUrl('');
      setStatus({ type: 'success', msg: 'Video added' });
      await reloadDetail();
    } catch (e) {
      setStatus({ type: 'error', msg: `Add failed: ${e.message}` });
    } finally {
      setAddingVideo(false);
    }
  }

  async function deleteVideo(videoId) {
    if (!window.confirm('Delete this video?')) return;
    setDeletingVideoId(videoId);
    try {
      await bcApi('DELETE', `/v3/catalog/products/${productId}/videos/${videoId}`);
      await reloadDetail();
    } catch (e) {
      setStatus({ type: 'error', msg: `Delete failed: ${e.message}` });
    } finally {
      setDeletingVideoId(null);
    }
  }

  async function saveVideoTitle(video) {
    const newTitle = editingTitle[video.id];
    if (newTitle == null || newTitle === video.title) {
      setEditingTitle(prev => { const next = { ...prev }; delete next[video.id]; return next; });
      return;
    }
    setSavingVideoId(video.id);
    try {
      // BC /videos PUT appears to be full-replacement like /variants — send every field we have.
      await bcApi('PUT', `/v3/catalog/products/${productId}/videos/${video.id}`, {
        title: newTitle,
        description: video.description || '',
        type: video.type || 'youtube',
        video_id: video.video_id,
        sort_order: video.sort_order ?? 0,
      });
      setEditingTitle(prev => { const next = { ...prev }; delete next[video.id]; return next; });
      await reloadDetail();
    } catch (e) {
      setStatus({ type: 'error', msg: `Save title failed: ${e.message}` });
    } finally {
      setSavingVideoId(null);
    }
  }

  const effectiveThumb = thumb || primaryImageUrl || '';
  const effectiveLabel = (label || '').trim() || 'Watch';

  return (
    <div style={{ marginBottom: 24 }}>
      <SectionHeader title="Videos" subtitle="Hero autoplay video + BigCommerce product videos" />

      <div style={{ border: '1px solid var(--gray-200)', borderRadius: 6, padding: 12, marginBottom: 16, background: '#fafbfc' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)', marginBottom: 8 }}>
          HERO VIDEO
          <span style={{ fontSize: 10, fontWeight: 400, color: 'var(--gray-500)', marginLeft: 6 }}>
            — autoplays over the main PDP image and appears as the first thumb in the carousel
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 14 }}>
          <div>
            <div style={{
              width: 120, height: 120, borderRadius: 6, border: '1px solid var(--gray-200)',
              background: effectiveThumb ? `url(${effectiveThumb}) center/cover no-repeat` : 'var(--gray-100)',
              position: 'relative', display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
            }}>
              {effectiveThumb ? (
                <div style={{
                  width: '100%', padding: '3px 0',
                  background: 'linear-gradient(to top, rgba(0,0,0,0.6), rgba(0,0,0,0))',
                  color: '#fff', fontSize: 9, fontWeight: 700, letterSpacing: '0.08em',
                  textTransform: 'uppercase', textAlign: 'center',
                }}>{effectiveLabel}</div>
              ) : (
                <div style={{ color: 'var(--gray-400)', fontSize: 11, marginBottom: 'auto', marginTop: 'auto' }}>no thumb</div>
              )}
            </div>
            <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 4, textAlign: 'center' }}>Live preview</div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-600)' }}>MP4 URL</label>
              <input
                value={url}
                onChange={e => setUrl(e.target.value)}
                placeholder="https://cdn11.bigcommerce.com/s-.../content/video.mp4"
                style={{ width: '100%', padding: '6px 8px', fontSize: 12, border: '1px solid var(--gray-300)', borderRadius: 4, boxSizing: 'border-box' }}
              />
              <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 2 }}>
                Upload the MP4 to BC via WebDAV /dav/content/, then paste the CDN URL here.
              </div>
            </div>

            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-600)' }}>Thumbnail image URL</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  value={thumb}
                  onChange={e => setThumb(e.target.value)}
                  placeholder="Leave empty to use main product image"
                  style={{ flex: 1, padding: '6px 8px', fontSize: 12, border: '1px solid var(--gray-300)', borderRadius: 4 }}
                />
                <button
                  onClick={() => thumbFileRef.current?.click()}
                  disabled={uploadingThumb}
                  style={{ padding: '6px 10px', fontSize: 11, fontWeight: 600, color: '#fff', background: 'var(--orange)', border: 'none', borderRadius: 4, cursor: uploadingThumb ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                  <Upload size={11} /> {uploadingThumb ? 'Uploading…' : 'Upload'}
                </button>
                <input
                  ref={thumbFileRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => uploadThumbFile(e.target.files?.[0])}
                />
              </div>
            </div>

            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-600)' }}>Thumb label text</label>
              <input
                value={label}
                onChange={e => setLabel(e.target.value)}
                placeholder="Watch"
                maxLength={32}
                style={{ width: '100%', padding: '6px 8px', fontSize: 12, border: '1px solid var(--gray-300)', borderRadius: 4, boxSizing: 'border-box' }}
              />
              <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 2 }}>
                Shown at the bottom of the thumb. Blank = "Watch".
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button
                onClick={saveHero}
                disabled={saving || !heroDirty}
                style={{
                  padding: '6px 14px', fontSize: 12, fontWeight: 600,
                  color: '#fff',
                  background: (heroDirty && !saving) ? 'var(--orange)' : 'var(--gray-300)',
                  border: 'none', borderRadius: 4,
                  cursor: (heroDirty && !saving) ? 'pointer' : 'not-allowed',
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                }}
              >
                <Save size={11} /> {saving ? 'Saving…' : 'Save'}
              </button>
              {(heroVideo?.url || heroVideo?.thumb || heroVideo?.label) && (
                <button
                  onClick={removeHero}
                  disabled={saving}
                  style={{ padding: '6px 10px', fontSize: 12, fontWeight: 600, color: '#c33', background: '#fff', border: '1px solid #c33', borderRadius: 4, cursor: saving ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                  <Trash2 size={11} /> Remove
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div style={{ border: '1px solid var(--gray-200)', borderRadius: 6, padding: 12, background: '#fafbfc' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)', marginBottom: 8 }}>
          BIGCOMMERCE VIDEOS ({bcVideos?.length || 0})
          <span style={{ fontSize: 10, fontWeight: 400, color: 'var(--gray-500)', marginLeft: 6 }}>
            — YouTube videos shown in the thumb carousel (after images)
          </span>
        </div>

        {(bcVideos?.length > 0) ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
            {bcVideos.map(v => {
              const editing = editingTitle[v.id] != null;
              const ytThumb = `https://i.ytimg.com/vi/${v.video_id}/hqdefault.jpg`;
              return (
                <div key={v.id} style={{ display: 'grid', gridTemplateColumns: '60px 1fr auto', gap: 10, alignItems: 'center', padding: 8, background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 4 }}>
                  <img src={ytThumb} alt="" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 3, background: 'var(--gray-100)' }} />
                  <div style={{ minWidth: 0 }}>
                    {editing ? (
                      <input
                        value={editingTitle[v.id]}
                        onChange={e => setEditingTitle(prev => ({ ...prev, [v.id]: e.target.value }))}
                        autoFocus
                        style={{ width: '100%', padding: '4px 8px', fontSize: 12, border: '1px solid var(--orange)', borderRadius: 3 }}
                      />
                    ) : (
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {v.title || <span style={{ color: 'var(--gray-400)', fontStyle: 'italic' }}>(no title)</span>}
                      </div>
                    )}
                    <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 2 }}>
                      youtube:{v.video_id} · <a href={`https://youtu.be/${v.video_id}`} target="_blank" rel="noreferrer" style={{ color: 'var(--gray-500)' }}>open</a>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {editing ? (
                      <>
                        <button
                          onClick={() => saveVideoTitle(v)}
                          disabled={savingVideoId === v.id}
                          style={{ padding: '4px 8px', fontSize: 11, color: '#fff', background: 'var(--orange)', border: 'none', borderRadius: 3, cursor: 'pointer' }}
                        >{savingVideoId === v.id ? '…' : <Check size={12} />}</button>
                        <button
                          onClick={() => setEditingTitle(prev => { const next = { ...prev }; delete next[v.id]; return next; })}
                          style={{ padding: '4px 8px', fontSize: 11, color: 'var(--gray-500)', background: '#fff', border: '1px solid var(--gray-300)', borderRadius: 3, cursor: 'pointer' }}
                        ><X size={12} /></button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => setEditingTitle(prev => ({ ...prev, [v.id]: v.title || '' }))}
                          style={{ padding: '4px 8px', fontSize: 11, color: 'var(--gray-500)', background: '#fff', border: '1px solid var(--gray-300)', borderRadius: 3, cursor: 'pointer' }}
                          title="Edit title"
                        >✎</button>
                        <button
                          onClick={() => deleteVideo(v.id)}
                          disabled={deletingVideoId === v.id}
                          style={{ padding: '4px 8px', fontSize: 11, color: '#c33', background: '#fff', border: '1px solid #c33', borderRadius: 3, cursor: (deletingVideoId === v.id) ? 'not-allowed' : 'pointer' }}
                          title="Delete video"
                        >{deletingVideoId === v.id ? '…' : <Trash2 size={12} />}</button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ fontSize: 11, color: 'var(--gray-500)', fontStyle: 'italic', padding: '8px 0', marginBottom: 8 }}>
            No BigCommerce videos on this product yet.
          </div>
        )}

        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input
            value={addYtUrl}
            onChange={e => setAddYtUrl(e.target.value)}
            placeholder="Paste a YouTube URL (https://youtu.be/... or https://www.youtube.com/watch?v=...)"
            style={{ flex: 1, padding: '6px 8px', fontSize: 12, border: '1px solid var(--gray-300)', borderRadius: 4 }}
          />
          <button
            onClick={addVideo}
            disabled={addingVideo || !addYtUrl.trim()}
            style={{
              padding: '6px 14px', fontSize: 12, fontWeight: 600, color: '#fff',
              background: (addYtUrl.trim() && !addingVideo) ? 'var(--orange)' : 'var(--gray-300)',
              border: 'none', borderRadius: 4,
              cursor: (addYtUrl.trim() && !addingVideo) ? 'pointer' : 'not-allowed',
            }}
          >{addingVideo ? 'Adding…' : '+ Add'}</button>
        </div>
      </div>
    </div>
  );
}

// Unified Media panel: one reorderable list of hero video + images + BC videos,
// with type-specific inline controls per row. Reorder writes to the
// pdp.media_order metafield immediately (no dirty-tracking); the storefront
// reads that metafield and reorders thumbnail <li> elements before slick
// initializes. Hero and video edits also save immediately. Image metadata
// edits (alt, primary, delete) stay in the tab-wide batched state and go out
// via the top Save button — matches existing image-editor behavior.
function MediaSection({
  productId,
  detail,
  imageEdits, patchImageEdit, getImageField, toggleDeleteImage, setThumbnail,
  uploading, openFilePicker, uploadGalleryFile, setPreview,
  replacingId, openReplacePicker,
  reloadDetail, setStatus,
  recipe, patchRecipe,
}) {
  // Hero videos are now a LIST (Phase 1). detail.heroVideos is the source of
  // truth; each entry is { id, url, thumb, label }.
  const heroVideos = detail.heroVideos || [];

  // Recipe tagging — only shown on image rows when the Recipe Button feature
  // is enabled for this product (configured in the Recipe Button section).
  const recipePhotoMap = new Map(((recipe && recipe.photos) || []).map(p => [Number(p.id), String(p.recipe || '')]));
  const [recipes, setRecipes] = useState([]);
  const [recipesLoading, setRecipesLoading] = useState(false);
  // Recipe list always loads so the per-photo "Recipe button" control shows on every
  // product's photos — no master toggle to hunt for. Flagging a photo auto-enables
  // the feature for the product (see toggleRecipePhoto); the storefront still only
  // shows the button on the photos actually flagged.
  useEffect(() => {
    let cancelled = false;
    setRecipesLoading(true);
    neonQuery('recipes_list', { limit: 200, visible: true, sort: 'name' })
      .then(r => { if (!cancelled) setRecipes(Array.isArray(r?.data) ? r.data : []); })
      .catch(() => { /* dropdowns just stay on Auto */ })
      .finally(() => { if (!cancelled) setRecipesLoading(false); });
    return () => { cancelled = true; };
  }, []);
  const toggleRecipePhoto = (id) => {
    const list = ((recipe && recipe.photos) || []).slice();
    const i = list.findIndex(p => Number(p.id) === id);
    if (i >= 0) list.splice(i, 1);
    else list.push({ id, recipe: '' });
    // Flagging a photo turns the feature on for this product; unflagging the last
    // photo turns it off. No separate master toggle needed.
    patchRecipe({ photos: list, enabled: list.length > 0 });
  };
  const setRecipePhoto = (id, slug) => {
    patchRecipe({ photos: ((recipe && recipe.photos) || []).map(p => Number(p.id) === id ? { ...p, recipe: slug } : p) });
  };

  // Hero editor state. We edit one hero video at a time. heroEditId is the id of
  // the entry being edited (or '__new__' when adding); null means the editor is
  // closed. heroDraft holds the in-progress url/thumb/label for that entry.
  const [heroEditId, setHeroEditId] = useState(null);
  const [heroDraft, setHeroDraft] = useState({ url: '', thumb: '', label: '' });
  const [uploadingHeroThumb, setUploadingHeroThumb] = useState(false);
  const [savingHero, setSavingHero] = useState(false);
  const heroEditing = heroEditId != null;
  const heroEditingNew = heroEditId === '__new__';
  // The hero edit panel renders below the media list — on a long list (or
  // smaller viewport) it opens off-screen. Scroll it into view when toggled
  // on so the user lands on the field they just asked to edit.
  const heroPanelRef = useRef(null);
  useEffect(() => {
    if (!heroEditing) return;
    const el = heroPanelRef.current;
    if (!el) return;
    requestAnimationFrame(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }, [heroEditing]);
  const heroThumbFileRef = useRef(null);

  const openHeroEditor = (hv) => {
    if (hv) {
      setHeroEditId(hv.id);
      setHeroDraft({ url: hv.url || '', thumb: hv.thumb || '', label: hv.label || '' });
    } else {
      setHeroEditId('__new__');
      setHeroDraft({ url: '', thumb: '', label: '' });
    }
  };
  const closeHeroEditor = () => {
    setHeroEditId(null);
    setHeroDraft({ url: '', thumb: '', label: '' });
  };

  // Per-video inline edits
  const [editingVideoTitle, setEditingVideoTitle] = useState({});
  const [savingVideoId, setSavingVideoId] = useState(null);
  const [deletingVideoId, setDeletingVideoId] = useState(null);

  // Add-new-YouTube form
  const [addYtUrl, setAddYtUrl] = useState('');
  const [addingVideo, setAddingVideo] = useState(false);

  // Reorder + delete state
  const [reordering, setReordering] = useState(false);

  // "Pick from Media Library" — pick assets, square-crop them, then add as
  // regular gallery images.
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  const [cropImages, setCropImages] = useState(null); // null | [{ url, name, alt }]
  const [addingFromLibrary, setAddingFromLibrary] = useState(false);

  // Picked Media Library assets → open the square-crop modal. The PDP main
  // photo is square, so source photos (usually vertical/horizontal) get framed
  // to fill it. Loaded over a same-origin relative URL so the crop canvas
  // isn't tainted.
  const openCropForAssets = (assets) => {
    const picked = (assets || []).map((a) => {
      const key = a.r2_original_key || a.r2_thumb_key;
      if (!key) return null;
      return {
        // r2-resize (not r2-image): it downsamples server-side, so big Media
        // Library masters come back well under Netlify's ~6 MB response cap.
        // r2-image base64-buffers the master whole and 502s on large photos.
        url: `/.netlify/functions/r2-resize?key=${encodeURIComponent(key)}&edge=2400`,
        name: `${(a.suggested_name || 'media-image').replace(/[^a-z0-9._-]+/gi, '-').slice(0, 60)}.jpg`,
        alt: a.alt_text || '',
        // Remember which Media Library original this crop came from so it can
        // be re-cropped later (see openRecrop).
        sourceKey: key,
      };
    }).filter(Boolean);
    if (picked.length) setCropImages(picked);
  };

  // Re-crop a gallery image — just opens the crop modal on the photo, no
  // hunting. If a prior crop saved the Media Library original
  // (detail.imageSources) we crop that pristine master; otherwise we crop the
  // current BC product image itself, loaded through bc-image-proxy so it's
  // same-origin and the export canvas isn't tainted.
  const openRecrop = (img) => {
    const savedKey = detail.imageSources && detail.imageSources[img.id];
    const url = savedKey
      ? `/.netlify/functions/r2-resize?key=${encodeURIComponent(savedKey)}&edge=2400`
      : `/.netlify/functions/bc-image-proxy?url=${encodeURIComponent(img.url_zoom || img.url_standard || img.url_thumbnail || '')}`;
    setCropImages([{
      url,
      name: `${(img.description || 'recipe-photo').replace(/[^a-z0-9._-]+/gi, '-').slice(0, 60) || 'photo'}.jpg`,
      alt: img.description || '',
      // Only re-link to a source when we cropped the true Media Library
      // original — a crop-of-the-current-image has no R2 master to point at.
      sourceKey: savedKey || null,
      recropImageId: img.id,
    }]);
  };

  // Whole-config write of the pdp.image_sources metafield (image id → R2 key).
  const saveImageSources = async (map) => {
    const body = { key: IMAGE_SOURCES_KEY, value: JSON.stringify(map), namespace: PDP_NS, permission_set: 'read_and_sf_access' };
    if (detail.imageSourcesMfId) {
      await bcApi('PUT', `/v3/catalog/products/${productId}/metafields/${detail.imageSourcesMfId}`, body);
    } else {
      await bcApi('POST', `/v3/catalog/products/${productId}/metafields`, body);
    }
  };

  // Cropped square JPEGs → uploaded as gallery images. Multipart upload (not
  // image_url) so several images all land reliably and show in the carousel.
  // An item with recropImageId replaces that image in place (upload new,
  // delete old) instead of adding. Either way the new image is linked back to
  // its Media Library original via the image_sources metafield.
  // items: [{ file, alt, sourceKey?, recropImageId? }] — one per photo loaded OK.
  const uploadCroppedGallery = async (items) => {
    setCropImages(null);
    if (!productId || !items || items.length === 0) return;
    setAddingFromLibrary(true);
    setStatus(null);
    let added = 0;
    let recropped = 0;
    const sources = { ...(detail.imageSources || {}) };
    try {
      let nextSort = (detail?.images || []).reduce((max, img) => Math.max(max, img.sort_order ?? 0), -1) + 1;
      for (const it of items) {
        if (it.recropImageId) {
          // Replace in place — inherit the old image's slot + alt + thumbnail.
          const oldImg = (detail.images || []).find(i => i.id === it.recropImageId);
          const res = await uploadProductImage(productId, it.file, {
            description: it.alt || (oldImg ? (oldImg.description || '') : ''),
            sortOrder: oldImg ? (oldImg.sort_order ?? nextSort++) : nextSort++,
            isThumbnail: oldImg ? !!oldImg.is_thumbnail : false,
          });
          await bcApi('DELETE', `/v3/catalog/products/${productId}/images/${it.recropImageId}`);
          delete sources[it.recropImageId];
          const newId = res && (res.id || (res.data && res.data.id));
          if (newId && it.sourceKey) sources[newId] = it.sourceKey;
          recropped++;
        } else {
          const res = await uploadProductImage(productId, it.file, {
            description: it.alt || '',
            sortOrder: nextSort++,
            isThumbnail: false,
          });
          const newId = res && (res.id || (res.data && res.data.id));
          if (newId && it.sourceKey) sources[newId] = it.sourceKey;
          added++;
        }
      }
      try { await saveImageSources(sources); } catch (e) { /* non-fatal — re-crop link just won't update */ }
      const parts = [];
      if (added) parts.push(`Added ${added} image${added === 1 ? '' : 's'}`);
      if (recropped) parts.push(`Re-cropped ${recropped} image${recropped === 1 ? '' : 's'}`);
      setStatus({ type: 'success', msg: `${parts.join(' · ') || 'Done'} from the Media Library.` });
      await reloadDetail();
    } catch (err) {
      setStatus({ type: 'error', msg: `Media Library crop failed: ${err.message}` });
      if (added > 0 || recropped > 0) await reloadDetail();
    } finally {
      setAddingFromLibrary(false);
    }
  };

  // Build the unified list. Hero comes in only if its URL is set. Images with
  // banner or variant-library markers are filtered out (they have their own
  // sections). Apply pdp.media_order when present; any items not yet listed
  // in the order (newly added) fall through to the end.
  const items = useMemo(() => {
    const list = [];
    for (const hv of heroVideos) {
      list.push({ key: `hero:${hv.id}`, type: 'hero', id: hv.id, video: hv });
    }
    for (const img of detail.images || []) {
      const alt = img.description || '';
      if (alt === '__PDP_BANNER__') continue;
      if (alt.indexOf('__VARIANT_LIBRARY__') !== -1) continue;
      list.push({ key: `image:${img.id}`, type: 'image', id: img.id, img });
    }
    for (const v of detail.bcVideos || []) {
      // Key by YouTube ID (what the storefront can see in the DOM) — the BC
      // internal record id (v.id) isn't exposed to the Stencil template, so
      // matching on v.video_id lets the reorder script find the <li>.
      list.push({ key: `video:${v.video_id}`, type: 'video', id: v.id, video: v });
    }
    const order = detail.mediaOrder;
    if (order && order.length) {
      const byKey = new Map(list.map(it => [it.key, it]));
      const ordered = [];
      const seen = new Set();
      for (const k of order) {
        const it = byKey.get(k);
        if (it) { ordered.push(it); seen.add(k); }
      }
      for (const it of list) {
        if (!seen.has(it.key)) ordered.push(it);
      }
      return ordered;
    }
    return list;
  }, [detail.images, detail.bcVideos, detail.heroVideos, detail.mediaOrder]); // eslint-disable-line react-hooks/exhaustive-deps

  async function writeMediaOrder(orderKeys) {
    const body = {
      namespace: PDP_NS,
      key: MEDIA_ORDER_KEY,
      value: JSON.stringify(orderKeys),
      permission_set: 'read_and_sf_access',
    };
    if (detail.mediaOrderMfId) {
      await bcApi('PUT', `/v3/catalog/products/${productId}/metafields/${detail.mediaOrderMfId}`, body);
    } else {
      await bcApi('POST', `/v3/catalog/products/${productId}/metafields`, body);
    }
  }

  async function moveMedia(idx, direction) {
    const nextIdx = idx + direction;
    if (nextIdx < 0 || nextIdx >= items.length) return;
    const next = items.slice();
    [next[idx], next[nextIdx]] = [next[nextIdx], next[idx]];
    setReordering(true);
    try {
      await writeMediaOrder(next.map(x => x.key));
      await reloadDetail();
    } catch (e) {
      setStatus({ type: 'error', msg: `Reorder failed: ${e.message}` });
    } finally {
      setReordering(false);
    }
  }

  // ─── Hero videos (list) save/remove/thumb-upload ──────────────────────────
  // Whole-list write of the pdp.hero_videos metafield (POST if no id yet, PUT to
  // update, DELETE when the list becomes empty). Same metafield pattern as
  // saveImageSources / writeMediaOrder. Also prunes any stale 'hero:<id>' keys
  // from pdp.media_order for entries that no longer exist (the storefront would
  // ignore them, but keeping the order clean avoids confusion).
  async function saveHeroVideos(nextList) {
    const clean = (nextList || [])
      .filter(v => v && typeof v.url === 'string' && v.url.trim())
      .map(v => ({ id: v.id || genHeroVideoId(), url: v.url.trim(), thumb: (v.thumb || '').trim(), label: (v.label || '').trim() }));
    if (clean.length === 0) {
      if (detail.heroVideosMfId) {
        await bcApi('DELETE', `/v3/catalog/products/${productId}/metafields/${detail.heroVideosMfId}`);
      }
    } else {
      const body = { key: HERO_VIDEOS_KEY, value: JSON.stringify(clean), namespace: PDP_NS, permission_set: 'read_and_sf_access' };
      if (detail.heroVideosMfId) {
        await bcApi('PUT', `/v3/catalog/products/${productId}/metafields/${detail.heroVideosMfId}`, body);
      } else {
        await bcApi('POST', `/v3/catalog/products/${productId}/metafields`, body);
      }
    }
    // Drop media_order keys for hero entries that no longer exist.
    const order = detail.mediaOrder;
    if (order && order.length) {
      const liveHeroKeys = new Set(clean.map(v => `hero:${v.id}`));
      const pruned = order.filter(k => !k.startsWith('hero:') || liveHeroKeys.has(k));
      if (pruned.length !== order.length) {
        try { await writeMediaOrder(pruned); } catch { /* non-fatal — stale keys are ignored at render */ }
      }
    }
  }

  // Upsert the entry currently in heroDraft into the list, then persist.
  async function saveHeroDraft() {
    const url = (heroDraft.url || '').trim();
    if (!url) return;
    setSavingHero(true);
    try {
      const draft = { url, thumb: (heroDraft.thumb || '').trim(), label: (heroDraft.label || '').trim() };
      let next;
      if (heroEditingNew) {
        next = [...heroVideos, { id: genHeroVideoId(), ...draft }];
      } else {
        next = heroVideos.map(v => v.id === heroEditId ? { ...v, ...draft } : v);
      }
      await saveHeroVideos(next);
      setStatus({ type: 'success', msg: heroEditingNew ? 'Hero video added' : 'Hero video saved' });
      closeHeroEditor();
      await reloadDetail();
    } catch (e) {
      setStatus({ type: 'error', msg: `Hero save failed: ${e.message}` });
    } finally {
      setSavingHero(false);
    }
  }

  async function removeHeroVideo(id) {
    if (!window.confirm('Remove this hero video from the product?')) return;
    setSavingHero(true);
    try {
      const next = heroVideos.filter(v => v.id !== id);
      await saveHeroVideos(next);
      if (heroEditId === id) closeHeroEditor();
      setStatus({ type: 'success', msg: 'Hero video removed' });
      await reloadDetail();
    } catch (e) {
      setStatus({ type: 'error', msg: `Remove failed: ${e.message}` });
    } finally {
      setSavingHero(false);
    }
  }

  async function uploadHeroThumbFile(file) {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setStatus({ type: 'error', msg: 'Thumb must be under 5 MB' });
      if (heroThumbFileRef.current) heroThumbFileRef.current.value = '';
      return;
    }
    setUploadingHeroThumb(true);
    try {
      const dataUrl = await readFileAsBase64(file);
      const base64 = dataUrl.split(',')[1];
      const res = await fetch('/.netlify/functions/r2-upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: file.name, contentType: file.type, data: base64 }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.url) throw new Error(data?.error || 'Upload failed');
      setHeroDraft(prev => ({ ...prev, thumb: data.url }));
      setStatus({ type: 'success', msg: 'Thumb uploaded — click Save to apply' });
    } catch (e) {
      setStatus({ type: 'error', msg: `Thumb upload failed: ${e.message}` });
    } finally {
      setUploadingHeroThumb(false);
      if (heroThumbFileRef.current) heroThumbFileRef.current.value = '';
    }
  }

  // ─── BC video helpers ─────────────────────────────────────────────────────
  function parseYouTubeId(str) {
    if (!str) return '';
    const s = String(str).trim();
    if (/^[\w-]{11}$/.test(s)) return s;
    const m = s.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
    return m ? m[1] : '';
  }

  async function addVideo() {
    const ytId = parseYouTubeId(addYtUrl);
    if (!ytId) {
      setStatus({ type: 'error', msg: 'Paste a YouTube URL or 11-char video ID' });
      return;
    }
    setAddingVideo(true);
    try {
      await bcApi('POST', `/v3/catalog/products/${productId}/videos`, {
        title: '', description: '', type: 'youtube', video_id: ytId, sort_order: (detail.bcVideos?.length || 0),
      });
      setAddYtUrl('');
      setStatus({ type: 'success', msg: 'Video added' });
      await reloadDetail();
    } catch (e) {
      setStatus({ type: 'error', msg: `Add failed: ${e.message}` });
    } finally {
      setAddingVideo(false);
    }
  }

  async function deleteVideo(videoId) {
    if (!window.confirm('Delete this video?')) return;
    setDeletingVideoId(videoId);
    try {
      await bcApi('DELETE', `/v3/catalog/products/${productId}/videos/${videoId}`);
      await reloadDetail();
    } catch (e) {
      setStatus({ type: 'error', msg: `Delete failed: ${e.message}` });
    } finally {
      setDeletingVideoId(null);
    }
  }

  async function saveVideoTitle(video) {
    const newTitle = editingVideoTitle[video.id];
    if (newTitle == null || newTitle === video.title) {
      setEditingVideoTitle(prev => { const next = { ...prev }; delete next[video.id]; return next; });
      return;
    }
    setSavingVideoId(video.id);
    try {
      await bcApi('PUT', `/v3/catalog/products/${productId}/videos/${video.id}`, {
        title: newTitle, description: video.description || '', type: video.type || 'youtube',
        video_id: video.video_id, sort_order: video.sort_order ?? 0,
      });
      setEditingVideoTitle(prev => { const next = { ...prev }; delete next[video.id]; return next; });
      await reloadDetail();
    } catch (e) {
      setStatus({ type: 'error', msg: `Save title failed: ${e.message}` });
    } finally {
      setSavingVideoId(null);
    }
  }

  // ─── Render helpers ───────────────────────────────────────────────────────
  const rowStyle = { display: 'grid', gridTemplateColumns: '28px 72px 1fr auto', gap: 10, alignItems: 'center', padding: 8, background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 4 };
  const moveBtnStyle = { padding: '2px 4px', fontSize: 11, color: 'var(--gray-600)', background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 3, cursor: 'pointer', display: 'block', width: 22, textAlign: 'center', lineHeight: 1 };
  const tagStyle = (bg) => ({ display: 'inline-block', padding: '1px 6px', borderRadius: 3, background: bg, color: '#fff', fontSize: 9, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' });
  const smallBtn = { padding: '4px 8px', fontSize: 11, background: '#fff', border: '1px solid var(--gray-300)', borderRadius: 3, cursor: 'pointer', color: 'var(--gray-600)' };
  const smallBtnDanger = { ...smallBtn, color: '#c33', borderColor: '#c33' };
  const inputStyle = { padding: '4px 8px', fontSize: 12, border: '1px solid var(--gray-300)', borderRadius: 3, width: '100%', boxSizing: 'border-box' };
  const addBtnStyle = { padding: '6px 14px', fontSize: 12, fontWeight: 600, color: '#fff', background: 'var(--orange)', border: 'none', borderRadius: 4, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 };
  const addBtnDisabled = { ...addBtnStyle, background: 'var(--gray-300)', cursor: 'not-allowed' };

  function MoveButtons({ idx, total }) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <button onClick={() => moveMedia(idx, -1)} disabled={reordering || idx === 0} title="Move up" style={{ ...moveBtnStyle, opacity: idx === 0 ? 0.3 : 1, cursor: idx === 0 || reordering ? 'not-allowed' : 'pointer' }}>▲</button>
        <button onClick={() => moveMedia(idx, 1)} disabled={reordering || idx === total - 1} title="Move down" style={{ ...moveBtnStyle, opacity: idx === total - 1 ? 0.3 : 1, cursor: idx === total - 1 || reordering ? 'not-allowed' : 'pointer' }}>▼</button>
      </div>
    );
  }

  // ─── Section render ───────────────────────────────────────────────────────
  return (
    <div style={{ marginBottom: 24 }}>
      <SectionHeader
        title="Media"
        subtitle="Thumbnail carousel on the PDP — images, hero autoplay video, and YouTube videos in one list. Use ▲ / ▼ to reorder."
        rightSlot={reordering ? <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>Saving order…</span> : null}
      />

      {items.length === 0 && (
        <div style={{ padding: 20, textAlign: 'center', fontSize: 12, color: 'var(--gray-500)', border: '1px dashed var(--gray-200)', borderRadius: 6, marginBottom: 12 }}>
          No media on this product yet. Add an image or video below.
        </div>
      )}

      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {items.map((item, idx) => {
          if (item.type === 'hero') {
            const hv = item.video || { id: item.id, url: '', thumb: '', label: '' };
            // When the editor is open on this entry, preview the in-progress
            // draft; otherwise preview the saved values.
            const isEditingThis = heroEditId === hv.id;
            const draftFor = isEditingThis ? heroDraft : hv;
            const previewThumb = (draftFor.thumb || '') || detail.primaryImageUrl || '';
            const previewLabel = (draftFor.label || '').trim() || 'Watch';
            return (
              <li key={item.key} style={{ ...rowStyle, background: '#fffaf6', borderColor: '#ffe3cf' }}>
                <MoveButtons idx={idx} total={items.length} />
                <div style={{ width: 72, height: 72, borderRadius: 4, background: previewThumb ? `url(${previewThumb}) center/cover no-repeat` : 'var(--gray-100)', position: 'relative', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                  {previewThumb && (
                    <div style={{ width: '100%', padding: '2px 0', background: 'linear-gradient(to top, rgba(0,0,0,0.6), rgba(0,0,0,0))', color: '#fff', fontSize: 8, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', textAlign: 'center' }}>{previewLabel}</div>
                  )}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                    <span style={tagStyle('var(--orange)')}>Hero video</span>
                    <span style={{ fontSize: 11, color: 'var(--gray-500)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{hv.url}</span>
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>
                    Label: <strong>{(hv.label || '').trim() || 'Watch'}</strong>
                    {!hv.thumb && <span style={{ marginLeft: 8 }}>· thumb: main product image</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button onClick={() => (isEditingThis ? closeHeroEditor() : openHeroEditor(hv))} style={smallBtn} title="Edit hero video">✎</button>
                  <button onClick={() => removeHeroVideo(hv.id)} style={smallBtnDanger} title="Remove hero video"><Trash2 size={12} /></button>
                </div>
              </li>
            );
          }

          if (item.type === 'image') {
            const img = item.img;
            const isDeleted = !!imageEdits[img.id]?.deleted;
            const isThumb = getImageField(img, 'is_thumbnail');
            const alt = getImageField(img, 'description') || '';
            return (
              <li key={item.key} style={{ ...rowStyle, opacity: isDeleted ? 0.45 : 1, textDecoration: isDeleted ? 'line-through' : 'none' }}>
                <MoveButtons idx={idx} total={items.length} />
                <button
                  onClick={() => setPreview({ url: img.url_standard || img.url_zoom, alt })}
                  style={{ padding: 0, background: 'none', border: 'none', cursor: 'pointer' }}
                  title="Preview"
                >
                  <img src={img.url_thumbnail || img.url_standard} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 4, background: 'var(--gray-100)' }} />
                </button>
                <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={tagStyle('var(--navy)')}>Image</span>
                    {isThumb && <span style={tagStyle('#1a8c3a')} title="Primary thumbnail"><Star size={9} style={{ verticalAlign: 'text-bottom' }} /> Primary</span>}
                  </div>
                  <input
                    value={alt}
                    onChange={e => patchImageEdit(img.id, { description: e.target.value })}
                    placeholder="Alt text (for accessibility)"
                    style={inputStyle}
                    disabled={isDeleted}
                  />
                  {!isDeleted && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 2 }}>
                      <label
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 600,
                          color: recipePhotoMap.has(img.id) ? 'var(--orange)' : 'var(--gray-600)', whiteSpace: 'nowrap',
                        }}
                        title="Show the “Send me this Recipe” button on this photo"
                      >
                        <input
                          type="checkbox"
                          checked={recipePhotoMap.has(img.id)}
                          onChange={() => toggleRecipePhoto(img.id)}
                          style={{ accentColor: 'var(--orange)' }}
                        />
                        Recipe button
                      </label>
                      {recipePhotoMap.has(img.id) && (
                        <div style={{ flex: 1, minWidth: 170 }}>
                          <RecipeSelect
                            value={recipePhotoMap.get(img.id) || ''}
                            recipes={recipes}
                            recipesLoading={recipesLoading}
                            onChange={(s) => setRecipePhoto(img.id, s)}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  {!isThumb && !isDeleted && (
                    <button onClick={() => setThumbnail(img.id)} style={smallBtn} title="Make primary thumbnail"><Star size={12} /></button>
                  )}
                  {!isDeleted && (
                    <button
                      onClick={() => openRecrop(img)}
                      disabled={addingFromLibrary}
                      style={smallBtn}
                      title="Re-crop / re-frame this photo"
                    >
                      <Crop size={12} />
                    </button>
                  )}
                  {!isDeleted && (
                    <button onClick={() => openReplacePicker(img.id)} disabled={replacingId === img.id} style={smallBtn} title="Replace image">
                      {replacingId === img.id ? '…' : <Upload size={12} />}
                    </button>
                  )}
                  <button onClick={() => toggleDeleteImage(img.id)} style={isDeleted ? smallBtn : smallBtnDanger} title={isDeleted ? 'Undo delete' : 'Delete image'}>
                    {isDeleted ? 'Undo' : <Trash2 size={12} />}
                  </button>
                </div>
              </li>
            );
          }

          if (item.type === 'video') {
            const v = item.video;
            const editing = editingVideoTitle[v.id] != null;
            const ytThumb = `https://i.ytimg.com/vi/${v.video_id}/hqdefault.jpg`;
            return (
              <li key={item.key} style={rowStyle}>
                <MoveButtons idx={idx} total={items.length} />
                <img src={ytThumb} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 4, background: 'var(--gray-100)' }} />
                <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={tagStyle('#c00')}>YouTube</span>
                    <span style={{ fontSize: 10, color: 'var(--gray-500)' }}>
                      {v.video_id} · <a href={`https://youtu.be/${v.video_id}`} target="_blank" rel="noreferrer" style={{ color: 'var(--gray-500)' }}>open</a>
                    </span>
                  </div>
                  {editing ? (
                    <input
                      value={editingVideoTitle[v.id]}
                      onChange={e => setEditingVideoTitle(prev => ({ ...prev, [v.id]: e.target.value }))}
                      autoFocus
                      style={inputStyle}
                    />
                  ) : (
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {v.title || <span style={{ color: 'var(--gray-400)', fontStyle: 'italic' }}>(no title)</span>}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  {editing ? (
                    <>
                      <button onClick={() => saveVideoTitle(v)} disabled={savingVideoId === v.id} style={{ ...smallBtn, color: '#fff', background: 'var(--orange)', borderColor: 'var(--orange)' }} title="Save title">
                        {savingVideoId === v.id ? '…' : <Check size={12} />}
                      </button>
                      <button onClick={() => setEditingVideoTitle(prev => { const n = { ...prev }; delete n[v.id]; return n; })} style={smallBtn} title="Cancel"><X size={12} /></button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => setEditingVideoTitle(prev => ({ ...prev, [v.id]: v.title || '' }))} style={smallBtn} title="Edit title">✎</button>
                      <button onClick={() => deleteVideo(v.id)} disabled={deletingVideoId === v.id} style={smallBtnDanger} title="Delete video">
                        {deletingVideoId === v.id ? '…' : <Trash2 size={12} />}
                      </button>
                    </>
                  )}
                </div>
              </li>
            );
          }
          return null;
        })}
      </ul>

      {/* Hero expanded form — used for both "add new" and "edit existing" */}
      {heroEditing && (
        <div ref={heroPanelRef} style={{ marginTop: 12, padding: 12, border: '1px solid #ffe3cf', borderRadius: 6, background: '#fffaf6', scrollMarginTop: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)', marginBottom: 8 }}>
            {heroEditingNew ? 'ADD HERO VIDEO' : 'EDIT HERO VIDEO'}
            <span style={{ fontSize: 10, fontWeight: 400, color: 'var(--gray-500)', marginLeft: 6 }}>
              — autoplays over the main PDP image
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: 12 }}>
            <div>
              <div style={{ width: 100, height: 100, borderRadius: 4, border: '1px solid var(--gray-200)', background: (heroDraft.thumb || detail.primaryImageUrl) ? `url(${heroDraft.thumb || detail.primaryImageUrl}) center/cover no-repeat` : 'var(--gray-100)', position: 'relative', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                {(heroDraft.thumb || detail.primaryImageUrl) && (
                  <div style={{ width: '100%', padding: '2px 0', background: 'linear-gradient(to top, rgba(0,0,0,0.6), rgba(0,0,0,0))', color: '#fff', fontSize: 8, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', textAlign: 'center' }}>{(heroDraft.label || '').trim() || 'Watch'}</div>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-600)' }}>MP4 URL</label>
                <input value={heroDraft.url} onChange={e => setHeroDraft(prev => ({ ...prev, url: e.target.value }))} placeholder="https://cdn11.bigcommerce.com/s-.../content/video.mp4" style={inputStyle} />
                <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 2 }}>Upload via BC WebDAV /dav/content/, then paste the CDN URL.</div>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-600)' }}>Thumbnail image URL</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input value={heroDraft.thumb} onChange={e => setHeroDraft(prev => ({ ...prev, thumb: e.target.value }))} placeholder="Leave empty to use main product image" style={inputStyle} />
                  <button onClick={() => heroThumbFileRef.current?.click()} disabled={uploadingHeroThumb} style={{ ...addBtnStyle, padding: '4px 10px', whiteSpace: 'nowrap' }}>
                    <Upload size={11}/> {uploadingHeroThumb ? '…' : 'Upload'}
                  </button>
                  <input ref={heroThumbFileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => uploadHeroThumbFile(e.target.files?.[0])} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-600)' }}>Thumb label</label>
                <input value={heroDraft.label} onChange={e => setHeroDraft(prev => ({ ...prev, label: e.target.value }))} placeholder="Watch" maxLength={32} style={inputStyle} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={saveHeroDraft} disabled={savingHero || !heroDraft.url.trim()} style={(heroDraft.url.trim() && !savingHero) ? addBtnStyle : addBtnDisabled}>
                  <Save size={11} /> {savingHero ? 'Saving…' : 'Save'}
                </button>
                <button onClick={closeHeroEditor} style={smallBtn}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add controls */}
      <div style={{ marginTop: 12, borderTop: '1px solid var(--gray-200)', paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={openFilePicker} disabled={uploading} style={uploading ? addBtnDisabled : addBtnStyle}>
            <Upload size={11}/> {uploading ? 'Uploading…' : '+ Add image'}
          </button>
          <button
            onClick={() => setMediaPickerOpen(true)}
            disabled={uploading || addingFromLibrary}
            style={(uploading || addingFromLibrary) ? addBtnDisabled : addBtnStyle}
          >
            <ImageIcon size={11}/> {addingFromLibrary ? 'Adding…' : 'Pick from Media Library'}
          </button>
          <button onClick={() => openHeroEditor(null)} disabled={heroEditingNew} style={heroEditingNew ? addBtnDisabled : addBtnStyle}>
            + Add hero video
          </button>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            value={addYtUrl}
            onChange={e => setAddYtUrl(e.target.value)}
            placeholder="Paste a YouTube URL to add a product video"
            style={{ ...inputStyle, padding: '6px 8px' }}
          />
          <button onClick={addVideo} disabled={addingVideo || !addYtUrl.trim()} style={(addYtUrl.trim() && !addingVideo) ? addBtnStyle : addBtnDisabled}>
            {addingVideo ? 'Adding…' : '+ Add video'}
          </button>
        </div>
      </div>

      {mediaPickerOpen && (
        <MediaPicker
          multiSelect
          onClose={() => setMediaPickerOpen(false)}
          onPick={(assets) => {
            setMediaPickerOpen(false);
            openCropForAssets(assets);
          }}
        />
      )}

      {cropImages && (
        <ImageCropModal
          images={cropImages}
          onCancel={() => setCropImages(null)}
          onConfirm={uploadCroppedGallery}
        />
      )}
    </div>
  );
}

function TagAuditView({ tagKey, onTagKeyChange, filter, setFilter, loading, rows, totalApplied, totalMissing, onToggleRow, onReload, dirty, dirtyCount, onSave, onDiscard, saving }) {
  const selectedTag = PRODUCT_TAGS.find(t => t.key === tagKey);
  return (
    <div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', padding: '10px 12px', border: '1px solid var(--gray-200)', borderRadius: 6, background: '#fafafa' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <TagIcon size={14} color="var(--gray-500)" />
          <select
            value={tagKey}
            onChange={(e) => onTagKeyChange(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid var(--gray-300)', borderRadius: 4, fontSize: 13, minWidth: 220 }}
          >
            {PRODUCT_TAGS.map(t => (
              <option key={t.key} value={t.key}>{t.label}</option>
            ))}
          </select>
          {selectedTag && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 999, background: selectedTag.color, color: '#fff', fontSize: 11, fontWeight: 600 }}>
              {selectedTag.label}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: 6 }}>
          {[
            { id: 'all', label: `All (${totalApplied + totalMissing})` },
            { id: 'applied', label: `Applied (${totalApplied})` },
            { id: 'missing', label: `Missing (${totalMissing})` },
          ].map(b => (
            <button
              key={b.id}
              onClick={() => setFilter(b.id)}
              style={{
                padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: filter === b.id ? 700 : 500,
                background: filter === b.id ? 'var(--orange)' : '#fff',
                color: filter === b.id ? '#fff' : 'var(--gray-600)',
                border: `1px solid ${filter === b.id ? 'var(--orange)' : 'var(--gray-300)'}`,
                cursor: 'pointer',
              }}
            >
              {b.label}
            </button>
          ))}
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {dirty && (
            <>
              <span style={{ fontSize: 12, color: 'var(--orange)', fontWeight: 600 }}>
                {dirtyCount} pending
              </span>
              <button onClick={onDiscard} disabled={saving} style={{ padding: '6px 10px', background: 'transparent', color: 'var(--gray-600)', border: '1px solid var(--gray-300)', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>Discard</button>
              <button onClick={onSave} disabled={saving} style={{ padding: '6px 12px', background: 'var(--orange)', color: '#fff', border: 'none', borderRadius: 4, cursor: saving ? 'wait' : 'pointer', fontSize: 12 }}>
                {saving ? 'Saving...' : 'Save changes'}
              </button>
            </>
          )}
          <button onClick={onReload} disabled={loading || saving} style={{ padding: '6px 10px', background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 4, cursor: loading ? 'wait' : 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <RefreshCw size={12} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} /> Refresh
          </button>
        </div>
      </div>

      <div style={{ border: '1px solid var(--gray-200)', borderRadius: 8, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>
            <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite', marginBottom: 6 }} />
            <div>Auditing tag across all products...</div>
          </div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--gray-400)', fontSize: 13 }}>
            No products match this filter.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: 'var(--gray-100)', textAlign: 'left' }}>
              <tr>
                <th style={thSm}>Product</th>
                <th style={{ ...thSm, width: 140 }}>SKU</th>
                <th style={{ ...thSm, width: 110 }}>Value</th>
                <th style={{ ...thSm, width: 110, textAlign: 'center' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} style={{ borderTop: '1px solid var(--gray-200)', background: r.pending ? '#fff8e1' : '#fff' }}>
                  <td style={tdSm}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--navy)' }}>
                      {r.name}
                      {!r.isVisible && <span style={{ marginLeft: 8, fontSize: 11, color: '#c53030' }}>hidden</span>}
                    </div>
                  </td>
                  <td style={{ ...tdSm, fontFamily: 'monospace', fontSize: 11, color: 'var(--gray-500)' }}>
                    {r.sku || '—'}
                  </td>
                  <td style={tdSm}>
                    {r.effectiveApplied ? (
                      <span style={{ color: '#1a7a1a', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                        <Check size={12} /> true
                      </span>
                    ) : (
                      <span style={{ color: '#c53030', fontStyle: 'italic', fontSize: 12 }}>missing</span>
                    )}
                    {r.pending && <span style={{ marginLeft: 8, fontSize: 10, color: 'var(--orange)', fontWeight: 600 }}>pending</span>}
                  </td>
                  <td style={{ ...tdSm, textAlign: 'center' }}>
                    <button
                      onClick={() => onToggleRow(r)}
                      style={{
                        background: 'none', border: '1px solid var(--gray-300)', borderRadius: 6,
                        padding: '4px 10px', cursor: 'pointer',
                        color: r.effectiveApplied ? '#c53030' : '#1a7a1a', fontSize: 12, fontWeight: 600,
                      }}
                    >
                      {r.effectiveApplied ? <X size={12} /> : <Check size={12} />}
                      {' '}
                      {r.effectiveApplied ? 'Remove' : 'Apply'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────
const thSm = { padding: '8px 12px', fontWeight: 600, color: 'var(--gray-600)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'left' };
const tdSm = { padding: '8px 12px', verticalAlign: 'middle' };
const input = { padding: '6px 8px', width: '100%', border: '1px solid var(--gray-300)', borderRadius: 4, fontSize: 13 };
const labelStyle = { fontSize: 12, color: 'var(--gray-600)', fontWeight: 600, paddingTop: 8 };
const arrowBtnStyle = {
  padding: '3px 6px', background: '#fff', border: '1px solid var(--gray-300)', borderRadius: 4, cursor: 'pointer',
  color: 'var(--gray-600)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};
